import * as i0 from '@angular/core';
import { Injectable, Pipe, Component, Input, TemplateRef, ContentChild, ElementRef, ViewChild, NgModule } from '@angular/core';
import * as i1$2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i7 from '@net7/components';
import { DvComponentsLibModule } from '@net7/components';
import * as i1 from '@net7/boilerplate-common';
import { helpers, SmartPaginationDS, SmartPaginationEH, AbstractLayout, N7BoilerplateCommonModule } from '@net7/boilerplate-common';
import { Subject, of, fromEvent, ReplaySubject } from 'rxjs';
import { takeUntil, delay, map, tap, debounceTime, switchMap, filter, switchMapTo, first, catchError } from 'rxjs/operators';
import { isEmpty, xor, merge, isObject, clone, cloneDeep } from 'lodash';
import { _t, LayoutDataSource, EventHandler, DataSource, translate } from '@net7/core';
import * as i2 from '@angular/router';
import { RoutesRecognized, NavigationStart } from '@angular/router';
import * as i1$1 from '@angular/platform-browser';
import OpenSeadragon from 'openseadragon';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import * as dayjs from 'dayjs';
import tippy from 'tippy.js';
import * as i1$3 from '@angular/common/http';

const hasValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return !!(value || value === 0);
};
var searchHelper = {
    stateToQueryParams(state, schemas) {
        const queryParams = {};
        Object.keys(state).forEach((key) => {
            const schema = schemas[key];
            const { multiple, valueType } = schema;
            const value = state[key];
            if (hasValue(value)) {
                let encodedValue;
                switch (valueType) {
                    case 'number':
                        queryParams[key] = multiple ? value.join(',') : value;
                        break;
                    case 'string':
                        if (Array.isArray(value)) {
                            encodedValue = value.map((element) => element.replace(/,/g, '%2c'));
                        }
                        else {
                            encodedValue = value.replace(/,/g, '%2c');
                        }
                        queryParams[key] = multiple ? encodedValue.join(',') : encodedValue;
                        break;
                    case 'boolean':
                        queryParams[key] = multiple ? value.map((v) => +v).join(',') : +value;
                        break;
                    default:
                        break;
                }
            }
        });
        return queryParams;
    },
    queryParamsToState(queryParams, schemas) {
        const state = {};
        Object.keys(queryParams).forEach((key) => {
            const value = queryParams[key];
            const schema = schemas[key];
            const { multiple, valueType } = schema;
            if (hasValue(value)) {
                if (hasValue(value)) {
                    switch (valueType) {
                        case 'number':
                            state[key] = multiple ? value.split(',').map((v) => +v) : +value;
                            break;
                        case 'string':
                            state[key] = multiple ? value.split(',').map((v) => `${v.replace(/%2c/g, ',')}`) : `${value.replace(/%2c/g, ',')}`;
                            break;
                        case 'boolean':
                            state[key] = multiple ? value.split(',').map((v) => !!v) : !!value;
                            break;
                        default:
                            break;
                    }
                }
            }
        });
        return state;
    }
};

/* eslint-disable camelcase */
const INPUT_STATE_CONTEXT = 'input';
const FACET_STATE_CONTEXT = 'facet';
const SECTION_STATE_CONTEXT = 'section';
const RESULTS_REQUEST_STATE_CONTEXT = 'resultsRequest';
const FACETS_REQUEST_STATE_CONTEXT = 'facetsRequest';
class MrSearchService {
    constructor(router, activatedRoute, communication) {
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.communication = communication;
        this.destroyed$ = new Subject();
        this.queryParamKeys = [];
        this.initializeKeys = [];
        this.initializeValues = {};
        this.inputSchemas = {};
        this.contextState = {};
        this.internalFilterKeys = [];
        this.internalFilterState = {
            globalParams: {},
            facets: {}
        };
        this.state$ = {};
        this.beforeHook = {};
        this.getConfig = () => this.config;
        this.isQueryParamKey = (input) => this.queryParamKeys.includes(input);
    }
    init(searchId, config, locale = null) {
        this.searchId = searchId;
        this.config = config;
        this.locale = locale;
        // first clear
        this.clear();
        // initial states
        this.initInputState();
        this.initFacetState();
        this.initSectionState();
        // listeners
        this.onInputsChange();
        this.onInternalInputsChange();
        this.onRouteChange();
        this.onResultsLoading();
        this.onFacetsScroll();
    }
    getState$(context, id) {
        const stateId = id ? `${context}.${id}` : context;
        if (!this.state$[stateId]) {
            throw Error(`Key "${stateId}" does not exist`);
        }
        return this.state$[stateId];
    }
    addStateContext(context) {
        if (this.state$[context]) {
            throw Error(`State key "${context}" already exists`);
        }
        // initial state
        this.contextState[context] = {};
        // create stream
        this.state$[context] = new Subject();
    }
    addState(context, id) {
        const stateId = `${context}.${id}`;
        if (!this.state$[context]) {
            throw Error(`
        State context "${context}" does not exist.
        You must add context first
      `);
        }
        if (this.state$[stateId]) {
            throw Error(`State key "${stateId}" already exists`);
        }
        // create stream
        this.state$[stateId] = new Subject();
    }
    setState(context, id, newValue) {
        const stateId = `${context}.${id}`;
        if (!this.state$[stateId]) {
            throw Error(`Key "${stateId}" does not exist`);
        }
        let value = newValue;
        // hook control
        if (this.beforeHook[stateId]) {
            value = this.beforeHook[stateId](value);
        }
        // update stream
        this.state$[stateId].next(value);
        // update context
        this.setContextState(context, id, value);
    }
    setBeforeHook(context, id, hook) {
        const stateId = `${context}.${id}`;
        if (!this.state$[stateId]) {
            throw Error(`Key "${stateId}" does not exist`);
        }
        this.beforeHook[stateId] = hook;
    }
    reset() {
        // clear input states
        Object.keys(this.contextState[INPUT_STATE_CONTEXT])
            .filter((id) => !this.internalFilterKeys.includes(id))
            .forEach((id) => {
            this.setState(INPUT_STATE_CONTEXT, id, null);
        });
    }
    destroy() {
        this.destroyed$.next();
    }
    clear() {
        this.contextState = {};
        this.state$ = {};
        this.beforeHook = {};
    }
    setContextState(context, id, newValue) {
        this.contextState[context] = {
            ...this.contextState[context],
            [`${id}`]: newValue
        };
        this.state$[context].next({
            lastUpdated: id,
            state: this.contextState[context]
        });
    }
    initInputState() {
        const { facets, layoutInputs } = this.config;
        // add context state
        this.addStateContext(INPUT_STATE_CONTEXT);
        // set facets input state
        facets.sections.forEach(({ header, inputs }) => {
            [header, ...inputs]
                .filter((input) => input)
                .forEach(({ id, queryParam, schema, limit, type, target, initialize }) => {
                if (!id) {
                    return;
                }
                this.addState(INPUT_STATE_CONTEXT, id);
                // is query param?
                if (queryParam) {
                    this.queryParamKeys.push(id);
                }
                // input has initial values request
                if (initialize) {
                    this.initializeKeys.push(id);
                }
                // schemas
                if (schema) {
                    this.inputSchemas[id] = schema;
                }
                // links internal state
                if (['link', 'map', 'histogram'].includes(type)) {
                    this.internalFilterState.facets[id] = {
                        id,
                        limit,
                        offset: 0,
                        query: '',
                        loading: false,
                        values: []
                    };
                }
                // internal filters
                if (target) {
                    this.internalFilterKeys.push(id);
                }
            });
        });
        // set layout input state
        layoutInputs.forEach(({ id, queryParam, schema }) => {
            this.addState(INPUT_STATE_CONTEXT, id);
            if (queryParam) {
                this.queryParamKeys.push(id);
            }
            // schemas
            if (schema) {
                this.inputSchemas[id] = schema;
            }
        });
    }
    initFacetState() {
        const { facets } = this.config;
        // add context state
        this.addStateContext(FACET_STATE_CONTEXT);
        // set input state
        facets.sections.forEach(({ header, inputs }) => {
            [header, ...inputs]
                .filter((input) => input)
                .forEach((input) => {
                this.addState(FACET_STATE_CONTEXT, input.id);
            });
        });
    }
    initSectionState() {
        const { facets } = this.config;
        // add context state
        this.addStateContext(SECTION_STATE_CONTEXT);
        // set input state
        facets.sections.forEach(({ id }) => {
            this.addState(SECTION_STATE_CONTEXT, id);
        });
    }
    onRouteChange() {
        const { results } = this.config.request;
        // add context state
        this.addStateContext(RESULTS_REQUEST_STATE_CONTEXT);
        // default states
        ['loading', 'request', 'success', 'error'].forEach((id) => {
            this.addState(RESULTS_REQUEST_STATE_CONTEXT, id);
        });
        this.activatedRoute.queryParams.pipe(takeUntil(this.destroyed$), 
        // fix initial listeners (symbolic timeout)
        delay(1), 
        // query params to state
        map((params) => searchHelper.queryParamsToState(params, this.inputSchemas)), 
        // state != queryParams control
        tap((params) => {
            if (isEmpty(params)) {
                this.reset();
            }
            // update state
            if (!isEmpty(params)) {
                const inputContext = this.contextState[INPUT_STATE_CONTEXT];
                if (isEmpty(inputContext)) {
                    Object.keys(params)
                        .filter((inputId) => this.queryParamKeys.includes(inputId))
                        .forEach((inputId) => {
                        this.setState(INPUT_STATE_CONTEXT, inputId, params[inputId]);
                    });
                }
                else {
                    Object.keys(params)
                        .filter((inputId) => this.queryParamKeys.includes(inputId))
                        .filter((inputId) => this.notEquals(inputContext[inputId], params[inputId]))
                        .forEach((inputId) => {
                        this.setState(INPUT_STATE_CONTEXT, inputId, (params[inputId] || params[inputId] === 0)
                            ? params[inputId]
                            : null);
                    });
                }
            }
        }), map((params) => {
            this.setState(RESULTS_REQUEST_STATE_CONTEXT, 'loading', params);
            return params;
        }), debounceTime(results.delay || 1), map((params) => {
            this.setState(RESULTS_REQUEST_STATE_CONTEXT, 'request', params);
            return params;
        }), switchMap((state) => this.communication.request$(results.id, {
            params: { ...state, searchId: this.searchId },
            urlParams: this.locale ? `?locale=${this.locale}` : '',
            method: 'POST',
            onError: (error) => {
                this.setState(RESULTS_REQUEST_STATE_CONTEXT, 'error', error);
            }
        }, results.provider || null))).subscribe((response) => {
            this.setState(RESULTS_REQUEST_STATE_CONTEXT, 'success', response);
        });
    }
    onInputsChange() {
        this.getState$(INPUT_STATE_CONTEXT).pipe(filter(({ lastUpdated }) => this.queryParamKeys.indexOf(lastUpdated) !== -1)).subscribe(({ state }) => {
            const filteredState = {};
            Object.keys(state).forEach((id) => {
                if (this.queryParamKeys.indexOf(id) !== -1) {
                    filteredState[id] = state[id];
                }
            });
            const queryParams = searchHelper.stateToQueryParams(filteredState, this.inputSchemas);
            this.router.navigate([], {
                queryParams
            });
        });
    }
    onInternalInputsChange() {
        this.getState$(INPUT_STATE_CONTEXT).pipe(filter(({ lastUpdated }) => this.queryParamKeys.indexOf(lastUpdated) === -1), map(({ lastUpdated, state }) => {
            const { sections } = this.config.facets;
            let inputConfig;
            sections.forEach((section) => {
                section.inputs.forEach((input) => {
                    if (input.id === lastUpdated) {
                        inputConfig = input;
                    }
                });
            });
            if (inputConfig && inputConfig.target) {
                return {
                    inputConfig,
                    value: state[lastUpdated]
                };
            }
            return null;
        }), filter((data) => data !== null)).subscribe((data) => {
            if (!data)
                throw Error('inputChange data is null');
            const { inputConfig, value } = data;
            const { target } = inputConfig;
            // update internal filters
            this.internalFilterState.facets[target].query = value;
            this.internalFilterState.facets[target].offset = 0;
            this.doSingleFacetRequest(target);
        });
    }
    doSingleFacetRequest(target) {
        const { facets } = this.config.request;
        const { globalParams } = this.internalFilterState;
        const { id, limit, offset, query } = this.internalFilterState.facets[target];
        this.communication.request$(facets.id, {
            params: {
                ...globalParams,
                facets: [{
                        id, limit, offset, query
                    }],
                searchId: this.searchId
            },
            urlParams: this.locale ? `?locale=${this.locale}` : '',
            method: 'POST',
            onError: (error) => {
                this.setState(FACETS_REQUEST_STATE_CONTEXT, 'error', error);
            }
        }, facets.provider || null).subscribe((response) => {
            this.onFacetsRequestSuccess(response);
            // reset loading
            this.internalFilterState.facets[target].loading = false;
        });
    }
    onResultsLoading() {
        const { facets } = this.config.request;
        if (!facets) {
            return;
        }
        // add context state
        this.addStateContext(FACETS_REQUEST_STATE_CONTEXT);
        // default states
        ['loading', 'request', 'success', 'error'].forEach((id) => {
            this.addState(FACETS_REQUEST_STATE_CONTEXT, id);
        });
        this.getState$(RESULTS_REQUEST_STATE_CONTEXT, 'loading').pipe(map((params) => {
            const facetsParams = { ...params };
            this.setState(FACETS_REQUEST_STATE_CONTEXT, 'loading', facetsParams);
            // updated internal filter state
            this.internalFilterState.globalParams = { ...facetsParams };
            return facetsParams;
        }), debounceTime(facets.delay || 1), map((params) => {
            params.facets = [];
            this.config.facets.sections.forEach(({ inputs }) => {
                inputs.filter(({ type }) => ['link', 'map', 'histogram'].includes(type))
                    .forEach(({ id }) => {
                    // reset offset
                    this.internalFilterState.facets[id].offset = 0;
                    const { limit, query, offset } = this.internalFilterState.facets[id];
                    params.facets.push({
                        id, limit, offset, query
                    });
                });
            });
            this.setState(FACETS_REQUEST_STATE_CONTEXT, 'request', params);
            return params;
        }), switchMap((state) => {
            let initializeRequest$ = of(true);
            if (this.initializeKeys.length && isEmpty(this.initializeValues)) {
                initializeRequest$ = this.communication.request$(facets.id, {
                    params: {
                        facets: state.facets,
                        searchId: this.searchId
                    },
                    urlParams: this.locale ? `?locale=${this.locale}` : '',
                    method: 'POST',
                    onError: (error) => {
                        this.setState(FACETS_REQUEST_STATE_CONTEXT, 'error', error);
                    }
                }, facets.provider || null);
            }
            return initializeRequest$.pipe(tap((response) => {
                if (response.facets) {
                    Object.keys(response.facets)
                        .filter((inputKey) => this.initializeKeys.includes(inputKey))
                        .forEach((inputKey) => {
                        this.initializeValues[inputKey] = response.facets[inputKey];
                    });
                }
            }), switchMapTo(of(state)));
        }), switchMap((state) => this.communication.request$(facets.id, {
            params: {
                ...state,
                searchId: this.searchId
            },
            urlParams: this.locale ? `?locale=${this.locale}` : '',
            method: 'POST',
            onError: (error) => {
                this.setState(FACETS_REQUEST_STATE_CONTEXT, 'error', error);
            }
        }, facets.provider || null))).subscribe((response) => {
            this.onFacetsRequestSuccess(response);
        });
        // update facet links
        this.getState$(FACETS_REQUEST_STATE_CONTEXT, 'success').subscribe((response) => {
            const { facets: responseFacets } = response;
            Object.keys(responseFacets).forEach((id) => {
                const { values: responseValues, filtered_total_count } = responseFacets[id];
                const { limit, offset, values: stateValues } = this.internalFilterState.facets[id];
                const filterState = this.internalFilterState.facets[id];
                if (offset > 0) {
                    // delete loading element
                    filterState.values.pop();
                    // merge new results
                    filterState.values = [
                        ...stateValues,
                        ...responseValues
                    ];
                }
                else {
                    filterState.values = [
                        ...responseValues
                    ];
                }
                if ((offset + limit) < filtered_total_count) {
                    filterState.values.push({
                        text: _t('global#facet_loading_text'),
                        classes: 'loading-text-link',
                        payload: null,
                    });
                }
                this.setState(FACET_STATE_CONTEXT, id, {
                    links: filterState.values
                });
            });
        });
    }
    onFacetsRequestSuccess(response) {
        const { facets: responseFacets } = response;
        if (!isEmpty(this.initializeValues)) {
            // intialValues and responseFacets merge strategy
            Object.keys(responseFacets).forEach((inputKey) => {
                if (this.initializeValues[inputKey]) {
                    const updatedValues = this.initializeValues[inputKey].values.map((initialValue) => {
                        const singleValue = responseFacets[inputKey].values.find(({ payload }) => payload === initialValue.payload);
                        return {
                            ...initialValue,
                            counter: singleValue?.counter || 0
                        };
                    });
                    // sort by counter
                    updatedValues.sort((a, b) => b.counter - a.counter);
                    responseFacets[inputKey].values = updatedValues;
                }
            });
        }
        Object.keys(responseFacets).forEach((inputKey) => {
            // update internal filter state
            const { filtered_total_count } = responseFacets[inputKey];
            this.internalFilterState.facets[inputKey].filtered_total_count = filtered_total_count;
        });
        this.setState(FACETS_REQUEST_STATE_CONTEXT, 'success', {
            ...response,
            facets: responseFacets
        });
    }
    onFacetsScroll() {
        setTimeout(() => {
            const { facets } = this.config;
            facets.sections.forEach(({ inputs }) => {
                inputs
                    .filter((input) => input)
                    .filter((input) => input.type === 'link')
                    .forEach(({ id }) => {
                    const scrollEl = document.querySelector(`#facet-container-${id} .n7-input-link`);
                    if (!scrollEl)
                        throw Error(`Element with id "facet-container-${id}" not found`);
                    const scroll$ = fromEvent(scrollEl, 'scroll');
                    scroll$.pipe(debounceTime(300)).subscribe(({ target }) => {
                        const { limit, offset, loading, filtered_total_count, } = this.internalFilterState.facets[id];
                        const { scrollTop, clientHeight, scrollHeight } = target;
                        if (
                        // checks if the user has scrolled to the bottom of a container
                        (scrollTop + clientHeight + 1 >= Math.ceil(scrollHeight))
                            // checks if there are more items to load
                            && (offset + limit < filtered_total_count)
                            // checks if the application is not currently loading more items
                            && loading === false) {
                            this.internalFilterState.facets[id].loading = true;
                            this.internalFilterState.facets[id].offset = offset + limit;
                            this.doSingleFacetRequest(id);
                        }
                    });
                });
            });
        });
    }
    notEquals(val1, val2) {
        if (Array.isArray(val1) && Array.isArray(val2)) {
            return !!xor(val1, val2).length;
        }
        return val1 !== val2;
    }
}
MrSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchService, deps: [{ token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1.CommunicationService }], target: i0.ɵɵFactoryTarget.Injectable });
MrSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1.CommunicationService }]; } });

var LayoutState;
(function (LayoutState) {
    LayoutState["IDLE"] = "IDLE";
    LayoutState["LOADING"] = "LOADING";
    LayoutState["SUCCESS"] = "SUCCESS";
    LayoutState["EMPTY"] = "EMPTY";
    LayoutState["ERROR"] = "ERROR";
})(LayoutState || (LayoutState = {}));
class MrLayoutStateService {
    constructor() {
        this.stateContainers = {};
    }
    add(id) {
        const ids = Array.isArray(id) ? id : [id];
        ids.forEach((key) => {
            this.stateContainers[key] = new ReplaySubject();
            // initial state
            this.stateContainers[key].next(LayoutState.IDLE);
        });
    }
    get$(id) {
        if (!this.stateContainers[id]) {
            throw Error(`Layout state id '${id}' does not exists`);
        }
        return this.stateContainers[id];
    }
    set(id, newState) {
        if (!this.stateContainers[id]) {
            throw Error(`Layout state id '${id}' does not exists`);
        }
        this.stateContainers[id].next(newState);
    }
}
MrLayoutStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLayoutStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MrLayoutStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLayoutStateService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLayoutStateService, decorators: [{
            type: Injectable
        }] });

class MrResourceModalService {
    constructor(configuration, communication) {
        this.configuration = configuration;
        this.communication = communication;
        this.state$ = new Subject();
        // default state
        this.state$.next({ status: 'IDLE' });
    }
    open(resourceId, configId) {
        this.state$.next({ status: 'LOADING' });
        const config = this.configuration.get(`resource-modal-${configId}`);
        // add translations
        ['top', 'content'].forEach((type) => {
            config.sections[type] = config.sections[type].map((section) => ({
                ...section,
                title: _t(section.title)
            }));
        });
        this.pageRequest$(resourceId, config, (err) => {
            console.warn(`Error loading resource modal for ${resourceId}`, err.message);
            this.state$.next({ status: 'ERROR' });
        }).subscribe((response) => {
            this.state$.next({ response, config, status: 'SUCCESS', });
        });
    }
    close() {
        this.state$.next({ status: 'IDLE' });
    }
    pageRequest$(id, config, onError) {
        const { top, content } = config.sections;
        const sections = top.concat(content);
        return this.communication.request$('resource', {
            onError,
            method: 'POST',
            params: {
                id,
                type: config.type,
                sections: sections.map((s) => s.id),
            }
        });
    }
}
MrResourceModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceModalService, deps: [{ token: i1.ConfigurationService }, { token: i1.CommunicationService }], target: i0.ɵɵFactoryTarget.Injectable });
MrResourceModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceModalService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceModalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.ConfigurationService }, { type: i1.CommunicationService }]; } });

class MrLocaleService {
    constructor(router) {
        this.router = router;
        this.resourceConfig = null;
        this.changed$ = new ReplaySubject();
        this.getRouteId = () => this.routeId;
        this.getLocale = () => this.locale;
        this.getLink = (locale, routeId, id, slug) => {
            const routeConfig = this.router.config.find((config) => {
                const { routeId: currentRouteId, locale: currentRouteLocale } = config?.data || {};
                return routeId === currentRouteId && locale === currentRouteLocale;
            });
            if (typeof routeConfig?.path === 'string') {
                const resourceLocaleConfig = this.resourceConfig && this.resourceConfig[locale]
                    ? this.resourceConfig[locale]
                    : { id: null, slug: null };
                let idParam = '';
                let slugParam = '';
                if (id) {
                    idParam = `/${id}`;
                }
                else if (resourceLocaleConfig.id) {
                    idParam = `/${resourceLocaleConfig.id}`;
                }
                if (slug) {
                    slugParam = `/${slug}`;
                }
                else if (resourceLocaleConfig.slug) {
                    slugParam = `/${resourceLocaleConfig.slug}`;
                }
                const href = routeConfig.path
                    .replace('/:id', idParam)
                    .replace('/:slug', slugParam);
                return href.indexOf('/') === 0 ? `${href}` : `/${href}`;
            }
            console.warn('LocaleService link not found', locale, routeId);
            return '';
        };
        this.getLinkByLocale = (locale, id, slug) => this.getLink(locale, this.routeId, id, slug);
        this.getLinkByRouteId = (routeId, id, slug) => this.getLink(this.locale, routeId, id, slug);
        this.setResourceConfig = (config) => {
            this.resourceConfig = config;
        };
        this.router.events.pipe(filter((event) => event instanceof RoutesRecognized), map((event) => event.state.root.firstChild.data)).subscribe(({ routeId, locale }) => {
            this.routeId = routeId;
            this.locale = locale;
            this.changed$.next({
                routeId: this.routeId,
                locale: this.locale
            });
            // remove previous config
            this.resourceConfig = null;
        });
    }
}
MrLocaleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLocaleService, deps: [{ token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
MrLocaleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLocaleService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrLocaleService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i2.Router }]; } });

class EscapeHtmlPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(content) {
        return this.sanitizer.bypassSecurityTrustHtml(content);
    }
}
EscapeHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EscapeHtmlPipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
EscapeHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: EscapeHtmlPipe, name: "keepHtml", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: EscapeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'keepHtml', pure: false }]
        }], ctorParameters: function () { return [{ type: i1$1.DomSanitizer }]; } });

class MrAdvancedResultsLayoutDS extends LayoutDataSource {
    onInit(payload) {
        this.configuration = payload.configuration;
        this.mainState = payload.mainState;
        this.routeData = payload.routeData;
        this.communication = payload.communication;
        this.localeService = payload.localeService;
        this.pageConfig = this.configuration.get(this.routeData.configId);
        // config
        this.all().updateOptions({ config: this.pageConfig, localeService: this.localeService });
        // manual updates
        this.one('mr-search-page-title').update({});
        // update head title
        this.updateHeadTitle();
        // update translations
        this.addTranslations(this.pageConfig);
    }
    updateSearchTags(params) {
        if (!this.pageConfig.filters) {
            return;
        }
        const { labels } = this.pageConfig.filters;
        const tags = [];
        Object.keys(labels)
            .filter((key) => !!params[key])
            .forEach((key) => {
            tags[key] = params[key];
        });
        this.one('mr-advanced-search-tags').updateOptions({ labels });
        this.one('mr-advanced-search-tags').update(tags);
    }
    request$(params, onError) {
        const { locale } = this.routeData;
        const { searchId } = this.pageConfig;
        const searchParams = {
            ...params
        };
        Object.keys(searchParams)
            .filter((key) => ['page', 'limit', 'sort'].includes(key))
            .forEach((key) => {
            searchParams.results = searchParams.results || {};
            searchParams.results[key] = searchParams[key];
            delete searchParams[key];
        });
        // normalize results filters
        const resultsParams = {};
        const results = searchParams.results || {};
        const page = results.page ? +results.page : 1;
        resultsParams.limit = results.limit ? +results.limit : 12;
        resultsParams.offset = page === 1 ? 0 : resultsParams.limit * (page - 1);
        resultsParams.sort = results.sort || 'sort_ASC';
        return this.communication.request$('advancedSearch', {
            method: 'POST',
            params: {
                ...searchParams,
                searchId,
                results: {
                    ...resultsParams
                }
            },
            urlParams: locale ? `?locale=${locale}` : '',
            onError
        });
    }
    handleResponse(response) {
        this.some([
            'mr-search-results-title',
            'mr-search-results',
        ]).update(response);
        // pagination
        this.one('n7-smart-pagination').updateOptions({ mode: 'payload' });
        this.one('n7-smart-pagination').update(this.getPaginationParams(response));
    }
    updateHeadTitle() {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle)].join(' > '));
    }
    addTranslations(config) {
        if (config?.sort?.label) {
            config.sort.label = _t(config.sort.label);
            config.sort.options = config.sort.options.map((option) => ({
                ...option,
                label: _t(option.label)
            }));
        }
        ['text', 'button'].forEach((key) => {
            if (config.fallback) {
                config.fallback[key] = _t(config.fallback[key]);
            }
            if (config.ko) {
                config.ko[key] = _t(config.ko[key]);
            }
        });
        // filters
        const { filters } = this.pageConfig;
        if (filters) {
            filters.title = _t(filters.title);
            Object.keys(filters.labels).forEach((key) => {
                filters.labels[key] = _t(filters.labels[key]);
            });
        }
    }
    getPaginationParams(response) {
        const { total_count: totalCount, offset, limit } = response;
        const { pagination: paginationConfig } = this.pageConfig;
        return {
            totalPages: Math.ceil(totalCount / limit),
            currentPage: (offset + limit) / limit,
            pageLimit: paginationConfig.limit,
            sizes: {
                label: paginationConfig.selectLabel ? _t(paginationConfig.selectLabel) : null,
                list: paginationConfig.options,
                active: limit,
            },
        };
    }
}

class MrAdvancedResultsLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-advanced-results-layout.init':
                    this.activatedRoute = payload.activatedRoute;
                    this.router = payload.router;
                    this.layoutState = payload.layoutState;
                    this.modalService = payload.modalService;
                    this.dataSource.onInit(payload);
                    // listen route changes
                    this.listenToRouterChanges();
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                case 'mr-advanced-results-layout.destroy':
                    this.destroy$.next();
                    break;
                case 'mr-advanced-results-layout.searchreset':
                    if (payload && payload?.action === 'redirect') {
                        this.router.navigate([payload.url], {});
                    }
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'n7-smart-pagination.click':
                    this.updateRouter({ page: payload.page });
                    break;
                case 'n7-smart-pagination.change':
                    this.updateRouter({ limit: payload.value, page: 1 });
                    break;
                case 'mr-search-results-title.change':
                    this.updateRouter({ sort: payload.value, page: 1 });
                    break;
                case 'mr-search-results.openresourcemodal': {
                    const { id, type: resourceType } = payload;
                    this.modalService.open(id, resourceType);
                    break;
                }
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
    }
    /** URL changes */
    listenToRouterChanges() {
        this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$), tap(() => {
            this.layoutState.set('results', LayoutState.LOADING);
        }), switchMap((params) => {
            this.dataSource.updateSearchTags(params);
            return this.dataSource.request$(params, (error) => {
                console.warn('Advanced search error', error);
                this.layoutState.set('results', LayoutState.ERROR);
            });
        })).subscribe((response) => {
            this.dataSource.handleResponse(response);
            this.layoutState.set('results', isEmpty(response.results) ? LayoutState.EMPTY : LayoutState.SUCCESS);
            // scroll to ref element
            if (!this.scrollRefElement) {
                this.scrollRefElement = document.querySelector('.scroll-ref');
            }
            else if (!helpers.isElementInViewport(this.scrollRefElement)) {
                this.scrollRefElement.scrollIntoView();
            }
        });
    }
    updateRouter(queryParams) {
        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge'
        });
    }
}

const extractQueryParams = (queryParams) => {
    const params = {};
    queryParams.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        params[key] = value;
    });
    return params;
};
var linksHelper = {
    getQueryParams(href) {
        const queryParams = href.split('?')[1] ? extractQueryParams(href.split('?')[1]) : null;
        return this.isExternalLink(href) ? null : queryParams;
    },
    getRouterLink(href) {
        return this.isExternalLink(href) ? href : href.split('?')[0];
    },
    isExternalLink(href) {
        return /^http(?:s)?:\/{2}\S+$/.test(href);
    },
    joinQueryParams(href, queryParams) {
        let params = '';
        queryParams.forEach((par) => {
            params += params === '' ? par.replace('?', '') : `&${par.replace('?', '')}`;
        });
        if (params !== '') {
            return href.split('?')[1] ? `${href}&${params}` : `${href}?${params}`;
        }
        return href;
    }
};

class MrBreadcrumbsDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        let items = [];
        if (Array.isArray(data) && data.length) {
            let { base } = this.options || {};
            base = Array.isArray(base) ? base : [];
            items = [
                ...base.map((item) => ({
                    label: _t(item.title),
                    anchor: this.getAnchor(item),
                })),
                ...data.map((item) => ({
                    label: item.title,
                    anchor: this.getAnchor(item),
                }))
            ];
        }
        // remove last link
        if (items.length) {
            items[items.length - 1].anchor = null;
        }
        return { items };
    }
    getAnchor({ link, routeId, slug, id }) {
        const { localeService } = this.options;
        // link
        let anchor;
        if (routeId) {
            const routeLink = localeService.getLinkByRouteId(routeId, id, slug);
            anchor = {
                href: routeLink
            };
        }
        else if (link) {
            anchor = {
                href: linksHelper.getRouterLink(link)
            };
        }
        return anchor;
    }
}

const ITEM_PREVIEW_DEFAULTS$2 = {
    limit: 100,
    striptags: true
};
class MrCollectionDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        const { localeService } = this.options;
        const { header, items } = data;
        // items check
        if (Array.isArray(items) && !items.length) {
            return null;
        }
        const { classes, itemPreview, linkTarget } = this.options;
        const itemPreviewOptions = merge(ITEM_PREVIEW_DEFAULTS$2, (itemPreview || {}));
        if ((header || {}).button) {
            const { link, text } = header.button;
            header.button = [{
                    text,
                    anchor: {
                        href: linksHelper.getRouterLink(link),
                        queryParams: linksHelper.getQueryParams(link)
                    }
                }];
        }
        return {
            header: header ? {
                title: {
                    main: {
                        text: header.title,
                        classes: 'bold'
                    },
                    secondary: header.subtitle ? {
                        text: header.subtitle,
                    } : null
                },
                actions: {
                    buttons: header.button
                }
            } : null,
            items: items.map((item) => {
                let anchor = null;
                if (item.text) {
                    // Sanitize HTML tags from the text content
                    if (itemPreviewOptions.striptags) {
                        item.text = helpers.striptags(item.text);
                    }
                    // Limit the length of the item preview text content
                    if (itemPreviewOptions.limit && (item.text.length > itemPreviewOptions.limit)) {
                        item.text = `${item.text.substring(0, itemPreviewOptions.limit)}...`;
                    }
                }
                // link
                if (item.routeId) {
                    const routeLink = localeService.getLinkByRouteId(item.routeId, item.id, item.slug);
                    anchor = {
                        href: routeLink,
                        queryParams: item.params || null,
                    };
                }
                else if (item.link) {
                    anchor = {
                        href: linksHelper.getRouterLink(item.link),
                        queryParams: linksHelper.getQueryParams(item.link),
                    };
                }
                if (anchor && linkTarget) {
                    anchor.target = linkTarget;
                }
                if (item.payload) {
                    anchor = {
                        payload: {
                            ...item.payload
                        }
                    };
                }
                return {
                    ...item,
                    anchor,
                    classes: classes || ''
                };
            })
        };
    }
}

class MrContentDS extends DataSource {
    transform(data) {
        return data;
    }
}

class MrFiltersDS extends DataSource {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transform(data) {
        return data;
    }
}

class MrHeroDS extends DataSource {
    transform(data) {
        const { classes, background } = this.options;
        const { localeService } = this.options;
        const { text, image, title, button } = data;
        const backgroundImage = background ? image : null;
        // link
        let anchor = null;
        if (button?.routeId) {
            const routeLink = localeService.getLinkByRouteId(button.routeId, button.id, button.slug);
            anchor = {
                href: routeLink,
                queryParams: button.params || null,
            };
        }
        else if (button?.link) {
            anchor = {
                href: linksHelper.getRouterLink(button.link),
                queryParams: linksHelper.getQueryParams(button.link),
            };
        }
        return {
            text,
            title,
            classes,
            backgroundImage,
            image: !backgroundImage ? image : null,
            button: button && anchor ? {
                ...button,
                anchor,
            } : null
        };
    }
}

/* eslint-disable import/no-extraneous-dependencies */
const svgNS = 'http://www.w3.org/2000/svg';
// based on https://github.com/openseadragon/svg-overlay
class Overlay {
    constructor(viewer) {
        this._containerWidth = 0;
        this._containerHeight = 0;
        this.node = () => this._node;
        this.svg = () => this._svg;
        this.resize = () => {
            if (this._containerWidth !== this._viewer.container.clientWidth) {
                this._containerWidth = this._viewer.container.clientWidth;
                this._svg.setAttribute('width', this._containerWidth);
            }
            if (this._containerHeight !== this._viewer.container.clientHeight) {
                this._containerHeight = this._viewer.container.clientHeight;
                this._svg.setAttribute('height', this._containerHeight);
            }
            const p = this._viewer.viewport.pixelFromPoint(new OpenSeadragon.Point(0, 0), true);
            const zoom = this._viewer.viewport.getZoom(true);
            const rotation = this._viewer.viewport.getRotation();
            const flipped = this._viewer.viewport.getFlip();
            const containerSizeX = this._viewer.viewport._containerInnerSize.x;
            let scaleX = containerSizeX * zoom;
            const scaleY = scaleX;
            if (flipped) {
                // Makes the x component of the scale negative to flip the svg
                scaleX = -scaleX;
                // Translates svg back into the correct coordinates when the x scale is made negative.
                p.x = -p.x + containerSizeX;
            }
            this._node.setAttribute('transform', `translate(${p.x},${p.y}) scale(${scaleX},${scaleY}) rotate(${rotation})`);
        };
        this.onClick = (node, handler) => {
            new OpenSeadragon.MouseTracker({
                element: node,
                clickHandler: handler
            }).setTracking(true);
        };
        this._viewer = viewer;
        this._svg = document.createElementNS(svgNS, 'svg');
        this._svg.style.position = 'absolute';
        this._svg.style.left = 0;
        this._svg.style.top = 0;
        this._svg.style.width = '100%';
        this._svg.style.height = '100%';
        this._viewer.canvas.appendChild(this._svg);
        this._node = document.createElementNS(svgNS, 'g');
        this._svg.appendChild(this._node);
        this._viewer.addHandler('animation', () => {
            this.resize();
        });
        this._viewer.addHandler('open', () => {
            this.resize();
        });
        this._viewer.addHandler('rotate', () => {
            this.resize();
        });
        this._viewer.addHandler('flip', () => {
            this.resize();
        });
        this._viewer.addHandler('resize', () => {
            this.resize();
        });
        this.resize();
    }
}
class MrImageViewerOverlayModel {
    constructor({ viewer, config, overlayEvents$ }) {
        this._page = 0;
        this._children = [];
        this._stylesDefaults = {
            highlight_color: 'red',
            highlight_opacity: 0.5,
            border_color: 'gray',
            border_opacity: 1,
            border_width: 2,
        };
        this._selectedStylesDefaults = {
            border_color: 'gray',
            border_opacity: 1,
            border_width: 5,
        };
        this.onPageChange = ({ page }) => {
            this._page = page;
            // emit signal
            this._overlayEvents$.next({ type: 'pagechange' });
            // load when image finish loading
            const onTileDrawn = () => {
                this.load();
                this._viewer.removeHandler('tile-drawn', onTileDrawn);
            };
            this._viewer.addHandler('tile-drawn', onTileDrawn);
        };
        if (this._viewer)
            return;
        this._viewer = viewer;
        this._config = config;
        this._overlayEvents$ = overlayEvents$;
    }
    init() {
        // listen viewer change
        this._viewer.addHandler('page', this.onPageChange);
        // load first overlay
        this.load();
    }
    destroy() {
        this._viewer.removeHandler('page', this.onPageChange);
    }
    resetStyles() {
        this._children.forEach((child) => {
            this.loadStyles(child);
        });
    }
    load() {
        // clear first
        this.clear();
        // config
        const currentConfig = this._config.overlay_images[this._page];
        // set overlay
        if (currentConfig?.hotspots) {
            this._overlay = new Overlay(this._viewer);
            currentConfig.hotspots.forEach((itemConfig) => {
                switch (itemConfig.shape) {
                    case 'polygon':
                    case 'rectangle':
                        this.loadPolygon(itemConfig);
                        break;
                    case 'circle':
                        this.loadCircle(itemConfig);
                        break;
                    default:
                        console.warn(`Overlay shape ${itemConfig.shape} does not exists`);
                        break;
                }
            });
        }
    }
    clear() {
        if (this._overlay) {
            // remove svg overlay
            this._overlay.svg().remove();
            this._overlay = null;
            this._children = [];
        }
    }
    loadPolygon(config) {
        const child = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        child.setAttribute('points', config.coordinates);
        this.loadStyles(child);
        child.addEventListener('click', () => {
            this.onClick(child, config);
        });
        this._children.push(child);
        this._overlay.node().appendChild(child);
    }
    loadCircle(config) {
        const child = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        const [cx, cy, r] = config.coordinates.split(',');
        child.setAttribute('cx', cx);
        child.setAttribute('cy', cy);
        child.setAttribute('r', r);
        this.loadStyles(child);
        child.addEventListener('click', () => {
            this.onClick(child, config);
        });
        this._children.push(child);
        this._overlay.node().appendChild(child);
    }
    onClick(child, config) {
        if (config.action === 'url' && config['action-url-url']) {
            document.location.href = config['action-url-url'];
            return;
        }
        this.setSelected(child);
        this._overlayEvents$.next({
            type: 'click',
            payload: config
        });
    }
    setSelected(selectedChild) {
        // reset
        this.resetStyles();
        // selected styles
        this.loadStyles(selectedChild, this._selectedStylesDefaults);
    }
    loadStyles(child, selectedStyles) {
        const currentConfig = this._config.overlay_images[this._page];
        const styles = {
            ...this._stylesDefaults,
            ...(currentConfig?.style || {})
        };
        child.setAttribute('fill', styles.highlight_color);
        child.setAttribute('stroke', selectedStyles?.border_color || styles.border_color);
        child.setAttribute('stroke-width', selectedStyles?.border_width || styles.border_width);
        child.setAttribute('style', `
      fill-opacity: ${styles.highlight_opacity}; 
      stroke-opacity: ${selectedStyles?.border_opacity || styles.border_opacity};
    `);
    }
}

/* eslint-disable camelcase */
class MrImageViewerDS extends DataSource {
    constructor() {
        super(...arguments);
        this.viewerLoaded$ = new Subject();
        this.overlayEvents$ = new Subject();
    }
    transform(data) {
        if (!data)
            return null;
        const { images, thumbs, overlay_images } = data;
        const { tools } = (this.options || {});
        return {
            images,
            thumbs,
            viewerId: this.id,
            hideNavigation: !(data.images.length > 1),
            libOptions: {
                /* SHOW GROUP */
                showNavigator: false,
                autoHideControls: false,
                // showNavigationControl: false,
                /* SHOW BUTTONS */
                showRotationControl: false,
                showSequenceControl: true,
                showHomeControl: true,
                showZoomControl: true,
                /* SEQUENCE */
                sequenceMode: true,
                showReferenceStrip: tools !== true,
                navigationControlAnchor: 'TOP_RIGHT',
            },
            _setViewer: (viewer) => {
                this.viewer = viewer;
                this.viewerLoaded$.next();
                // overlay test
                if (overlay_images) {
                    // FIXME: togliere mock
                    this.loadOverlays(data);
                }
            }
        };
    }
    changePage(index) {
        this.viewer.goToPage(index); // call to OpenSeadragon APIs
    }
    overlayCloseClick() {
        this.overlayModel.resetStyles();
    }
    loadOverlays(data) {
        this.overlayModel = new MrImageViewerOverlayModel({
            viewer: this.viewer,
            config: data,
            overlayEvents$: this.overlayEvents$
        });
        this.overlayModel.init();
    }
}

class MrImageViewerToolsDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        const { thumbs } = data;
        const images = data.images.map(({ caption }, thumbindex) => ({
            caption,
            thumb: thumbs[thumbindex],
            payload: { thumbindex }
        }));
        return {
            images,
            classes: '',
            navigation: data.images.length > 7 ? {
                prev: {
                    payload: 'prev',
                    classes: 'n7-image-viewer-tools__thumbs-scroll-left'
                },
                next: {
                    payload: 'next',
                    classes: 'n7-image-viewer-tools__thumbs-scroll-right'
                },
            } : null,
            controls: {
                description: images[0].caption ? {
                    icon: 'n7-icon-info1',
                    anchor: { payload: 'toggle-description' },
                    isActive: false,
                } : null,
                thumbs: {
                    icon: 'n7-icon-images',
                    anchor: { payload: 'toggle-thumbs' },
                    isActive: false,
                },
                closedescription: {
                    icon: 'n7-icon-close-circle',
                    anchor: { payload: 'close-description' }
                }
            },
            isVisible: {
                description: false,
                thumbnails: false,
            },
            description: images[0].caption,
            initial: 0
        };
    }
    toggleDescription() {
        this.output.isVisible.description = !this.output.isVisible.description;
        this.output.isVisible.thumbnails = false;
        this.output.controls.description.isActive = !this.output.controls.description.isActive;
        this.output.controls.thumbs.isActive = false;
    }
    toggleThumbs() {
        this.output.isVisible.thumbnails = !this.output.isVisible.thumbnails;
        this.output.isVisible.description = false;
        this.output.controls.thumbs.isActive = !this.output.controls.thumbs.isActive;
        if (this.output.controls.description) {
            this.output.controls.description.isActive = false;
        }
    }
    handleThumbs(index) {
        this.output.initial = index;
        this.updateDescription();
    }
    //
    scrollRight() {
        const thumbsStrip = document.querySelectorAll('div.n7-image-viewer-tools__thumbs-strip')[0];
        const rightArrow = document.querySelectorAll(`div.${this.output.navigation.next.classes}`)[0];
        const leftArrow = document.querySelectorAll(`div.${this.output.navigation.prev.classes}`)[0];
        const maxStrip = thumbsStrip.scrollWidth - thumbsStrip.clientWidth;
        const { scrollLeft } = thumbsStrip;
        if ((scrollLeft + 400) >= maxStrip) { // mettere value da config e.g. scrollStrength
            thumbsStrip.scrollBy({
                top: 0,
                left: +400,
                behavior: 'smooth'
            });
            rightArrow.style.opacity = '0.5';
        }
        else {
            thumbsStrip.scrollBy({
                top: 0,
                left: +400,
                behavior: 'smooth'
            });
        }
        if (((scrollLeft + 400) > 0)) {
            leftArrow.style.opacity = '1.0';
        }
    }
    scrollLeft() {
        const thumbsStrip = document.querySelectorAll('div.n7-image-viewer-tools__thumbs-strip')[0];
        const rightArrow = document.querySelectorAll(`div.${this.output.navigation.next.classes}`)[0];
        const leftArrow = document.querySelectorAll(`div.${this.output.navigation.prev.classes}`)[0];
        const maxStrip = thumbsStrip.scrollWidth - thumbsStrip.clientWidth;
        const { scrollLeft } = thumbsStrip;
        if ((scrollLeft - 400) <= 0) { // mettere value da config e.g. scrollStrength
            thumbsStrip.scrollBy({
                top: 0,
                left: -400,
                behavior: 'smooth'
            });
            leftArrow.style.opacity = '0.5';
        }
        else {
            thumbsStrip.scrollBy({
                top: 0,
                left: -400,
                behavior: 'smooth'
            });
        }
        if (((scrollLeft - 400) < maxStrip)) {
            rightArrow.style.opacity = '1.0';
        }
    }
    //
    handlePageChange(payload) {
        this.handleThumbs(payload.page);
    }
    updateDescription() {
        const index = this.output.initial;
        const { images } = this.output;
        this.output.description = images[index].caption;
        if (!this.output.description) {
            this.output.controls.description = null;
        }
        else {
            this.output.controls.description = {
                icon: 'n7-icon-info1',
                anchor: { payload: 'toggle-description' }
            };
        }
    }
}

class MrImageViewerOverlayDetailsDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        return {
            title: data.title,
            text: data.description,
            image: data.detail_image,
            classes: 'is-vertical'
        };
    }
    show(data) {
        this.run(data);
    }
    hide() {
        this.run(null);
    }
}

class MrInfoBoxDS extends DataSource {
    transform(data) {
        return data;
    }
}

class MrInnerTitleDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        const { title, description, button } = data;
        return {
            title: {
                main: {
                    text: title,
                    classes: 'bold'
                },
                secondary: {
                    text: description,
                    classes: 'italic'
                }
            },
            actions: button && button.link ? {
                buttons: [
                    {
                        anchor: {
                            href: linksHelper.getRouterLink(button.link),
                            queryParams: linksHelper.getQueryParams(button.link)
                        },
                        text: button.text,
                        classes: 'n7-btn-cta'
                    }
                ]
            } : null
        };
    }
}

const ITEM_PREVIEW_DEFAULTS$1 = {
    limit: 100,
    striptags: true
};
class MrItemPreviewDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        const { classes, itemPreview } = this.options;
        const { localeService } = this.options;
        const itemPreviewOptions = merge(ITEM_PREVIEW_DEFAULTS$1, (itemPreview || {}));
        // striptags
        if (itemPreviewOptions.striptags) {
            data.text = helpers.striptags(data.text);
        }
        // limit
        if (itemPreviewOptions.limit && (data.text.length > itemPreviewOptions.limit)) {
            data.text = `${data.text.substring(0, itemPreviewOptions.limit)}...`;
        }
        // link
        let anchor;
        if (data.routeId) {
            const routeLink = localeService.getLinkByRouteId(data.routeId, data.id, data.slug);
            anchor = {
                href: routeLink,
                queryParams: data.params || null,
            };
        }
        else if (data.link) {
            anchor = {
                href: linksHelper.getRouterLink(data.link),
                queryParams: linksHelper.getQueryParams(data.link),
            };
        }
        return {
            ...data,
            anchor,
            classes: classes || ''
        };
    }
}

class MrItemPreviewsDS extends DataSource {
    constructor() {
        super(...arguments);
        // ===== MOCK DATA =====
        this.mock = {
            resources: [
                {
                    image: 'https://i.imgur.com/8bNcgR6.png',
                    title: 'Unattributed version',
                    text: 'A japanese colored version',
                    metadata: [{
                            classes: 'metadata',
                            items: [
                                { label: 'Artista', value: 'Massimo Berruti' },
                                { label: 'Tecnica', value: 'Fotografia' },
                                { label: 'Galleria', value: 'Galleria Tonelli' },
                            ]
                        }]
                }, {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'Yudi Shanhai Quantu',
                    text: 'Complete Map of all mountains and seas',
                }, {
                    image: 'https://i.imgur.com/sLu7u2v.png',
                    title: 'Reconstruction of D\'Elia\'s map',
                    text: 'A digital collage of the map portions from Pasquale D\'Elia "mappamondo"',
                    metadata: [{
                            classes: 'metadata',
                            items: [
                                { label: 'Artista', value: 'Massimo Berruti' },
                                { label: 'Tecnica', value: 'Fotografia' },
                                { label: 'Galleria', value: 'Galleria Tonelli' },
                            ]
                        }]
                }, {
                    image: 'https://i.imgur.com/8bNcgR6.png',
                    title: 'Unattributed version',
                    text: 'A japanese colored version',
                }
            ],
            collections: [
                {
                    image: 'https://i.imgur.com/8bNcgR6.png',
                    title: 'Unattributed version',
                    text: 'A japanese colored version',
                }, {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'Yudi Shanhai Quantu',
                    text: 'Complete Map of all mountains and seas',
                }, {
                    image: 'https://i.imgur.com/sLu7u2v.png',
                    title: 'Reconstruction of D\'Elia\'s map',
                    text: 'A digital collage of the map portions from Pasquale D\'Elia "mappamondo"',
                }
            ],
            search: [
                {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'Yudi Shanhai Quantu',
                    text: 'Complete Map of all mountains and seas',
                }, {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'World Map based on Matteo Ricci 1850',
                    text: 'Complete Map fo all mountains and seas',
                }, {
                    image: '',
                    title: 'Reconstruction of D\'Elia\'s map',
                    text: 'A digital collage of the map portions from Pasquale D\'Elia "mappamondo"',
                }, {
                    image: '',
                    title: 'Unattributed version',
                    text: 'A japanese colored version',
                }, {
                    image: '',
                    title: 'Matteo Ricci\'s way from Macau to Beijing',
                    text: 'A japanese colored version',
                }, {
                    image: '',
                    title: 'The 400-year-old map that shows China as the centre of the world',
                    text: 'A japanese colored version',
                }, {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'Yudi Shanhai Quantu',
                    text: 'Complete Map of all mountains and seas',
                }, {
                    image: 'https://i.imgur.com/52UFqca.png',
                    title: 'World Map based on Matteo Ricci 1850',
                    text: 'Complete Map fo all mountains and seas',
                }, {
                    image: '',
                    title: 'Reconstruction of D\'Elia\'s map',
                    text: 'A digital collage of the map portions from Pasquale D\'Elia "mappamondo"',
                }, {
                    image: '',
                    title: 'Unattributed version',
                    text: 'A japanese colored version',
                }, {
                    image: '',
                    title: 'Matteo Ricci\'s way from Macau to Beijing',
                    text: 'A japanese colored version',
                }, {
                    image: '',
                    title: 'The 400-year-old map that shows China as the centre of the world',
                    text: 'A japanese colored version',
                }
            ]
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transform(data) {
        return this.mock[this.options.source];
    }
}

const MARKER_ICON = L.icon({
    iconUrl: '/assets/pin.png',
    iconSize: [20, 30],
    popupAnchor: [0, -20],
    iconAnchor: [10, 30],
    className: 'marker-icon'
});
const MARKER_ICON_SELECTED = L.icon({
    iconUrl: '/assets/pin-selected.png',
    iconSize: [20, 30],
    popupAnchor: [0, -20],
    className: 'marker-icon-selected'
});
class MrMapDS extends DataSource {
    constructor() {
        super(...arguments);
        this.mapLoaded$ = new Subject();
        this.getMarkerById = (id) => {
            let marker = null;
            this.mapInstance.eachLayer((layer) => {
                if (layer instanceof L.Marker) {
                    if (`${layer.id}` === id) {
                        marker = layer;
                    }
                }
            });
            return marker;
        };
        this.getSelectedMarker = () => {
            let marker = null;
            this.mapInstance.eachLayer((layer) => {
                if (layer instanceof L.Marker) {
                    const { icon } = layer.options || {};
                    const className = icon?.options?.className || '';
                    const isSelected = className.includes('selected');
                    if (isSelected) {
                        marker = layer;
                    }
                }
            });
            return marker;
        };
    }
    // eslint-disable-next-line consistent-return
    transform(data) {
        let markers;
        const d = data.find((z) => z.zoom);
        if (data.find((a) => a.markers)) {
            markers = data
                .map((area) => (area.markers
                .map((m) => ({
                // convert to leaflet marker format
                coords: [+m.lat, +m.lng],
                template: m.default_label ?? m.label,
                title: m.label ?? m.default_label,
                id: area.id,
                slug: area.slug,
            }))))
                // flatten the list of markers
                .reduce((acc, val) => acc.concat(val), []);
        }
        const mapCenter = d.map_center ? [d.map_center.lat, d.map_center.lng]
            : [54.5260, 15.2551];
        const initialView = {
            // center of europe (only for initial load)
            center: mapCenter,
            zoom: d.zoom,
        };
        // if the map and the markers already exist
        // update the already existing layers.
        if (this.mapInstance && this.markerLayer) {
            this.buildMarkers(markers);
            this.fitMapToBounds(markers.map((m) => m.coords), d.zoom);
        }
        return {
            // only called once, on component init!
            _setInstance: (instance) => {
                this.mapInstance = instance;
                // center the map on the markers
                this.fitMapToBounds(markers.map((m) => m.coords), d.zoom);
                // load custom markers
                this.buildMarkers(markers);
                this.mapLoaded$.next({ map: instance, markers: this.markerLayer });
            },
            containerId: 'map-canvas',
            libOptions: {
                attributionControl: false,
                ...this.options.libOptions,
            },
            tileLayers: [{
                    // url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
                    // url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png',
                    options: {}
                }],
            initialView,
        };
    }
    updateMarkersState(id) {
        if (id) {
            const marker = this.getMarkerById(id);
            if (marker) {
                marker.setIcon(MARKER_ICON_SELECTED);
                marker.openPopup();
            }
        }
        else {
            const marker = this.getSelectedMarker();
            if (marker) {
                marker.setIcon(MARKER_ICON);
                marker.closePopup();
            }
        }
    }
    fitMapToBounds(bounds, zoom = 10) {
        if (this.mapInstance) {
            this.mapInstance.fitBounds(bounds, {
                maxZoom: zoom,
                padding: [20, 20]
            });
        }
        else {
            console.warn('map instance is missing');
        }
    }
    /**
     * Builds markers with a custom icon and adds them to the map.
     * @param markers an array of markers
     */
    buildMarkers(markers) {
        if (!markers)
            return;
        // remove all existing markers
        if (this.markerLayer) {
            this.markerLayer.clearLayers();
            this.mapInstance.removeLayer(this.markerLayer);
        }
        const markerGroup = L.markerClusterGroup({
            maxClusterRadius: 10,
            disableClusteringAtZoom: 8
        });
        markers.forEach(({ coords, template, id, slug }) => {
            // create custom icon marker
            const newMarker = L.marker(coords, { icon: MARKER_ICON });
            if (id && slug) {
                newMarker.id = id;
                newMarker.slug = slug;
            }
            newMarker
                // add the marker to the group
                .addTo(markerGroup)
                // add the on-click tooltip
                .bindPopup(template, {
                closeButton: false
            });
            newMarker.getPopup().on('remove', ({ target }) => {
                target._source.setIcon(MARKER_ICON);
            });
            newMarker.getPopup().on('add', ({ target }) => {
                target._source.setIcon(MARKER_ICON_SELECTED);
            });
        });
        // add the markers to the map instance
        this.mapInstance.addLayer(markerGroup);
        // update the marker layer instance
        this.markerLayer = markerGroup;
    }
}

class MrMetadataDS extends DataSource {
    constructor() {
        super(...arguments);
        /** Test if a string is a valid URL */
        this.isUrl = /^(?:http(s)?:\/\/)[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
        /** Turn a string into an anchor element */
        this.toUrl = (string) => `<a href="${string}" target="_blank">${string}<a>`;
        this.defaultReadmore = {
            height: 300,
            labels: {
                more: _t('global#readmore'),
                less: _t('global#readless')
            }
        };
    }
    transform(data) {
        if (!data)
            return null;
        // readmore applies to the whole metadata group, while
        // groupReadmore applies to the nested metadata sub-groups
        const { hideLabels, readmore, groupReadmore } = this.options;
        const { group } = data;
        if (!(group || []).length) {
            return null;
        }
        const result = { group: [] };
        group
            .filter(({ items }) => Array.isArray(items))
            .forEach(({ items }) => {
            items
                .filter((item) => isObject(item))
                .forEach(({ label, value }) => {
                const itemLabel = label && !hideLabels ? label : null;
                const classLabel = label && !hideLabels ? label.replace(/ /g, '_').toLowerCase() : null;
                if (Array.isArray(value)) {
                    result.group.push({
                        group: [{
                                title: _t(itemLabel),
                                classes: `mrc-${classLabel}`,
                                // use default values if not specified
                                readmore: merge({ ...this.defaultReadmore }, readmore),
                                groupReadmore,
                                ...this.getItemGroup(value)
                            }]
                    });
                }
                else {
                    result.group.push({
                        group: [{
                                // use default values if not specified
                                readmore: merge({ ...this.defaultReadmore }, readmore),
                                groupReadmore,
                                classes: `mrc-${classLabel}`,
                                items: value ? [{
                                        label: _t(itemLabel),
                                        value: this.getItemValue(value)
                                    }] : []
                            }]
                    });
                }
            });
        });
        return result;
    }
    getItemGroup(value) {
        const { groupReadmore } = this.options;
        if (Array.isArray(value) && Array.isArray(value[0])) {
            return {
                group: value.map((val) => ({
                    ...this.getItemGroup(val)
                }))
            };
        }
        return {
            items: value
                .filter((childItem) => !!childItem.value)
                .map((childItem) => ({
                label: _t(childItem.label),
                value: this.getItemValue(childItem.value),
            })),
            // load the optional "readmore" configuration
            groupReadmore,
        };
    }
    getItemValue(value) {
        return this.isUrl.test(value) ? this.toUrl(value) : value;
    }
}

class MrNavDS extends DataSource {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transform(data) {
        const items = [];
        data.nav.forEach((el) => {
            items.push({
                text: el.title,
                anchor: {
                    href: `http://localhost:4200/mr/static/${el.id}`,
                    target: '_blank',
                    payload: el.id
                }
            });
        });
        return {
            items,
        };
    }
}

class MrResourceTabsDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        const { currentTab, root, slug, id: resourceId } = this.options;
        const { localeService } = this.options;
        let baseUrl = root;
        if (typeof root !== 'string') {
            const locale = localeService.getLocale();
            baseUrl = root[locale];
        }
        return data.map(({ id, label }) => ({
            label: _t(label),
            classes: currentTab === id ? 'is-active' : '',
            anchor: {
                href: `/${baseUrl}/${resourceId}/${slug}/${id}`
            }
        }));
    }
}

class MrTextViewerDS extends DataSource {
    transform(data) {
        const { enableClickOnEntities, toggleColumn, searchId, searchApi } = this.options || {};
        data.toggleColumn = toggleColumn;
        // force tei publisher endpoint value
        document.addEventListener('pb-page-ready', (ev) => {
            const { detail } = ev;
            detail.endpoint = data.endpoint;
        }, { once: true });
        const params = new URLSearchParams(document.location.search);
        const id = params.get('id');
        if (data.docs[0]?.view === 'page' && id) {
            data.docs[0].view = 'div';
            document.addEventListener('pb-end-update', () => {
                setTimeout(() => {
                    document.dispatchEvent(new CustomEvent('pb-toggle', {
                        detail: {
                            properties: {
                                view: 'page',
                            },
                            action: 'refresh',
                            key: 'transcription',
                        },
                    }));
                    document.dispatchEvent(new CustomEvent('pb-toggle', {
                        detail: {
                            properties: {
                                view: 'single',
                            },
                            action: 'refresh',
                            key: 'addChannel',
                        },
                    }));
                    document.dispatchEvent(new CustomEvent('pb-toggle', {
                        detail: {
                            properties: {
                                view: 'single',
                                id: '',
                            },
                            action: 'refresh',
                            key: 'index',
                        },
                    }));
                }, 500);
            }, { once: true });
        }
        if (enableClickOnEntities) {
            document.addEventListener('pb-end-update', (ev) => {
                this.scrollElementsIntoView(ev.detail, 'chapter', '#view1');
            });
        }
        if (params.get('hq')) {
            if (searchApi) {
                const xmlQueryUrl = `${searchApi.url}?resource-id=${searchApi['resource-id']}&searchId=${searchId}&xml=${data.docs[0]?.xml}&${params.toString()}`;
                data.docs[0].url = xmlQueryUrl;
                data.docs[0].rootPath = 'api/mrcparts';
            }
            document.addEventListener('pb-end-update', (ev) => {
                this.scrollElementsIntoView(ev.detail, 'hq', '#view0');
            });
        }
        return data;
    }
    displayIndex() {
        if (this.output.toggleColumn) {
            this.output.toggleColumn = false;
        }
        else {
            this.output.toggleColumn = true;
        }
    }
    onClick(payload) {
        if (this.output.toggleColumn === false) {
            let target = null;
            if (payload.path) {
                target = payload.path.find(({ tagName }) => tagName === 'PB-HIGHLIGHT');
            }
            else {
                target = payload.composedPath().find(({ tagName }) => tagName === 'PB-HIGHLIGHT');
            }
            if (target.__key
                && (target.className.includes('person')
                    || target.className.includes('place'))) {
                this.output.toggleColumn = true;
                this.scrollElementsIntoView(target, 'entity', '#text-viewer-index');
            }
        }
        else
            this.output.toggleColumn = false;
    }
    viewListenerUpdate() {
        document.addEventListener('pb-start-update', function _listener() {
            document.removeEventListener('pb-start-update', _listener, true);
        }, true);
    }
    changeView(view, refresh) {
        setTimeout((v, r) => {
            // console.log(`TEST CHANGE VIEW: ${view}`);
            document.dispatchEvent(new CustomEvent('pb-toggle', {
                detail: {
                    properties: {
                        view: v,
                    },
                    action: r ? 'refresh' : '',
                    key: 'transcription',
                },
            }));
        }, 600, view, refresh);
    }
    scrollElementsIntoView(target, type, view) {
        setTimeout(() => {
            let element;
            if (type === 'chapter') {
                if (document
                    .querySelector('.n7-text-viewer #view0')
                    .shadowRoot.querySelector('pb-highlight')) {
                    const highlight = document
                        .querySelector('.n7-text-viewer #view0')
                        .shadowRoot.querySelectorAll('pb-highlight');
                    const highlightId = highlight[highlight.length - 1].id; // s.x
                    const fragmentNumber = highlightId.replace('.', '\\.');
                    element = document
                        .querySelector(view)
                        .shadowRoot.querySelector(`#${fragmentNumber}`);
                }
            }
            else if (type === 'entity') {
                const key = target.__key;
                element = document
                    .querySelector(`.n7-text-viewer ${view}`)
                    .shadowRoot.querySelector(`#${key}`);
            }
            else if (type === 'hq') {
                element = document
                    .querySelector(`.n7-text-viewer ${view}`)
                    .shadowRoot.querySelector('.tei-em');
            }
            const container = document.querySelector(`.n7-text-viewer ${view}`);
            if (element) {
                container.scrollTop = element.offsetTop;
            }
        }, 600);
    }
}

class MrTimelineDS extends DataSource {
    constructor() {
        super(...arguments);
        this.timelineLoaded$ = new Subject();
    }
    transform(data) {
        return {
            containerID: 'mr-timeline',
            libOptions: {
                height: '500px',
                locale: 'it_IT',
                align: 'left',
                showTooltips: false,
                tooltip: {
                    followMouse: false,
                    template: (d, element) => `<div class="tooltip">${element.title}</div>`
                },
                width: '100%',
                minHeight: '350px',
                maxHeight: '800px',
                zoomFriction: 8,
                ...this.options.libOptions
            },
            dataSet: data.dataSet.map((d) => {
                // Show dates that have identical start and end dates as points
                if (d.end && d.end === d.start) {
                    return { ...d, ...{ end: undefined } };
                }
                return d;
            }),
            _setInstance: (timeline) => {
                this.timeline = timeline;
                this.timelineLoaded$.next(timeline);
            }
        };
    }
}

class MrYearHeaderDS extends DataSource {
    transform(data) {
        return data;
    }
}

class MrGalleryDS extends DataSource {
    transform(data) {
        if (!data) {
            return null;
        }
        return {
            selected: null,
            items: data.map(({ id, title, thumbnail, image }) => ({
                id,
                title,
                thumbSrc: thumbnail,
                fullSrc: image,
                payload: id
            }))
        };
    }
    setSelected(itemId) {
        this.output.selected = this.output.items.find(({ id }) => id === itemId);
    }
    removeSelected() {
        this.output.selected = null;
    }
}

class MrSearchPageTitleDS extends DataSource {
    transform() {
        const { title, description, searchId } = this.options.config;
        const data = {
            title: {
                main: {
                    text: _t(title)
                }
            }
        };
        if (description && description.buttonText) {
            data.actions = {
                buttons: [{
                        text: _t(description.buttonText),
                        anchor: {
                            payload: searchId
                        }
                    }]
            };
        }
        return data;
    }
}

class MrSearchResultsTitleDS extends DataSource {
    transform(data) {
        const { totalResultsText, sort } = this.options.config;
        const { total_count: totalCount, sort: currentSort } = data;
        const mainText = _t(totalResultsText, { total: totalCount }, (key, { total }) => {
            if (total === 0) {
                return `${key}_0`;
            }
            if (total === 1) {
                return `${key}_1`;
            }
            return key;
        });
        return {
            title: {
                main: {
                    text: mainText
                }
            },
            actions: {
                select: {
                    label: sort.label,
                    options: sort.options.map(({ label, value, selected, disabled }) => ({
                        value,
                        disabled,
                        selected: currentSort ? value === currentSort : selected,
                        text: label
                    })),
                    payload: 'sort'
                }
            }
        };
    }
    OnInputQueryChange(value) {
        const { sort } = this.options.config;
        sort.options.forEach((option) => {
            if (option.value === '_score') {
                option.disabled = !value;
            }
        });
        if (this.input) {
            this.update(this.input);
        }
    }
}

const ITEM_PREVIEW_DEFAULTS = {
    limit: 100,
    striptags: true
};
class MrSearchResultsDS extends DataSource {
    transform(data) {
        const { results } = data;
        const { itemPreview, highlights: highlightsOptions } = this.options.config;
        const itemPreviewOptions = merge(clone(ITEM_PREVIEW_DEFAULTS), (itemPreview || {}));
        return results.map((item) => {
            if (typeof item.text === 'string') {
                // striptags
                if (itemPreviewOptions.striptags) {
                    item.text = helpers.striptags(item.text);
                }
                // limit
                if (itemPreviewOptions.limit && (item.text.length > itemPreviewOptions.limit)) {
                    item.text = `${item.text.substring(0, itemPreviewOptions.limit)}...`;
                }
            }
            // metadata
            const metadata = [];
            if (Array.isArray(item.metadata)) {
                item.metadata.forEach((group) => {
                    const items = [];
                    (group.items || []).forEach((metadataItem) => {
                        items.push({
                            ...metadataItem,
                            label: _t(metadataItem.label)
                        });
                    });
                    metadata.push({ items });
                });
            }
            // link
            const anchor = this.getItemPreviewAnchor(item, itemPreview?.linkTarget);
            /*
              Add the highlights to the item's metadata with a custom group
            */
            const highlights = [];
            if (item.highlights) {
                const highlightGroup = {
                    title: _t('advancedsearch#highlights_title'),
                    items: [],
                    classes: 'n7-item-preview__highlights'
                };
                item.highlights.forEach((highlight) => {
                    // if the item is an array interpret it as [label, [value]]
                    if (Array.isArray(highlight)) {
                        highlightGroup.items.push({
                            label: _t(highlight[0]),
                            value: _t(highlight[1][0])
                        });
                        // if it's an object then it should have a custom hyperlink
                    }
                    else {
                        const href = this.getHighlightLink(highlight, anchor.href);
                        highlightGroup.items.push({
                            label: highlight.label ? _t(highlight.label) : undefined,
                            value: highlight.text ?? '',
                            target: itemPreview?.linkTarget || '_blank',
                            href, // custom hyperlink
                        });
                    }
                });
                highlights.push(highlightGroup);
            }
            return {
                ...item,
                metadata,
                anchor,
                highlights,
                highlightsHidden: !!highlightsOptions?.hidden,
                highlightsHasToggle: !!highlightsOptions?.hasToggle,
                classes: itemPreviewOptions.classes,
            };
        });
    }
    getItemPreviewAnchor(item, target = '') {
        const { localeService } = this.options;
        let anchor = null;
        if (item.routeId) {
            const routeLink = localeService.getLinkByRouteId(item.routeId, `${item.id}`, item.slug);
            anchor = {
                href: routeLink,
                queryParams: item.params || null,
            };
        }
        else if (item.link) {
            anchor = {
                href: linksHelper.getRouterLink(item.link),
                queryParams: linksHelper.getQueryParams(item.link),
            };
        }
        else if (item.payload) {
            anchor = {
                payload: {
                    ...item.payload
                }
            };
        }
        if (item.routeId || item.link) {
            anchor.target = target || '_blank';
        }
        return anchor;
    }
    getHighlightLink(highlight, itemLink) {
        let href = '';
        if (!highlight.link) {
            return href;
        }
        if (highlight.link.absolute) {
            // path is absolute
            href = `${highlight.link.absolute}`;
        }
        else {
            href = this.getHighlightRelativeUrl(highlight.link, itemLink);
            // path is relative to the item-preview url
        }
        return this.getHighlightParams(highlight.link, href);
    }
    getHighlightRelativeUrl(highlightLink, href) {
        if (!highlightLink) {
            return '';
        }
        if (typeof highlightLink === 'string') {
            return `${href}${highlightLink}`;
        }
        if (highlightLink.relative) {
            return `${href}${highlightLink.relative}`;
        }
        return href;
    }
    getHighlightParams(highlightLink, href) {
        const params = highlightLink.params ? [highlightLink.params] : [];
        // includes current query _string
        if (highlightLink.query_string) {
            params.push(document.location.search);
        }
        return linksHelper.joinQueryParams(href, params);
    }
}

class MrSearchTagsDS extends DataSource {
    constructor() {
        super(...arguments);
        this.hasFilters = false;
    }
    transform(data) {
        const { state, linksResponse, facetsConfig } = data;
        const { facets } = linksResponse;
        const tags = [];
        // inputs config
        facetsConfig.sections.forEach(({ inputs }) => {
            inputs
                .filter(({ queryParam }) => queryParam)
                .forEach(({ id }) => {
                if (state[id] || state[id] === 0) {
                    const values = Array.isArray(state[id]) ? state[id] : [state[id]];
                    values
                        .forEach((value) => {
                        let text = `${value}`;
                        if (facets[id]) {
                            const selectedFacet = facets[id].values.find(({ payload }) => payload === value);
                            const cachedTag = Array.isArray(this.output)
                                ? this.output.find(({ payload }) => payload.value === value)
                                : null;
                            if (selectedFacet?.text) {
                                text = selectedFacet.text;
                            }
                            else if (cachedTag?.text) {
                                text = cachedTag?.text;
                            }
                        }
                        tags.push({
                            text,
                            icon: 'n7-icon-close',
                            payload: {
                                id,
                                value
                            }
                        });
                    });
                }
            });
        });
        this.hasFilters = !!tags.length;
        return tags;
    }
}

class MrSearchPageDescriptionDS extends DataSource {
    transform(data) {
        const { description } = this.options.config;
        if (!description) {
            return null;
        }
        const { linkText } = description;
        const { text } = data;
        return {
            text,
            link: {
                text: _t(linkText),
                payload: true
            }
        };
    }
}

var dateHelper = {
    format(date, format) {
        return dayjs(date).format(format);
    }
};

class MrStaticMetadataDS extends DataSource {
    transform(data) {
        const items = ['authors', 'date', 'time_to_read']
            .filter((metakey) => data[metakey])
            .map((metakey) => {
            const itemValue = metakey === 'date' ? dateHelper.format(data[metakey], _t('global#date_human')) : data[metakey];
            return {
                label: _t(`resource#${metakey}`),
                value: itemValue
            };
        });
        return { group: [{ items }] };
    }
}

const ICON_OPEN$2 = 'n7-icon-angle-up';
const ICON_CLOSE$2 = 'n7-icon-angle-down';
class MrFormWrapperAccordionDS extends DataSource {
    transform(data) {
        const { form } = data;
        const { groups } = form.config;
        // set accordion headers
        data.form.config.groups = groups.map((group) => ({
            ...group,
            options: {
                ...group.options,
                text: group.options.label,
                payload: group.id,
                iconRight: group.options.isOpen ? ICON_OPEN$2 : ICON_CLOSE$2,
                isOpen: group.options.isOpen
            }
        }));
        return data;
    }
    toggleGroup(groupId) {
        this.output.form.config.groups.forEach((group) => {
            if (group.id === groupId) {
                const { isOpen } = group.options;
                group.options.iconRight = isOpen ? ICON_CLOSE$2 : ICON_OPEN$2;
                group.options.isOpen = !group.options.isOpen;
            }
        });
    }
    checkInfoTooltips() {
        setTimeout(() => {
            tippy(document.querySelectorAll('.mr-input-info'), {
                content: (reference) => reference.getAttribute('alt'),
            });
        }, 1000);
    }
}

class MrAdvancedSearchTagsDS extends DataSource {
    transform(data) {
        const { labels } = this.options;
        return Object.keys(data).map((key) => ({
            text: `${labels[key] || key}: ${_t(data[key])}`
        }));
    }
}

const ICON_OPEN$1 = 'n7-icon-angle-up';
const ICON_CLOSE$1 = 'n7-icon-angle-down';
class MrMetadataDynamicDS extends DataSource {
    transform(data) {
        if (!data)
            return null;
        // set accordion headers and options
        data.data = data.data.map((group, index) => ({
            ...group,
            groupId: index,
            options: {
                ...group.options,
                text: group.title,
                label: group.title,
                payload: index,
                isOpen: index === 0,
                showHeader: true,
                iconRight: index === 0 ? ICON_OPEN$1 : ICON_CLOSE$1,
            }
        }));
        return data;
    }
    prepareMeta(data) {
        const metadataDS = new MrMetadataDS();
        return metadataDS.transform(data);
    }
    toggleGroup(payload) {
        this.output.data.forEach((group) => {
            if (group.groupId === payload) {
                const { options } = group;
                const { isOpen } = options;
                options.iconRight = isOpen ? ICON_CLOSE$1 : ICON_OPEN$1;
                options.isOpen = !isOpen;
            }
        });
    }
}

var DS = /*#__PURE__*/Object.freeze({
    __proto__: null,
    MrBreadcrumbsDS: MrBreadcrumbsDS,
    MrCollectionDS: MrCollectionDS,
    MrContentDS: MrContentDS,
    MrFiltersDS: MrFiltersDS,
    MrHeroDS: MrHeroDS,
    MrImageViewerDS: MrImageViewerDS,
    MrImageViewerToolsDS: MrImageViewerToolsDS,
    MrImageViewerOverlayDetailsDS: MrImageViewerOverlayDetailsDS,
    MrInfoBoxDS: MrInfoBoxDS,
    MrInnerTitleDS: MrInnerTitleDS,
    MrItemPreviewDS: MrItemPreviewDS,
    MrItemPreviewsDS: MrItemPreviewsDS,
    MrMapDS: MrMapDS,
    MrMetadataDS: MrMetadataDS,
    MrNavDS: MrNavDS,
    MrResourceTabsDS: MrResourceTabsDS,
    MrTextViewerDS: MrTextViewerDS,
    MrTimelineDS: MrTimelineDS,
    MrYearHeaderDS: MrYearHeaderDS,
    MrGalleryDS: MrGalleryDS,
    MrSearchPageTitleDS: MrSearchPageTitleDS,
    MrSearchResultsTitleDS: MrSearchResultsTitleDS,
    MrSearchResultsDS: MrSearchResultsDS,
    MrSearchTagsDS: MrSearchTagsDS,
    MrSearchPageDescriptionDS: MrSearchPageDescriptionDS,
    MrStaticMetadataDS: MrStaticMetadataDS,
    MrFormWrapperAccordionDS: MrFormWrapperAccordionDS,
    MrAdvancedSearchTagsDS: MrAdvancedSearchTagsDS,
    MrMetadataDynamicDS: MrMetadataDynamicDS
});

class MrDummyEH extends EventHandler {
    listen() {
        // TODO
    }
}

class MrFiltersEH extends EventHandler {
    listen() {
        // TODO
    }
}

class MrNavEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'dv-nav.click':
                    this.emitOuter('navclick', payload);
                    break;
                default:
                    console.warn('unhandled event of type', type);
                    break;
            }
        });
    }
}

class MrTimelineEH extends EventHandler {
    listen() {
        // this.innerEvents$.subscribe(({ type, payload }) => {
        //   switch (type) {
        //     case `${this.dataSource.id}.<event-type>`:
        //       // TODO
        //       break;
        //     default:
        //       break;
        //   }
        // });
    }
}

class MrYearHeaderEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type }) => {
            switch (type) {
                case 'mr-year-header.click':
                    this.emitOuter('closeevent');
                    break;
                default:
                    break;
            }
        });
    }
}

class MrGalleryEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.click`:
                    this.dataSource.setSelected(payload);
                    break;
                case `${this.dataSource.id}.close`:
                    this.dataSource.removeSelected();
                    break;
                default:
                    break;
            }
        });
    }
}

class MrCollectionEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.click`: {
                    const { action } = payload;
                    if (action === 'resource-modal') {
                        this.emitOuter('openresourcemodal', payload);
                    }
                    break;
                }
                default:
                    break;
            }
        });
    }
}

class MrTextViewerEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.hostId}.click`:
                    this.dataSource.onClick(payload);
                    break;
                case `${this.hostId}.togglecolumn`:
                    this.dataSource.displayIndex();
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
    }
}

class MrSearchTagsEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-tags.click':
                    this.emitOuter('click', payload);
                    break;
                default:
                    break;
            }
        });
    }
}

class MrSearchResultsTitleEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-results-title.change':
                    this.emitOuter('change', payload);
                    break;
                default:
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-layout.inputquerychange':
                    this.dataSource.OnInputQueryChange(payload);
                    break;
                default:
                    break;
            }
        });
    }
}

class MrSearchPageTitleEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-page-title.click':
                    this.emitOuter('click', payload);
                    break;
                default:
                    break;
            }
        });
    }
}

class MrSearchPageDescriptionEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-page-description.click':
                    this.emitOuter('click', payload);
                    break;
                default:
                    break;
            }
        });
    }
}

class MrSearchResultsEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-results.click': {
                    const { action } = payload;
                    if (action === 'resource-modal') {
                        this.emitOuter('openresourcemodal', payload);
                    }
                    break;
                }
                default:
                    break;
            }
        });
    }
}

class MrFormWrapperAccordionEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-form-wrapper-accordion.init':
                    this.listenKeyUpEvents();
                    this.dataSource.checkInfoTooltips();
                    break;
                case 'mr-form-wrapper-accordion.destroy':
                    this.destroy$.next();
                    break;
                case 'mr-form-wrapper-accordion.submit': {
                    const { form } = this.dataSource.output;
                    this.emitOuter('submit', {
                        state: form.getState()
                    });
                    break;
                }
                case 'mr-form-wrapper-accordion.reset':
                    this.emitOuter('reset');
                    break;
                case 'mr-form-wrapper-accordion.click':
                    this.dataSource.toggleGroup(payload);
                    this.dataSource.checkInfoTooltips();
                    break;
                default:
                    break;
            }
        });
    }
    listenKeyUpEvents() {
        const keyup$ = fromEvent(window, 'keyup');
        keyup$.pipe(filter((event) => event.key === 'Enter'), takeUntil(this.destroy$)).subscribe(() => {
            this.emitInner('submit');
        });
    }
}

class MrMetadataDynamicEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'metadata-contenuti.click':
                    this.dataSource.toggleGroup(payload);
                    break;
                default:
                    break;
            }
        });
    }
}

class MrMapEH extends EventHandler {
    listen() {
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-map-layout.routechanged':
                    if (!this.dataSource.mapInstance) {
                        this.dataSource.mapLoaded$.pipe(delay(1000), // markers loaded timeout
                        first()).subscribe(() => {
                            this.dataSource.updateMarkersState(payload);
                        });
                    }
                    else {
                        this.dataSource.updateMarkersState(payload);
                    }
                    break;
                default:
                    break;
            }
        });
    }
}

var EH = /*#__PURE__*/Object.freeze({
    __proto__: null,
    MrDummyEH: MrDummyEH,
    MrFiltersEH: MrFiltersEH,
    MrNavEH: MrNavEH,
    MrTimelineEH: MrTimelineEH,
    MrYearHeaderEH: MrYearHeaderEH,
    MrGalleryEH: MrGalleryEH,
    MrCollectionEH: MrCollectionEH,
    MrTextViewerEH: MrTextViewerEH,
    MrSearchTagsEH: MrSearchTagsEH,
    MrSearchResultsTitleEH: MrSearchResultsTitleEH,
    MrSearchPageTitleEH: MrSearchPageTitleEH,
    MrSearchPageDescriptionEH: MrSearchPageDescriptionEH,
    MrSearchResultsEH: MrSearchResultsEH,
    MrFormWrapperAccordionEH: MrFormWrapperAccordionEH,
    MrMetadataDynamicEH: MrMetadataDynamicEH,
    MrMapEH: MrMapEH
});

const MrAdvancedResultsLayoutConfig = {
    layoutId: 'mr-advanced-results-layout',
    widgets: [
        {
            id: 'mr-search-page-title'
        }, {
            id: 'mr-search-results-title'
        }, {
            id: 'mr-search-results'
        }, {
            id: 'n7-smart-pagination',
            dataSource: SmartPaginationDS,
            eventHandler: SmartPaginationEH,
        }, {
            id: 'mr-advanced-search-tags'
        }
    ],
    layoutDS: MrAdvancedResultsLayoutDS,
    layoutEH: MrAdvancedResultsLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class MrAdvancedResultComponent {
    constructor() {
        /** Returns true if there are some highlights to render */
        this.hasHighlights = () => this.data
            ?.highlights
            // there is at least one group that has highlights
            ?.some((d) => d.items.length > 0);
    }
    isHidden() {
        if (this.data.highlightsHasToggle) {
            if (!this.data.highlightsTitle)
                return false;
            return this.data.highlightsHidden;
        }
        return false;
    }
    onClick(payload) {
        if (!this.emit)
            return;
        this.emit('click', payload);
    }
    onHeaderClick(data) {
        if (!data.highlightsHasToggle)
            return;
        data.highlightsHidden = !data.highlightsHidden;
    }
}
MrAdvancedResultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedResultComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrAdvancedResultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrAdvancedResultComponent, selector: "mr-advanced-result", inputs: { data: "data", emit: "emit" }, ngImport: i0, template: "<div *ngIf=\"data\"\n     class=\"mr-advanced-result\">\n     <!-- RESULT MAIN CONTENT -->\n    <n7-item-preview [data]=\"data\"></n7-item-preview>\n    <!-- RESULT HIGHLIGHTS SECTION -->\n    <ng-container *ngIf=\"hasHighlights()\">\n        <div *ngIf=\"data?.highlightsTitle\" class=\"mr-advanced-result__header\"\n        [ngClass]=\"{\n            'has-toggle': !!data.highlightsHasToggle\n        }\"\n        (click)=\"onHeaderClick(data)\">\n            <span class=\"mr-advanced-result__header-label\">\n                {{ data.highlightsTitle }}\n            </span>\n            <span class=\"mr-advanced-result__header-icon\"\n            [ngClass]=\"{\n                'n7-icon-angle-down': data.highlightsHidden,\n                'n7-icon-angle-up': !data.highlightsHidden\n            }\">\n            </span>\n        </div>\n        <div [hidden]=\"isHidden()\" \n        class=\"mr-advanced-result__content\"\n            *ngFor=\"let highlightGroup of data.highlights\">\n            <div class=\"mr-advanced-result__content-group\">\n                <!-- METADATA ITEM -->\n                <div class=\"mr-advanced-result__item {{ item.classes || '' }}\"\n                    *ngFor=\"let item of highlightGroup.items\">\n                    <!-- ICON -->\n                    <span class=\"mr-advanced-result__icon {{item.icon}}\"\n                        *ngIf=\"item.icon\">\n                    </span>\n                    <!-- LABEL -->\n                    <span class=\"mr-advanced-result__label\"\n                        *ngIf=\"item.label\"\n                        [innerHTML]=\"item.label\">\n                    </span>\n                    <!-- VALUE W/ HREF -->\n                    <a *ngIf=\"item.href\"\n                    [href]=\"item.href\"\n                    [target]=\"item.target\"\n                    >\n                        <span class=\"mr-advanced-result__value\"\n                            *ngIf=\"item.value\"\n                            [innerHTML]=\"item.value\">\n                        </span>\n                    </a>\n                    <!-- VALUE W/OUT HREF -->\n                    <span class=\"mr-advanced-result__value\"\n                        *ngIf=\"item.value && !item.href\"\n                        [innerHTML]=\"item.value\">\n                    </span>\n                </div>\n            </div>\n        </div>\n    </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedResultComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-advanced-result', template: "<div *ngIf=\"data\"\n     class=\"mr-advanced-result\">\n     <!-- RESULT MAIN CONTENT -->\n    <n7-item-preview [data]=\"data\"></n7-item-preview>\n    <!-- RESULT HIGHLIGHTS SECTION -->\n    <ng-container *ngIf=\"hasHighlights()\">\n        <div *ngIf=\"data?.highlightsTitle\" class=\"mr-advanced-result__header\"\n        [ngClass]=\"{\n            'has-toggle': !!data.highlightsHasToggle\n        }\"\n        (click)=\"onHeaderClick(data)\">\n            <span class=\"mr-advanced-result__header-label\">\n                {{ data.highlightsTitle }}\n            </span>\n            <span class=\"mr-advanced-result__header-icon\"\n            [ngClass]=\"{\n                'n7-icon-angle-down': data.highlightsHidden,\n                'n7-icon-angle-up': !data.highlightsHidden\n            }\">\n            </span>\n        </div>\n        <div [hidden]=\"isHidden()\" \n        class=\"mr-advanced-result__content\"\n            *ngFor=\"let highlightGroup of data.highlights\">\n            <div class=\"mr-advanced-result__content-group\">\n                <!-- METADATA ITEM -->\n                <div class=\"mr-advanced-result__item {{ item.classes || '' }}\"\n                    *ngFor=\"let item of highlightGroup.items\">\n                    <!-- ICON -->\n                    <span class=\"mr-advanced-result__icon {{item.icon}}\"\n                        *ngIf=\"item.icon\">\n                    </span>\n                    <!-- LABEL -->\n                    <span class=\"mr-advanced-result__label\"\n                        *ngIf=\"item.label\"\n                        [innerHTML]=\"item.label\">\n                    </span>\n                    <!-- VALUE W/ HREF -->\n                    <a *ngIf=\"item.href\"\n                    [href]=\"item.href\"\n                    [target]=\"item.target\"\n                    >\n                        <span class=\"mr-advanced-result__value\"\n                            *ngIf=\"item.value\"\n                            [innerHTML]=\"item.value\">\n                        </span>\n                    </a>\n                    <!-- VALUE W/OUT HREF -->\n                    <span class=\"mr-advanced-result__value\"\n                        *ngIf=\"item.value && !item.href\"\n                        [innerHTML]=\"item.value\">\n                    </span>\n                </div>\n            </div>\n        </div>\n    </ng-container>\n</div>\n" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }] } });

class MrAdvancedResultsLayoutComponent extends AbstractLayout {
    constructor(router, activatedRoute, mainState, configuration, communication, layoutState, modalService, localeService, layoutsConfiguration) {
        super(layoutsConfiguration.get('MrAdvancedResultsLayoutConfig') || MrAdvancedResultsLayoutConfig);
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.mainState = mainState;
        this.configuration = configuration;
        this.communication = communication;
        this.layoutState = layoutState;
        this.modalService = modalService;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routeData: this.routeData,
            configuration: this.configuration,
            communication: this.communication,
            mainState: this.mainState,
            router: this.router,
            activatedRoute: this.activatedRoute,
            layoutState: this.layoutState,
            modalService: this.modalService,
            localeService: this.localeService,
            options: this.config.options || {},
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routeData) => {
            this.routeData = routeData;
            // add layout states
            this.layoutState.add(['results']);
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrAdvancedResultsLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedResultsLayoutComponent, deps: [{ token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1.MainStateService }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: MrLayoutStateService }, { token: MrResourceModalService }, { token: MrLocaleService }, { token: i1.LayoutsConfigurationService }], target: i0.ɵɵFactoryTarget.Component });
MrAdvancedResultsLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrAdvancedResultsLayoutComponent, selector: "mr-advanced-results-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-advanced-results mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-advanced-results__title\">\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n        \n        <div class=\"mr-advanced-results__results-content\">\n            <div class=\"scroll-ref\">&nbsp;</div>\n            <div class=\"mr-advanced-results__results-wrapper\">\n                \n                <div class=\"mr-advanced-results__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                \n                <div *ngIf=\"lb.dataSource.pageConfig['filters'] \n                            && (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)?.length\" \n                     class=\"mr-active-filters\">\n                    <span *ngIf=\"lb.dataSource.pageConfig['filters'].title\" \n                    class=\"mr-active-filters__label\">{{ lb.dataSource.pageConfig['filters'].title }}</span>\n                    <div class=\"mr-active-filters__tags-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)\"\n                            [data]=\"tag\">\n                        </n7-tag>\n                    </div>\n                </div>\n\n                <main class=\"mr-advanced-results__results\">\n                    \n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-advanced-results__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <!-- Use a custom item preview with clickable metadata items -->\n                                <mr-advanced-result\n                                    *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                    [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                </mr-advanced-result>\n                                <!-- ../../components/advanced-result/advanced-result.html -->\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div *ngIf=\"lb.dataSource.pageConfig?.fallback?.text\" class=\"mr-advanced-results__results-fallback\">\n                                <p class=\"mr-advanced-results__feedback-text\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <div class=\"mr-advanced-results__buttons\">\n                                    <button class=\"n7-btn n7-btn-xl mr-advanced-results__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset', lb.dataSource.pageConfig.fallback.payload)\">\n                                        {{ lb.dataSource.pageConfig.fallback.button }}\n                                    </button>\n                                </div>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p *ngIf=\"lb.dataSource.pageConfig?.ko?.text\" class=\"mr-advanced-results__feedback-text\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <!-- <div class=\"mr-advanced-results__buttons\">\n                                <button class=\"n7-btn n7-btn-xl mr-advanced-results__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.ko.button }}\n                                </button>\n                            </div> -->\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                \n                <n7-smart-pagination\n                    *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                    [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n\n            </div>\n        </div>\n    </section>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.ContentPlaceholderComponent, selector: "n7-content-placeholder", inputs: ["data"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.TagComponent, selector: "n7-tag", inputs: ["data", "emit"] }, { kind: "component", type: i1.SmartPaginationComponent, selector: "n7-smart-pagination", inputs: ["data", "emit"] }, { kind: "component", type: MrAdvancedResultComponent, selector: "mr-advanced-result", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedResultsLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-advanced-results-layout', template: "<div class=\"mr-advanced-results mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-advanced-results__title\">\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n        \n        <div class=\"mr-advanced-results__results-content\">\n            <div class=\"scroll-ref\">&nbsp;</div>\n            <div class=\"mr-advanced-results__results-wrapper\">\n                \n                <div class=\"mr-advanced-results__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                \n                <div *ngIf=\"lb.dataSource.pageConfig['filters'] \n                            && (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)?.length\" \n                     class=\"mr-active-filters\">\n                    <span *ngIf=\"lb.dataSource.pageConfig['filters'].title\" \n                    class=\"mr-active-filters__label\">{{ lb.dataSource.pageConfig['filters'].title }}</span>\n                    <div class=\"mr-active-filters__tags-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)\"\n                            [data]=\"tag\">\n                        </n7-tag>\n                    </div>\n                </div>\n\n                <main class=\"mr-advanced-results__results\">\n                    \n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-advanced-results__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <!-- Use a custom item preview with clickable metadata items -->\n                                <mr-advanced-result\n                                    *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                    [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                </mr-advanced-result>\n                                <!-- ../../components/advanced-result/advanced-result.html -->\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div *ngIf=\"lb.dataSource.pageConfig?.fallback?.text\" class=\"mr-advanced-results__results-fallback\">\n                                <p class=\"mr-advanced-results__feedback-text\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <div class=\"mr-advanced-results__buttons\">\n                                    <button class=\"n7-btn n7-btn-xl mr-advanced-results__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset', lb.dataSource.pageConfig.fallback.payload)\">\n                                        {{ lb.dataSource.pageConfig.fallback.button }}\n                                    </button>\n                                </div>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p *ngIf=\"lb.dataSource.pageConfig?.ko?.text\" class=\"mr-advanced-results__feedback-text\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <!-- <div class=\"mr-advanced-results__buttons\">\n                                <button class=\"n7-btn n7-btn-xl mr-advanced-results__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.ko.button }}\n                                </button>\n                            </div> -->\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                \n                <n7-smart-pagination\n                    *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                    [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n\n            </div>\n        </div>\n    </section>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1.MainStateService }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: MrLayoutStateService }, { type: MrResourceModalService }, { type: MrLocaleService }, { type: i1.LayoutsConfigurationService }]; } });

class MrInputTextDS extends DataSource {
    constructor() {
        super(...arguments);
        this.state = {
            value: null,
            disabled: false,
            hidden: false,
        };
        this.getState = () => this.state;
    }
    transform(data) {
        return {
            ...data,
            placeholder: _t(data.placeholder)
        };
    }
    setState(newState) {
        this.state = {
            ...this.state,
            ...newState
        };
        this.refresh();
    }
    clear() {
        this.setState({ value: null });
    }
    refresh() {
        const { value, hidden, disabled } = this.state;
        // render value
        this.output.value = value;
        // fix element update
        const el = document.getElementById(this.id);
        if (el) {
            el.value = value;
        }
        // render disabled
        this.output.disabled = disabled;
        // render hidden
        this.output.classes = hidden ? 'is-hidden' : '';
    }
}

class MrInputTextEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`: {
                    const { value } = payload;
                    // set new value
                    this.dataSource.setState({ value });
                    // emit changed signal
                    this.changed$.next({
                        id: this.dataSource.id,
                        state: this.dataSource.getState()
                    });
                    break;
                }
                default:
                    break;
            }
        });
    }
}

class MrInputSelectDS extends DataSource {
    constructor() {
        super(...arguments);
        this.state = {
            value: null,
            disabled: false,
            hidden: false,
        };
        this.getState = () => this.state;
    }
    transform(data) {
        return {
            ...data,
            options: this.getOptions(data.options)
        };
    }
    setState(newState) {
        this.state = {
            ...this.state,
            ...newState
        };
        this.refresh();
    }
    clear() {
        this.setState({ value: null });
    }
    refresh() {
        const { hidden, disabled } = this.state;
        // render value
        this.output.options = this.getOptions(this.output.options);
        // render disabled
        this.output.disabled = disabled;
        // render hidden
        this.output.classes = hidden ? 'is-hidden' : '';
    }
    getOptions(options) {
        const { value } = this.state;
        return options.map((option) => ({
            ...option,
            label: _t(option.label),
            selected: value === option.value
        }));
    }
}

class MrInputSelectEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`: {
                    const { value } = payload;
                    // set new value
                    this.dataSource.setState({ value });
                    // emit changed signal
                    this.changed$.next({
                        id: this.dataSource.id,
                        state: this.dataSource.getState()
                    });
                    break;
                }
                default:
                    break;
            }
        });
    }
}

// eslint-disable-next-line max-len
class MrInputCheckboxDS extends DataSource {
    constructor() {
        super(...arguments);
        this.state = {
            value: [],
            disabled: false,
            hidden: false,
        };
        this.getState = () => this.state;
    }
    transform(data) {
        return {
            ...data,
            checkboxes: this.getCheckboxes(data.checkboxes)
        };
    }
    setState(newState) {
        this.state = {
            ...this.state,
            ...newState
        };
        this.refresh();
    }
    clear() {
        this.setState({ value: [] });
    }
    refresh() {
        const { hidden } = this.state;
        // render value
        this.output.checkboxes = this.getCheckboxes(this.output.checkboxes);
        // render hidden
        this.output.classes = hidden ? 'is-hidden' : '';
    }
    toggleValue({ inputPayload, value: isChecked }) {
        const { value } = this.state;
        const exists = !!(value.includes(inputPayload));
        if (isChecked && !exists) {
            value.push(inputPayload);
        }
        else if (!isChecked && exists) {
            value.splice(value.indexOf(inputPayload), 1);
        }
        this.setState({ value });
    }
    getCheckboxes(checkboxes) {
        const { value, disabled } = this.state;
        return checkboxes.map((checkbox, index) => ({
            ...checkbox,
            id: `${this.id}-${index}`,
            disabled,
            label: _t(checkbox.label),
            checked: !!(value.includes(checkbox.payload))
        }));
    }
}

class MrInputCheckboxEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`: {
                    // update value
                    this.dataSource.toggleValue(payload);
                    // emit changed signal
                    this.changed$.next({
                        id: this.dataSource.id,
                        state: this.dataSource.getState()
                    });
                    break;
                }
                default:
                    break;
            }
        });
    }
}

class MrFormModel {
    constructor() {
        this.loaded$ = new ReplaySubject();
        this.inputs = {};
        this.inputTypes = {
            text: {
                ds: MrInputTextDS,
                eh: MrInputTextEH
            },
            select: {
                ds: MrInputSelectDS,
                eh: MrInputSelectEH
            },
            checkbox: {
                ds: MrInputCheckboxDS,
                eh: MrInputCheckboxEH
            }
        };
        this.changed$ = new Subject();
        this.getInput = (id) => this.inputs[id].ds;
        this.getInputs = () => {
            const inputs = {};
            Object.keys(this.inputs).forEach((id) => {
                inputs[id] = this.getInput(id);
            });
            return inputs;
        };
    }
    init(config) {
        this.config = config;
        // init inputs
        this.initInputs();
        // emit signal
        this.loaded$.next(true);
    }
    getState() {
        const state = {};
        Object.keys(this.inputs).forEach((key) => {
            state[key] = this.inputs[key].ds.getState();
        });
        return state;
    }
    addInputType(type, ds, eh) {
        if (this.inputTypes[type]) {
            throw Error(`input type ${type} already exists!`);
        }
        this.inputTypes[type] = { ds, eh };
    }
    initInputs() {
        const { sections } = this.config;
        sections.forEach((section) => {
            section.inputs.forEach(({ id, type, options, state, data }) => {
                const DSClass = this.inputTypes[type].ds;
                const EHClass = this.inputTypes[type].eh;
                const DSInstance = new DSClass(options || {});
                const EHInstance = new EHClass();
                // set datasource id
                DSInstance.id = id;
                // set initial data
                if (data) {
                    DSInstance.update(data);
                }
                // set state
                if (state) {
                    DSInstance.setState(state);
                }
                // set eventhandler hostid
                EHInstance.hostId = id;
                // attach datasource to eventhandler
                EHInstance.dataSource = DSInstance;
                // attach changed$ to eventhandler
                EHInstance.changed$ = this.changed$;
                // listen to input events
                EHInstance.listen();
                // save it to input
                this.inputs[id] = {
                    ds: DSInstance,
                    eh: EHInstance,
                    emit: (t, p) => EHInstance.emitInner(t, p)
                };
            });
        });
    }
}

class MrAdvancedSearchLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.initialState = {};
        this.handleOptionsRequest = (response) => {
            const { formConfig } = this.pageConfig;
            Object.keys(response).forEach((key) => {
                formConfig.sections.forEach(({ inputs }) => {
                    inputs.forEach((input) => {
                        if (input.id === key) {
                            input.data = {
                                ...input.data,
                                ...response[key]
                            };
                        }
                    });
                });
            });
        };
    }
    onInit(payload) {
        this.router = payload.router;
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.mainState = payload.mainState;
        this.routeData = payload.routeData;
        this.localeService = payload.localeService;
        this.pageConfig = this.configuration.get(this.routeData.configId);
        // add translations
        this.addTranslations(this.pageConfig);
        let optionsReq$ = of(true);
        if (this.pageConfig.hasDynamicOptions) {
            const dynamicReq$ = this.communication.request$('advancedSearchOptions', {
                onError: (err) => {
                    console.warn('Request error', err);
                }
            });
            optionsReq$ = dynamicReq$.pipe(tap(this.handleOptionsRequest));
        }
        optionsReq$.subscribe({
            complete: () => {
                // init form
                this.form = new MrFormModel();
                // form init
                this.form.init(this.pageConfig.formConfig);
                // set initial state
                this.initialState = cloneDeep(this.form.getState());
                this.one('mr-form-wrapper-accordion').update({
                    form: this.form
                });
            }
        });
        // update head title
        this.updateHeadTitle();
    }
    updateHeadTitle() {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle)].join(' > '));
    }
    onSubmit({ state }) {
        if (!isEmpty(state)) {
            const { resultsUrl } = this.pageConfig;
            let baseUrl = resultsUrl;
            if (typeof resultsUrl !== 'string') {
                const locale = this.localeService.getLocale();
                baseUrl = resultsUrl[locale];
            }
            const params = Object.keys(state)
                .filter((key) => !(state[key].disabled || isEmpty(state[key].value)))
                .map((key) => ({
                key,
                value: Array.isArray(state[key].value)
                    ? state[key].value.join(',')
                    : state[key].value
            }))
                .map(({ key, value }) => `${key}=${encodeURIComponent(value)}`);
            const url = `${baseUrl}?${params.join('&')}`;
            window.open(url, '_blank');
        }
    }
    onReset() {
        Object.keys(this.initialState).forEach((key) => {
            const inputState = cloneDeep(this.initialState[key]);
            this.form.getInput(key).setState(inputState);
        });
    }
    addTranslations(pageConfig) {
        const { formConfig } = pageConfig;
        // page title
        pageConfig.title = _t(pageConfig.title);
        // submit
        if (formConfig.submitButton) {
            formConfig.submitButton.label = _t(formConfig.submitButton.label);
        }
        // reset
        if (formConfig.resetButton) {
            formConfig.resetButton.label = _t(formConfig.resetButton.label);
        }
        // groups
        formConfig.groups.forEach((group) => {
            if (group.options?.label) {
                group.options.label = _t(group.options.label);
            }
        });
        // sections
        formConfig.sections.forEach((section) => {
            if (section.title) {
                section.title = _t(section.title);
            }
            if (section.description) {
                section.description = _t(section.description);
            }
            section.inputs.forEach((input) => {
                if (input.data.label) {
                    input.data.label = _t(input.data.label);
                }
                if (input.data.legend) {
                    input.data.legend = _t(input.data.legend);
                }
                // input text
                if (input.type === 'text') {
                    if (input.data.placeholder) {
                        input.data.placeholder = _t(input.data.placeholder);
                    }
                }
                // input checkbox
                if (input.type === 'checkbox') {
                    input.data.checkboxes.forEach((checkbox) => {
                        checkbox.label = _t(checkbox.label);
                    });
                }
                // input select
                if (input.type === 'select') {
                    input.data.options.forEach((option) => {
                        option.label = _t(option.label);
                    });
                }
                // info tooltip
                if (input.info && input.data.label && ['text', 'select'].includes(input.type)) {
                    const inputData = input.data;
                    if (/n7-icon/.test(input.data.label))
                        return;
                    input.data.label = [
                        `<span>${inputData.label}</span>`,
                        `<span class="mr-input-info n7-icon n7-icon-info-circle" alt="${_t(input.info)}"></span>`
                    ].join('');
                }
                if (input.info && input.data.legend && input.type === 'checkbox') {
                    const inputData = input.data;
                    if (/n7-icon/.test(input.data.legend))
                        return;
                    input.data.legend = [
                        `<span>${inputData.legend}</span>`,
                        `<span class="mr-input-info n7-icon n7-icon-info-circle" alt="${_t(input.info)}"></span>`
                    ].join('');
                }
            });
        });
    }
}

class MrAdvancedSearchLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-advanced-search-layout.init':
                    this.dataSource.onInit(payload);
                    // init hook
                    this.onInit();
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                case 'mr-advanced-search-layout.destroy':
                    this.destroy$.next();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-form-wrapper-accordion.submit':
                    this.dataSource.onSubmit(payload);
                    break;
                case 'mr-form-wrapper-accordion.reset':
                    this.dataSource.onReset();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
    }
    /**
     * @example
     * protected onInit() {
     *   this.dataSource.form.changed$.subscribe(({ id, state }) => {
     *     console.log('changed$', { id, state });
     *   });
     * }
     */
    onInit() {
        // to be extended on project
    }
}

const MrAdvancedSearchLayoutConfig = {
    layoutId: 'mr-advanced-search-layout',
    widgets: [{
            id: 'mr-form-wrapper-accordion'
        }],
    layoutDS: MrAdvancedSearchLayoutDS,
    layoutEH: MrAdvancedSearchLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class MrFormComponent {
    ngOnInit() {
        if (this.group) {
            this.sections = this.form.config.sections
                .filter(({ id }) => this.group.sections.includes(id));
        }
        else {
            this.sections = this.form.config.sections;
        }
        // translations
        this.sections = this.sections.map((section) => ({
            ...section,
            title: _t(section.title),
            description: _t(section.description)
        }));
    }
}
MrFormComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrFormComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrFormComponent, selector: "mr-form", inputs: { form: "form", group: "group" }, queries: [{ propertyName: "templateRef", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0, template: "<div *ngIf=\"form.loaded$ | async\" class=\"mr-form {{ group?.classes || '' }}\">\n    <div *ngFor=\"let section of sections\" class=\"mr-form__section {{ section.classes || '' }}\" \n         [ngClass]=\"{ 'mr-form__section-advanced' : section.advancedSection  }\" >\n        \n        <div class=\"mr-form__section-header\">\n            <h3 *ngIf=\"section.title\" class=\"mr-form__section-title\">{{ section.title }}</h3>\n            <p *ngIf=\"section.description\" class=\"mr-form__section-description\">{{ section.description }}</p>\n        </div>\n        \n        <div class=\"mr-form__section-content\">\n            <div *ngFor=\"let input of section.inputs\" class=\"mr-form__element {{ input.options?.classes || '' }}\">\n                <ng-container [ngSwitch]=\"input.type\">\n\n                    <!-- INPUT TEXT -->\n                    <n7-input-text *ngSwitchCase=\"'text'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-text>\n\n                    <!-- INPUT CHECKBOX -->\n                    <n7-input-checkbox *ngSwitchCase=\"'checkbox'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-checkbox>\n\n                    <!-- INPUT SELECT -->\n                    <n7-input-select *ngSwitchCase=\"'select'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-select>\n\n                    <!-- DEFAULT (external template) -->\n                    <ng-container *ngSwitchDefault>\n                        <ng-template *ngTemplateOutlet=\"\n                        templateRef; \n                        context: { \n                            type: input.type, \n                            input: form.inputs[input.id] \n                        }\"></ng-template>\n                    </ng-container>\n\n                </ng-container>\n            </div>\n        </div>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i1$2.NgSwitchDefault, selector: "[ngSwitchDefault]" }, { kind: "component", type: i7.InputCheckboxComponent, selector: "n7-input-checkbox", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputSelectComponent, selector: "n7-input-select", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputTextComponent, selector: "n7-input-text", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-form', template: "<div *ngIf=\"form.loaded$ | async\" class=\"mr-form {{ group?.classes || '' }}\">\n    <div *ngFor=\"let section of sections\" class=\"mr-form__section {{ section.classes || '' }}\" \n         [ngClass]=\"{ 'mr-form__section-advanced' : section.advancedSection  }\" >\n        \n        <div class=\"mr-form__section-header\">\n            <h3 *ngIf=\"section.title\" class=\"mr-form__section-title\">{{ section.title }}</h3>\n            <p *ngIf=\"section.description\" class=\"mr-form__section-description\">{{ section.description }}</p>\n        </div>\n        \n        <div class=\"mr-form__section-content\">\n            <div *ngFor=\"let input of section.inputs\" class=\"mr-form__element {{ input.options?.classes || '' }}\">\n                <ng-container [ngSwitch]=\"input.type\">\n\n                    <!-- INPUT TEXT -->\n                    <n7-input-text *ngSwitchCase=\"'text'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-text>\n\n                    <!-- INPUT CHECKBOX -->\n                    <n7-input-checkbox *ngSwitchCase=\"'checkbox'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-checkbox>\n\n                    <!-- INPUT SELECT -->\n                    <n7-input-select *ngSwitchCase=\"'select'\" \n                        [data]=\"form.inputs[input.id].ds.out$ | async\"\n                        [emit]=\"form.inputs[input.id].emit\"></n7-input-select>\n\n                    <!-- DEFAULT (external template) -->\n                    <ng-container *ngSwitchDefault>\n                        <ng-template *ngTemplateOutlet=\"\n                        templateRef; \n                        context: { \n                            type: input.type, \n                            input: form.inputs[input.id] \n                        }\"></ng-template>\n                    </ng-container>\n\n                </ng-container>\n            </div>\n        </div>\n    </div>\n</div>" }]
        }], propDecorators: { form: [{
                type: Input
            }], group: [{
                type: Input
            }], templateRef: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });

class MrFormWrapperAccordionComponent {
    constructor() {
        this.fakeEmit = (type, payload) => {
            if (!this.emit) {
                return;
            }
            this.emit(type, payload);
        };
    }
    ngOnInit() {
        this.fakeEmit('init');
    }
    ngOnDestroy() {
        this.fakeEmit('destroy');
    }
    onReset() {
        this.fakeEmit('reset');
    }
    onSubmit() {
        this.fakeEmit('submit');
    }
}
MrFormWrapperAccordionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFormWrapperAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrFormWrapperAccordionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrFormWrapperAccordionComponent, selector: "mr-form-wrapper-accordion", inputs: { data: "data", emit: "emit" }, ngImport: i0, template: "<div *ngIf=\"data\" class=\"mr-form-wrapper-accordion\">\n    <ng-container *ngFor=\"let group of data.form.config.groups; index as $i\">\n        <div *ngIf=\"group.options && (group.options.showHeader !== false)\" \n            class=\"mr-form-wrapper-accordion__header\">\n            <n7-facet-header\n                [data]=\"group.options\"\n                [emit]=\"fakeEmit\"\n            ></n7-facet-header>\n        </div>\n\n        <div *ngIf=\"group.options.isOpen\" class=\"mr-form-wrapper-accordion__content\" [attr.id]=\"group.id\">\n            <mr-form [form]=\"data.form\" [group]=\"group\">\n                <!-- CUSTOM INPUTS -->\n                <!-- <ng-template let-type=\"type\" let-input=\"input\">\n                    <ng-container [ngSwitch]=\"type\">\n                        \n                        <n7-tag *ngSwitchCase=\"'tag'\" \n                            [data]=\"input.ds.out$ | async\"\n                            [emit]=\"input.emit\"></n7-tag>\n    \n                    </ng-container>\n                </ng-template> -->\n            </mr-form>\n        </div>\n    </ng-container>\n    \n    <div class=\"mr-form-wrapper-accordion__actions\">\n        <a *ngIf=\"data.form.config.resetButton\" \n            class=\"n7-btn n7-btn-xl n7-btn-danger\" \n            (click)=\"onReset()\">{{ data.form.config.resetButton.label }}</a>\n        <a *ngIf=\"data.form.config.submitButton\" \n            class=\"n7-btn n7-btn-cta n7-btn-xl n7-btn-info\" \n            (click)=\"onSubmit()\">{{ data.form.config.submitButton.label }}</a>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i7.FacetHeaderComponent, selector: "n7-facet-header", inputs: ["data", "emit"] }, { kind: "component", type: MrFormComponent, selector: "mr-form", inputs: ["form", "group"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFormWrapperAccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-form-wrapper-accordion', template: "<div *ngIf=\"data\" class=\"mr-form-wrapper-accordion\">\n    <ng-container *ngFor=\"let group of data.form.config.groups; index as $i\">\n        <div *ngIf=\"group.options && (group.options.showHeader !== false)\" \n            class=\"mr-form-wrapper-accordion__header\">\n            <n7-facet-header\n                [data]=\"group.options\"\n                [emit]=\"fakeEmit\"\n            ></n7-facet-header>\n        </div>\n\n        <div *ngIf=\"group.options.isOpen\" class=\"mr-form-wrapper-accordion__content\" [attr.id]=\"group.id\">\n            <mr-form [form]=\"data.form\" [group]=\"group\">\n                <!-- CUSTOM INPUTS -->\n                <!-- <ng-template let-type=\"type\" let-input=\"input\">\n                    <ng-container [ngSwitch]=\"type\">\n                        \n                        <n7-tag *ngSwitchCase=\"'tag'\" \n                            [data]=\"input.ds.out$ | async\"\n                            [emit]=\"input.emit\"></n7-tag>\n    \n                    </ng-container>\n                </ng-template> -->\n            </mr-form>\n        </div>\n    </ng-container>\n    \n    <div class=\"mr-form-wrapper-accordion__actions\">\n        <a *ngIf=\"data.form.config.resetButton\" \n            class=\"n7-btn n7-btn-xl n7-btn-danger\" \n            (click)=\"onReset()\">{{ data.form.config.resetButton.label }}</a>\n        <a *ngIf=\"data.form.config.submitButton\" \n            class=\"n7-btn n7-btn-cta n7-btn-xl n7-btn-info\" \n            (click)=\"onSubmit()\">{{ data.form.config.submitButton.label }}</a>\n    </div>\n</div>" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }] } });

class MrAdvancedSearchLayoutComponent extends AbstractLayout {
    constructor(router, activatedRoute, mainState, configuration, communication, localeService, layoutsConfiguration) {
        super(layoutsConfiguration.get('MrAdvancedSearchLayoutConfig') || MrAdvancedSearchLayoutConfig);
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.mainState = mainState;
        this.configuration = configuration;
        this.communication = communication;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routeData: this.routeData,
            configuration: this.configuration,
            communication: this.communication,
            mainState: this.mainState,
            router: this.router,
            activatedRoute: this.activatedRoute,
            localeService: this.localeService,
            options: this.config.options || {},
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routeData) => {
            this.routeData = routeData;
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrAdvancedSearchLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedSearchLayoutComponent, deps: [{ token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1.MainStateService }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: MrLocaleService }, { token: i1.LayoutsConfigurationService }], target: i0.ɵɵFactoryTarget.Component });
MrAdvancedSearchLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrAdvancedSearchLayoutComponent, selector: "mr-advanced-search-layout", usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"lb.dataSource\" class=\"mr-advanced-search mr-layout\">\n    <div class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <n7-inner-title [data]=\"{\n            title: {\n                main: {\n                    text: lb.dataSource.pageConfig.title\n                }\n            }\n        }\"></n7-inner-title>\n\n        <mr-form-wrapper-accordion \n            [data]=\"lb.widgets['mr-form-wrapper-accordion'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-form-wrapper-accordion'].emit\">\n        </mr-form-wrapper-accordion>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: MrFormWrapperAccordionComponent, selector: "mr-form-wrapper-accordion", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrAdvancedSearchLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-advanced-search-layout', template: "<div *ngIf=\"lb.dataSource\" class=\"mr-advanced-search mr-layout\">\n    <div class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <n7-inner-title [data]=\"{\n            title: {\n                main: {\n                    text: lb.dataSource.pageConfig.title\n                }\n            }\n        }\"></n7-inner-title>\n\n        <mr-form-wrapper-accordion \n            [data]=\"lb.widgets['mr-form-wrapper-accordion'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-form-wrapper-accordion'].emit\">\n        </mr-form-wrapper-accordion>\n    </div>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1.MainStateService }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: MrLocaleService }, { type: i1.LayoutsConfigurationService }]; } });

class MrHomeLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.errorTitle = _t('global#layout_error_title');
        this.errorDescription = _t('global#layout_error_description');
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.mainState = payload.mainState;
        this.layoutState = payload.layoutState;
        this.routeData = payload.routeData;
        this.pageConfig = this.configuration.get(this.routeData.configId) || {};
        this.doRequest();
        // update head title
        this.updateHeadTitle();
    }
    doRequest() {
        const { sections } = this.pageConfig;
        const { configId, locale } = this.routeData;
        if (!isEmpty(sections)) {
            this.layoutState.set('content', LayoutState.LOADING);
            this.communication.request$('home', {
                method: 'POST',
                params: sections.map(({ id }) => id),
                urlParams: locale ? `?locale=${locale}` : '',
                onError: (err) => {
                    console.warn(`Error loading ${configId} sections`, err.message);
                    this.layoutState.set('content', LayoutState.ERROR);
                }
            }).subscribe((response) => {
                this.layoutState.set('content', LayoutState.SUCCESS);
                this.initSections(response);
            });
        }
        else {
            console.warn(`There are no sections configured for ${configId} layout`);
        }
    }
    initSections(response) {
        const { sections } = this.pageConfig;
        if (sections) {
            sections.forEach(({ id }) => {
                const widgetDataSource = this.getWidgetDataSource(id);
                const responseData = response[id];
                // set id
                widgetDataSource.id = id;
                // update data
                if (responseData) {
                    this.one(id).update(responseData);
                }
            });
        }
    }
    updateHeadTitle() {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, pageTitle].join(' > '));
    }
}

class MrHomeLayoutEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-home-layout.init':
                    this.dataSource.onInit(payload);
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                default:
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type }) => {
            switch (type) {
                default:
                    break;
            }
        });
    }
}

const MrHomeLayoutConfig = {
    layoutId: 'mr-home-layout',
    widgets: [],
    layoutDS: MrHomeLayoutDS,
    layoutEH: MrHomeLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    options: {
    // TODO
    },
};

class MrSliderDS extends DataSource {
    transform(data) {
        const { slides } = data;
        return {
            slides,
            containerId: `carousel-${this.id}`,
            // classes: 'demo',
            libOptions: {
                count: 1,
                move: 1,
                // touch: true,
                // mode: 'align',
                buttons: true,
                dots: true,
                rewind: true,
                autoplay: 0,
                animation: 500,
                // responsive: {
                //   0: { count: 1.5, buttons: false },
                //   480: { count: 2.5, buttons: false },
                //   768: { count: 3, touch: false },
                //   1440: { count: 4, touch: false },
                // },
            },
        };
    }
}

class MrSliderEH extends EventHandler {
    listen() {
        // this.innerEvents$.subscribe(({ type, payload }) => {
        //   switch (type) {
        //     case `${this.dataSource.id}.<event-type>`:
        //       // TODO
        //       break;
        //     default:
        //       break;
        //   }
        // });
    }
}

class MrHeroEH extends EventHandler {
    listen() {
        // this.innerEvents$.subscribe(({ type, payload }) => {
        //   switch (type) {
        //     case `${this.dataSource.id}.<event-type>`:
        //       // TODO
        //       break;
        //     default:
        //       break;
        //   }
        // });
    }
}

const DATASOURCE_MAP$4 = {
    slider: MrSliderDS,
    collection: MrCollectionDS,
    hero: MrHeroDS,
    content: MrContentDS,
};
const EVENTHANDLER_MAP$3 = {
    slider: MrSliderEH,
    collection: MrCollectionEH,
    hero: MrHeroEH,
};
class MrHomeLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, activatedRoute, configuration, communication, mainState, layoutState, localeService) {
        super(layoutsConfiguration.get('MrHomeLayoutConfig') || MrHomeLayoutConfig);
        this.activatedRoute = activatedRoute;
        this.configuration = configuration;
        this.communication = communication;
        this.mainState = mainState;
        this.layoutState = layoutState;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routeData: this.routeData,
            mainState: this.mainState,
            configuration: this.configuration,
            communication: this.communication,
            layoutState: this.layoutState,
            options: this.config.options || {}
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routeData) => {
            this.routeData = routeData;
            this.layoutState.add('content');
            this.loadWidgets();
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
    loadWidgets() {
        const { configId } = this.routeData;
        const homeConfig = this.configuration.get(configId) || {};
        const { sections } = homeConfig;
        this.widgets = [];
        if (sections) {
            sections.forEach(({ id, type, options }) => {
                const widgetOptions = options || {};
                widgetOptions.localeService = this.localeService;
                this.widgets.push({
                    id,
                    options: widgetOptions,
                    dataSource: DATASOURCE_MAP$4[type],
                    eventHandler: EVENTHANDLER_MAP$3[type]
                });
            });
        }
    }
}
MrHomeLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrHomeLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.ActivatedRoute }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: i1.MainStateService }, { token: MrLayoutStateService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrHomeLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrHomeLayoutComponent, selector: "mr-home-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-home mr-layout\"\n     *ngIf=\"lb.dataSource\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- HOME CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <section *ngFor=\"let section of lb.dataSource.pageConfig.sections\" class=\"{{ 'mr-layout__' + section.type }}\">\n                <ng-container [ngSwitch]=\"section.type\">\n        \n                    <!-- SLIDER -->\n                    <ng-container *ngSwitchCase=\"'slider'\">\n                        <n7-carousel \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                        [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-carousel> \n                    </ng-container>\n        \n                    <!-- COLLECTION -->\n                    <ng-container *ngSwitchCase=\"'collection'\">\n                        <div class=\"mr-layout__maxwidth mr-items-preview\">\n                            <n7-inner-title \n                            [data]=\"(lb.widgets[section.id].ds.out$ | async)?.header\"\n                            [emit]=\"lb.widgets[section.id].emit\">\n                            </n7-inner-title>\n                            <div class=\"{{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview\n                                *ngFor=\"let item of (lb.widgets[section.id].ds.out$ | async)?.items\"\n                                [data]=\"item\"\n                                [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n        \n                    <!-- HERO -->\n                    <ng-container *ngSwitchCase=\"'hero'\">\n                        <n7-hero \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                        [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-hero> \n                    </ng-container>\n        \n                    <!-- CONTENT -->\n                    <ng-container *ngSwitchCase=\"'content'\">\n                        <div [innerHTML]=\"lb.widgets[section.id].ds.out$ | async\"></div>\n                    </ng-container>\n                \n                </ng-container>\n            </section>\n        </ng-container>\n\n    </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.CarouselComponent, selector: "n7-carousel", inputs: ["data", "emit"] }, { kind: "component", type: i7.HeroComponent, selector: "n7-hero", inputs: ["data", "emit"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrHomeLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-home-layout', template: "<div class=\"mr-home mr-layout\"\n     *ngIf=\"lb.dataSource\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- HOME CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <section *ngFor=\"let section of lb.dataSource.pageConfig.sections\" class=\"{{ 'mr-layout__' + section.type }}\">\n                <ng-container [ngSwitch]=\"section.type\">\n        \n                    <!-- SLIDER -->\n                    <ng-container *ngSwitchCase=\"'slider'\">\n                        <n7-carousel \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                        [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-carousel> \n                    </ng-container>\n        \n                    <!-- COLLECTION -->\n                    <ng-container *ngSwitchCase=\"'collection'\">\n                        <div class=\"mr-layout__maxwidth mr-items-preview\">\n                            <n7-inner-title \n                            [data]=\"(lb.widgets[section.id].ds.out$ | async)?.header\"\n                            [emit]=\"lb.widgets[section.id].emit\">\n                            </n7-inner-title>\n                            <div class=\"{{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview\n                                *ngFor=\"let item of (lb.widgets[section.id].ds.out$ | async)?.items\"\n                                [data]=\"item\"\n                                [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n        \n                    <!-- HERO -->\n                    <ng-container *ngSwitchCase=\"'hero'\">\n                        <n7-hero \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                        [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-hero> \n                    </ng-container>\n        \n                    <!-- CONTENT -->\n                    <ng-container *ngSwitchCase=\"'content'\">\n                        <div [innerHTML]=\"lb.widgets[section.id].ds.out$ | async\"></div>\n                    </ng-container>\n                \n                </ng-container>\n            </section>\n        </ng-container>\n\n    </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.ActivatedRoute }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: i1.MainStateService }, { type: MrLayoutStateService }, { type: MrLocaleService }]; } });

class MrItineraryLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.errorTitle = _t('global#layout_error_title');
        this.errorDescription = _t('global#layout_error_description');
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.localeService = payload.localeService;
        this.mainState = payload.mainState;
        this.routeData = payload.routeData;
        this.pageConfig = this.configuration.get(this.routeData.configId);
        // add translations
        this.pageConfig.sections = this.pageConfig.sections.map((section) => ({
            ...section,
            title: _t(section.title)
        }));
    }
    pageRequest$(id, onError) {
        const { locale } = this.routeData;
        return this.communication.request$('itinerary', {
            onError,
            method: 'GET',
            urlParams: locale ? `${id}?locale=${locale}` : id
        });
    }
    handleResponse(response) {
        this.updateTitle(response);
        this.updateContent(response);
        this.updateMetadata(response);
        this.initSections(response);
        this.setLocaleResourceConfig(response);
        this.updateHeadTitle(response);
    }
    updateTitle({ title }) {
        this.title = title;
    }
    updateContent({ content }) {
        this.content = content;
    }
    updateMetadata(response) {
        this.one('mr-static-metadata').update(response);
    }
    initSections(response) {
        const { sections } = this.pageConfig;
        sections.forEach(({ id }) => {
            const widgetDataSource = this.getWidgetDataSource(id);
            if (!widgetDataSource)
                return;
            const responseSection = response.sections[id];
            // set id
            widgetDataSource.id = id;
            // update data
            if (responseSection) {
                this.one(id).update(responseSection);
            }
        });
    }
    updateHeadTitle({ title: itineraryTitle }) {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle), itineraryTitle].join(' > '));
    }
    // set resource locale config
    // used by language switcher
    setLocaleResourceConfig(response) {
        if (response?.locale) {
            this.localeService.setResourceConfig(response.locale);
        }
    }
}

class MrItineraryLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-itinerary-layout.init':
                    this.route = payload.route;
                    this.router = payload.router;
                    this.layoutState = payload.layoutState;
                    this.modalService = payload.modalService;
                    this.dataSource.onInit(payload);
                    this.listenRoute();
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                case 'mr-resource-layout.destroy':
                    this.destroy$.next();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            if (type.indexOf('openresourcemodal') !== -1) {
                const { id, type: resourceType } = payload;
                this.modalService.open(id, resourceType);
            }
        });
    }
    listenRoute() {
        this.route.paramMap.pipe(takeUntil(this.destroy$), tap(() => {
            this.layoutState.set('content', LayoutState.LOADING);
        }), map((params) => params.get('id')), switchMap((id) => this.dataSource.pageRequest$(id, (err) => {
            if (err.status === 404) {
                // getting not found path
                const { config } = this.router;
                const route404 = config.find(({ data }) => data?.id === 'page-404');
                const path404 = route404?.path || 'page-404';
                this.router.navigate([path404]);
            }
            console.warn(`Error loading resource layout for ${id}`, err.message);
            this.layoutState.set('content', LayoutState.ERROR);
        }))).subscribe((response) => {
            this.layoutState.set('content', LayoutState.SUCCESS);
            this.dataSource.handleResponse(response);
            // scroll top
            window.scrollTo(0, 0);
        });
    }
}

const MrItineraryLayoutConfig = {
    layoutId: 'mr-itinerary-layout',
    widgets: [
        { id: 'mr-static-metadata' }
    ],
    layoutDS: MrItineraryLayoutDS,
    layoutEH: MrItineraryLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    options: {
    // TODO
    },
};

//---------------------------
const HEIGHT_MARGIN = 50;
class ReadMoreComponent {
    constructor() {
        this.collapsed = true;
        this.hasReadmore = false;
        this._loaded = false;
    }
    /**
     * Determine if the view is taller than the given height limit,
     * if it is, render the "Read-more" button.
     */
    ngAfterViewChecked() {
        if (this._loaded || !this.data)
            return;
        if (this.root && this.root.nativeElement.clientHeight > 0) {
            this._loaded = true;
            this.clientHeight = this.root.nativeElement.clientHeight;
            const { height, labels } = this.data;
            // translate labels
            Object.keys(labels).forEach((key) => {
                this.data.labels[key] = _t(labels[key]);
            });
            if (this.clientHeight > (height + HEIGHT_MARGIN)) {
                setTimeout(() => {
                    this.hasReadmore = true;
                    this.updateWrapperHeight();
                });
            }
        }
    }
    handleToggle() {
        this.collapsed = !this.collapsed;
        this.updateWrapperHeight();
    }
    updateWrapperHeight() {
        this.wrapperHeight = this.collapsed
            ? this.data.height
            : this.clientHeight;
    }
}
ReadMoreComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ReadMoreComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ReadMoreComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: ReadMoreComponent, selector: "mr-read-more", inputs: { data: "data" }, viewQueries: [{ propertyName: "root", first: true, predicate: ["root"], descendants: true, read: ElementRef }], ngImport: i0, template: "<div #root class=\"mr-read-more\"\n    [ngClass]=\"{\n        'is-collapsed': !!(hasReadmore && collapsed),\n        'is-expanded': !!(hasReadmore && !collapsed)\n    }\">\n        <div class=\"mr-read-more__content\"\n        [ngStyle]=\"{\n            height: hasReadmore ? wrapperHeight + 'px' : false\n        }\">\n            <!-- Child component -->\n            <ng-content class=\"content\"></ng-content>\n        </div>\n        <div *ngIf=\"hasReadmore\" class=\"mr-read-more__btn\" (click)=\"handleToggle()\">\n            <span class=\"n7-icon-{{ collapsed ? 'plus' : 'minus' }}\"></span>\n            <span class=\"mr-read-more__btn-text\">{{ collapsed ? data.labels.more : data.labels.less }}</span>\n        </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: ReadMoreComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-read-more', template: "<div #root class=\"mr-read-more\"\n    [ngClass]=\"{\n        'is-collapsed': !!(hasReadmore && collapsed),\n        'is-expanded': !!(hasReadmore && !collapsed)\n    }\">\n        <div class=\"mr-read-more__content\"\n        [ngStyle]=\"{\n            height: hasReadmore ? wrapperHeight + 'px' : false\n        }\">\n            <!-- Child component -->\n            <ng-content class=\"content\"></ng-content>\n        </div>\n        <div *ngIf=\"hasReadmore\" class=\"mr-read-more__btn\" (click)=\"handleToggle()\">\n            <span class=\"n7-icon-{{ collapsed ? 'plus' : 'minus' }}\"></span>\n            <span class=\"mr-read-more__btn-text\">{{ collapsed ? data.labels.more : data.labels.less }}</span>\n        </div>\n</div>\n" }]
        }], propDecorators: { data: [{
                type: Input
            }], root: [{
                type: ViewChild,
                args: ['root', { read: ElementRef }]
            }] } });

class MrGalleryComponent {
    onClick(payload) {
        if (this.emit) {
            this.emit('click', payload);
        }
    }
    onClose() {
        if (this.emit) {
            this.emit('close');
        }
    }
}
MrGalleryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrGalleryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrGalleryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrGalleryComponent, selector: "mr-gallery", inputs: { data: "data", emit: "emit", grid: "grid" }, ngImport: i0, template: "<div *ngIf=\"data\" class=\"mr-gallery mr-wp-content\">\n    <div class=\"mr-gallery__wrapper wp-block-gallery has-zoom {{ grid ? 'columns-' + grid : '' }}\">\n        <ul class=\"mr-gallery__items blocks-gallery-grid\">\n            <li *ngFor=\"let item of data.items\" class=\"mr-gallery__item blocks-gallery-item\">\n                <figure>\n                    <a (click)=\"onClick(item.payload)\" class=\"mr-gallery__link\">\n                        <img [src]=\"item.thumbSrc\" [alt]=\"item.title\" class=\"mr-gallery__image\">\n                    </a>\n                </figure>\n            </li>\n        </ul>\n    </div>\n    <div *ngIf=\"data.selected\" class=\"mr-modal mr-gallery-modal\" (click)=\"onClose()\">\n        <div class=\"mr-modal__overlay\">\n            <div class=\"mr-modal__window mr-gallery-modal__window\">\n                <div class=\"mr-modal__header mr-gallery-modal__header\">\n                    <div class=\"mr-modal__close\">\n                        <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                    </div>\n                </div>\n                <div class=\"mr-modal__content mr-gallery-modal__content\">\n                    <div class=\"mr-gallery__zoom-image-wrapper\">\n                        <img [src]=\"data.selected.fullSrc\" [alt]=\"data.selected.title\" class=\"mr-gallery__zoom-image\">\n                    </div>\n                </div>\n            </div>\n        </div>\n    </div>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrGalleryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-gallery', template: "<div *ngIf=\"data\" class=\"mr-gallery mr-wp-content\">\n    <div class=\"mr-gallery__wrapper wp-block-gallery has-zoom {{ grid ? 'columns-' + grid : '' }}\">\n        <ul class=\"mr-gallery__items blocks-gallery-grid\">\n            <li *ngFor=\"let item of data.items\" class=\"mr-gallery__item blocks-gallery-item\">\n                <figure>\n                    <a (click)=\"onClick(item.payload)\" class=\"mr-gallery__link\">\n                        <img [src]=\"item.thumbSrc\" [alt]=\"item.title\" class=\"mr-gallery__image\">\n                    </a>\n                </figure>\n            </li>\n        </ul>\n    </div>\n    <div *ngIf=\"data.selected\" class=\"mr-modal mr-gallery-modal\" (click)=\"onClose()\">\n        <div class=\"mr-modal__overlay\">\n            <div class=\"mr-modal__window mr-gallery-modal__window\">\n                <div class=\"mr-modal__header mr-gallery-modal__header\">\n                    <div class=\"mr-modal__close\">\n                        <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                    </div>\n                </div>\n                <div class=\"mr-modal__content mr-gallery-modal__content\">\n                    <div class=\"mr-gallery__zoom-image-wrapper\">\n                        <img [src]=\"data.selected.fullSrc\" [alt]=\"data.selected.title\" class=\"mr-gallery__zoom-image\">\n                    </div>\n                </div>\n            </div>\n        </div>\n    </div>\n</div>" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }], grid: [{
                type: Input
            }] } });

const DATASOURCE_MAP$3 = {
    collection: MrCollectionDS,
    metadata: MrMetadataDS,
    gallery: MrGalleryDS,
};
const EVENTHANDLER_MAP$2 = {
    collection: MrCollectionEH,
    gallery: MrGalleryEH,
};
class MrItineraryLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, activatedRoute, configuration, communication, mainState, route, router, layoutState, modalService, localeService) {
        super(layoutsConfiguration.get('MrItineraryLayoutConfig') || MrItineraryLayoutConfig);
        this.activatedRoute = activatedRoute;
        this.configuration = configuration;
        this.communication = communication;
        this.mainState = mainState;
        this.route = route;
        this.router = router;
        this.layoutState = layoutState;
        this.modalService = modalService;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routeData: this.routeData,
            configuration: this.configuration,
            communication: this.communication,
            mainState: this.mainState,
            layoutState: this.layoutState,
            modalService: this.modalService,
            localeService: this.localeService,
            options: this.config.options || {},
            route: this.route,
            router: this.router
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routeData) => {
            this.layoutState.add('content');
            this.routeData = routeData;
            this.loadWidgets();
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
    loadWidgets() {
        const { configId } = this.routeData;
        const { sections } = this.configuration.get(configId);
        if (sections) {
            sections.forEach(({ id, type, options }) => {
                const widgetOptions = options || {};
                widgetOptions.localeService = this.localeService;
                this.widgets.push({
                    id,
                    options: widgetOptions,
                    dataSource: DATASOURCE_MAP$3[type],
                    eventHandler: EVENTHANDLER_MAP$2[type]
                });
            });
        }
    }
}
MrItineraryLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrItineraryLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.ActivatedRoute }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: i1.MainStateService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: MrLayoutStateService }, { token: MrResourceModalService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrItineraryLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrItineraryLayoutComponent, selector: "mr-itinerary-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-static mr-layout\" \n     *ngIf=\"lb.dataSource && lb.dataSource.pageConfig\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- ITINERARY LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <div class=\"mr-static__top\">\n                <h1 class=\"mr-static__title mr-generated-title-WP\">{{lb.dataSource.title}}</h1>\n                <div class=\"mr-static__metadata\">\n                    <n7-metadata-viewer \n                    [data]=\"lb.widgets['mr-static-metadata'].ds.out$ | async\">\n                    </n7-metadata-viewer>\n                </div>\n            </div>\n\n            <div class=\"mr-static__content mr-side-margin\">\n                <!-- Page content html -->\n                <div class=\"mr-wp-content\" [innerHTML]=\"lb.dataSource.content | keepHtml\"></div>\n    \n                <!-- Pass the list of blocks to render to the block template -->\n                <div class=\"mr-static__related-resources\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: lb.dataSource.pageConfig.sections }\"></ng-container>\n                </div>\n            </div>\n        </ng-container>\n\n    </ng-container>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"lb.widgets[section.id].ds.out$ | async\"\n        class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n    \n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    \n                    <div class=\"mr-content-block mr-content-block-metadata\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-read-more [data]=\"section.readmore\">\n                                <n7-metadata-viewer [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                    [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-metadata-viewer>\n                            </mr-read-more>\n                        </div>\n                    </div>\n\n                </ng-container>\n    \n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"lb.widgets[section.id].ds.out$ | async as collection$\">\n                        \n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-content-block mr-content-block-collection\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                                {{ section.title }}\n                            </h3>\n                            <div class=\"mr-content-block__content {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\"\n                                    [data]=\"item\" [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n\n                    </ng-container>\n                </ng-container>\n    \n                <!-- GALLERY -->\n                <ng-container *ngSwitchCase=\"'gallery'\">\n                    <div class=\"mr-content-block mr-content-block-gallery\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-gallery [grid]=\"section.grid\" [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">        \n                            </mr-gallery>\n                        </div>\n                    </div>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "component", type: i7.MetadataViewerComponent, selector: "n7-metadata-viewer", inputs: ["data", "emit"] }, { kind: "component", type: ReadMoreComponent, selector: "mr-read-more", inputs: ["data"] }, { kind: "component", type: MrGalleryComponent, selector: "mr-gallery", inputs: ["data", "emit", "grid"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }, { kind: "pipe", type: EscapeHtmlPipe, name: "keepHtml" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrItineraryLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-itinerary-layout', template: "<div class=\"mr-static mr-layout\" \n     *ngIf=\"lb.dataSource && lb.dataSource.pageConfig\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- ITINERARY LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <div class=\"mr-static__top\">\n                <h1 class=\"mr-static__title mr-generated-title-WP\">{{lb.dataSource.title}}</h1>\n                <div class=\"mr-static__metadata\">\n                    <n7-metadata-viewer \n                    [data]=\"lb.widgets['mr-static-metadata'].ds.out$ | async\">\n                    </n7-metadata-viewer>\n                </div>\n            </div>\n\n            <div class=\"mr-static__content mr-side-margin\">\n                <!-- Page content html -->\n                <div class=\"mr-wp-content\" [innerHTML]=\"lb.dataSource.content | keepHtml\"></div>\n    \n                <!-- Pass the list of blocks to render to the block template -->\n                <div class=\"mr-static__related-resources\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: lb.dataSource.pageConfig.sections }\"></ng-container>\n                </div>\n            </div>\n        </ng-container>\n\n    </ng-container>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"lb.widgets[section.id].ds.out$ | async\"\n        class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n    \n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    \n                    <div class=\"mr-content-block mr-content-block-metadata\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-read-more [data]=\"section.readmore\">\n                                <n7-metadata-viewer [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                    [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-metadata-viewer>\n                            </mr-read-more>\n                        </div>\n                    </div>\n\n                </ng-container>\n    \n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"lb.widgets[section.id].ds.out$ | async as collection$\">\n                        \n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-content-block mr-content-block-collection\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                                {{ section.title }}\n                            </h3>\n                            <div class=\"mr-content-block__content {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\"\n                                    [data]=\"item\" [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n\n                    </ng-container>\n                </ng-container>\n    \n                <!-- GALLERY -->\n                <ng-container *ngSwitchCase=\"'gallery'\">\n                    <div class=\"mr-content-block mr-content-block-gallery\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-gallery [grid]=\"section.grid\" [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">        \n                            </mr-gallery>\n                        </div>\n                    </div>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.ActivatedRoute }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: i1.MainStateService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: MrLayoutStateService }, { type: MrResourceModalService }, { type: MrLocaleService }]; } });

class MrMapLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.loading = {
            resourceDetails: true,
            timeline: true,
        };
        this.eventDescription = '';
        this.mapListener$ = new Subject();
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.localeService = payload.localeService;
        this.location = payload.location;
        this.route = payload.route;
        this.router = payload.router;
        this.routerData = payload.routerData;
        this.pageConfig = this.configuration.get(this.routerData.configId) || {};
        // overwrite leaflet options with configuration.libOptions
        this.one('mr-map').updateOptions({ libOptions: this.pageConfig.libOptions });
        // update the map
        const { locale } = this.routerData;
        this.communication.request$('map', {
            method: 'GET',
            urlParams: locale ? `?locale=${locale}` : '',
            onError: (e) => console.error(e)
        }).subscribe(({ dataSet }) => {
            if (dataSet) {
                this.one('mr-map').update(dataSet);
            }
        });
        this.getWidgetDataSource('mr-map').mapLoaded$
            .pipe(first())
            .subscribe(({ map, markers }) => {
            this.mapListener$.next({ map, markers });
        });
    }
    loadDefaults(navigate) {
        this.eventDescription = _t(this.pageConfig.defaultText);
        this.eventHeader = '';
        this.bibliographyData = undefined;
        this.collectionWitnessData = undefined;
        this.collectionWorksData = undefined;
        this.collectionGalleryData = undefined;
        if (navigate) {
            const href = this.localeService.getLinkByRouteId('map');
            this.location.go(href);
        }
        this.one('mr-year-header').update({
            title: { main: { text: _t(this.pageConfig.title) } },
        });
    }
    updatePageDetails(id) {
        const { locale } = this.routerData;
        this.communication.request$('resource', {
            onError: (e) => console.error(e),
            method: 'POST',
            params: {
                id, type: 'views/places'
            },
            urlParams: locale ? `?locale=${locale}` : '',
        }).subscribe((res) => {
            if (!res || res == null)
                return;
            const { 
            /* eslint-disable */
            'collection-bibliography': bibData, 'collection-places': placesData, 'collection-witnesses': witnessData, 'collection-works': worksData, gallery, header,
            /* eslint-enable */
             } = res.sections;
            if (placesData) {
                // this.hasMap = true;
                this.one('mr-map').update(placesData);
            }
            else {
                // this.hasMap = false;
            }
            if (bibData) {
                this.bibliographyData = bibData;
            }
            else {
                this.bibliographyData = undefined;
            }
            if (witnessData) {
                this.collectionWitnessData = {
                    items: witnessData.items.map((witness) => {
                        let anchor;
                        if (witness.routeId) {
                            const href = this.localeService
                                .getLinkByRouteId(witness.routeId, witness.id, witness.slug);
                            anchor = { href };
                        }
                        else if (witness.link) {
                            anchor = { href: witness.link };
                        }
                        return {
                            anchor,
                            title: witness.title,
                        };
                    }),
                    header: witnessData.header
                };
            }
            else {
                this.collectionWitnessData = undefined;
            }
            if (worksData?.items) {
                this.collectionWorksData = {
                    header: worksData.header,
                    items: worksData.items.map((item) => {
                        let anchor;
                        if (item.routeId) {
                            const href = this.localeService
                                .getLinkByRouteId(item.routeId, item.id, item.slug);
                            anchor = { href };
                        }
                        else if (item.link) {
                            anchor = { href: item.link };
                        }
                        return {
                            anchor,
                            image: item.image,
                            title: item.title,
                            text: item.text,
                        };
                    })
                };
            }
            else {
                this.collectionWorksData = undefined;
            }
            if (gallery) {
                this.collectionGalleryData = gallery;
            }
            else {
                this.collectionGalleryData = undefined;
            }
            if (header) {
                this.eventDescription = header.content;
                this.eventHeader = res.title;
                this.one('mr-year-header').update({
                    title: { main: { text: res.title } },
                    actions: {
                        buttons: [{
                                text: '',
                                icon: 'n7-icon-close',
                                anchor: {
                                    payload: 'closebutton'
                                }
                            }]
                    }
                });
            }
            this.loading.resourceDetails = false;
        });
    }
}

class MrMapLayoutEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-map-layout.init':
                    this.dataSource.onInit(payload);
                    this.route = payload.route;
                    this.router = payload.router;
                    this.localeService = payload.localeService;
                    this.location = payload.location;
                    this.listenRoute();
                    // scroll top
                    window.scrollTo(0, 0);
                    // listen for clicks on the map markers
                    this.dataSource.mapListener$
                        .subscribe(({ markers }) => {
                        markers.on('click', ({ layer: marker }) => {
                            if (!marker.id)
                                return;
                            const isSelected = marker.getIcon().options.className.includes('selected');
                            if (isSelected) {
                                // navigate to the clicked resource / marker
                                const href = this.localeService.getLinkByRouteId('mapItem', marker.id, marker.slug);
                                this.location.go(href);
                                this.dataSource.updatePageDetails(marker.id);
                            }
                            else {
                                const href = this.localeService.getLinkByRouteId('map');
                                this.location.go(href);
                                this.dataSource.loadDefaults();
                            }
                        });
                    });
                    break;
                case 'mr-timeline-layout.destroy':
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type }) => {
            switch (type) {
                case 'mr-year-header.closeevent':
                    this.dataSource.loadDefaults(true);
                    // emit signal
                    this.emitOuter('routechanged', null);
                    break;
                default:
                    break;
            }
        });
    }
    listenRoute() {
        this.route.paramMap.subscribe((params) => {
            const paramId = params.get('id');
            if (paramId) {
                this.dataSource.currentId = paramId;
                this.dataSource.updatePageDetails(paramId);
                // emit signal
                this.emitOuter('routechanged', paramId);
            }
            else {
                this.dataSource.loadDefaults(true);
            }
        });
    }
}

const MrMapLayoutConfig = {
    layoutId: 'mr-map-layout',
    widgets: [
        { id: 'mr-map' },
        { id: 'mr-year-header' }
    ],
    layoutDS: MrMapLayoutDS,
    layoutEH: MrMapLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    options: {},
};

class MrMapLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, route, router, location, configuration, communication, mainState, layoutState, localeService) {
        super(layoutsConfiguration.get('MrMapLayoutConfig') || MrMapLayoutConfig);
        this.route = route;
        this.router = router;
        this.location = location;
        this.configuration = configuration;
        this.communication = communication;
        this.mainState = mainState;
        this.layoutState = layoutState;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routerData: this.routerData,
            mainState: this.mainState,
            configuration: this.configuration,
            communication: this.communication,
            layoutState: this.layoutState,
            route: this.route,
            router: this.router,
            location: this.location,
            localeService: this.localeService,
            options: this.config.options || {}
        };
    }
    ngOnInit() {
        this.route.data.pipe(first()).subscribe((routerData) => {
            this.routerData = routerData;
            this.layoutState.add('content');
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrMapLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMapLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: i1$2.Location }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: i1.MainStateService }, { token: MrLayoutStateService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrMapLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrMapLayoutComponent, selector: "mr-map-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-map-layout mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <div class=\"mr-map-layout__map\">\n        <div class=\"mr-map-layout__map-loading\"\n             *ngIf=\"lb.dataSource.loading.timeline\">\n        </div>\n        <n7-map [data]=\"lb.widgets['mr-map'].ds.out$ | async\"></n7-map>\n        <!-- <n7-timeline [data]=\"lb.widgets['mr-timeline'].ds.out$ | async\"\n                     *ngIf=\"!lb.dataSource.loading.timeline\">\n        </n7-timeline> -->\n    </div>\n\n    <div class=\"mr-map-layout__page mr-side-margin\">\n        <div class=\"mr-map-layout__date\">\n            <n7-inner-title [data]=\"lb.widgets['mr-year-header'].ds.out$ | async\"\n                            [emit]=\"lb.widgets['mr-year-header'].emit\">\n            </n7-inner-title>\n        </div>\n        <!--\n        <h1 class=\"mr-map-layout__title\"\n            *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n            {{lb.dataSource.eventHeader}}\n        </h1>\n        -->\n        <div class=\"mr-map-layout__content\">\n            <!-- DESCRIZIONE -->\n            <div class=\"mr-content-block mr-content-block-description\" *ngIf=\"lb.dataSource.eventDescription\">\n                <p [innerHTML]=\"lb.dataSource.eventDescription\">\n                <p>\n            </div>\n            <ng-container *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n\n                <!-- GALLERIA -->\n                <div class=\"mr-content-block n7-grid-6\">\n                    <ng-container *ngFor=\"let image of lb.dataSource.collectionGalleryData\">\n                        <a [href]=\"image.image\" class=\"mr-gallery__image\">\n                            <img [src]=\"image.thumbnail\" alt=\"image.title\">\n                        </a>\n                    </ng-container>\n                </div>\n\n                <!-- BIBLIOGRAFIA -->\n                <ng-container *ngIf=\"lb.dataSource.bibliographyData as biblio\">\n                    <ng-container *ngIf=\"biblio.items && biblio.items.length > 0\">\n                        <div class=\"mr-content-block mr-content-block-collection\">\n                            <h3 class=\"mr-content-block__title\">{{ biblio.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-1\">\n                                <ng-container *ngFor=\"let item of biblio.items\">\n                                    <div class=\"mr-map-layout__collection-content\">\n                                        <n7-item-preview [data]=\"item\"></n7-item-preview>\n                                    </div>\n                                </ng-container>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- TESTIMONI -->\n                <ng-container *ngIf=\"lb.dataSource.collectionWitnessData as wit\">\n                    <ng-container *ngIf=\"wit.items && wit.items.length > 0\">\n                        <div class=\"mr-content-block-collection mr-content-block\">\n                            <h3 class=\"mr-content-block__title\">{{ wit.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-3\">\n                                <n7-item-preview *ngFor=\"let item of wit.items\"\n                                                 [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- OPERE -->\n                <ng-container *ngIf=\"lb.dataSource.collectionWorksData as works\">\n                    <ng-container *ngIf=\"works.items && works.items.length > 0\">\n                        <div class=\"mr-content-block-collection mr-content-block\">\n                            <h3 class=\"mr-content-block__title\">{{ works.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-3\">\n                                <n7-item-preview *ngFor=\"let item of works.items\"\n                                                 [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n            </ng-container>\n        </div>\n    </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.MapComponent, selector: "n7-map", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMapLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-map-layout', template: "<div class=\"mr-map-layout mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <div class=\"mr-map-layout__map\">\n        <div class=\"mr-map-layout__map-loading\"\n             *ngIf=\"lb.dataSource.loading.timeline\">\n        </div>\n        <n7-map [data]=\"lb.widgets['mr-map'].ds.out$ | async\"></n7-map>\n        <!-- <n7-timeline [data]=\"lb.widgets['mr-timeline'].ds.out$ | async\"\n                     *ngIf=\"!lb.dataSource.loading.timeline\">\n        </n7-timeline> -->\n    </div>\n\n    <div class=\"mr-map-layout__page mr-side-margin\">\n        <div class=\"mr-map-layout__date\">\n            <n7-inner-title [data]=\"lb.widgets['mr-year-header'].ds.out$ | async\"\n                            [emit]=\"lb.widgets['mr-year-header'].emit\">\n            </n7-inner-title>\n        </div>\n        <!--\n        <h1 class=\"mr-map-layout__title\"\n            *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n            {{lb.dataSource.eventHeader}}\n        </h1>\n        -->\n        <div class=\"mr-map-layout__content\">\n            <!-- DESCRIZIONE -->\n            <div class=\"mr-content-block mr-content-block-description\" *ngIf=\"lb.dataSource.eventDescription\">\n                <p [innerHTML]=\"lb.dataSource.eventDescription\">\n                <p>\n            </div>\n            <ng-container *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n\n                <!-- GALLERIA -->\n                <div class=\"mr-content-block n7-grid-6\">\n                    <ng-container *ngFor=\"let image of lb.dataSource.collectionGalleryData\">\n                        <a [href]=\"image.image\" class=\"mr-gallery__image\">\n                            <img [src]=\"image.thumbnail\" alt=\"image.title\">\n                        </a>\n                    </ng-container>\n                </div>\n\n                <!-- BIBLIOGRAFIA -->\n                <ng-container *ngIf=\"lb.dataSource.bibliographyData as biblio\">\n                    <ng-container *ngIf=\"biblio.items && biblio.items.length > 0\">\n                        <div class=\"mr-content-block mr-content-block-collection\">\n                            <h3 class=\"mr-content-block__title\">{{ biblio.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-1\">\n                                <ng-container *ngFor=\"let item of biblio.items\">\n                                    <div class=\"mr-map-layout__collection-content\">\n                                        <n7-item-preview [data]=\"item\"></n7-item-preview>\n                                    </div>\n                                </ng-container>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- TESTIMONI -->\n                <ng-container *ngIf=\"lb.dataSource.collectionWitnessData as wit\">\n                    <ng-container *ngIf=\"wit.items && wit.items.length > 0\">\n                        <div class=\"mr-content-block-collection mr-content-block\">\n                            <h3 class=\"mr-content-block__title\">{{ wit.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-3\">\n                                <n7-item-preview *ngFor=\"let item of wit.items\"\n                                                 [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- OPERE -->\n                <ng-container *ngIf=\"lb.dataSource.collectionWorksData as works\">\n                    <ng-container *ngIf=\"works.items && works.items.length > 0\">\n                        <div class=\"mr-content-block-collection mr-content-block\">\n                            <h3 class=\"mr-content-block__title\">{{ works.header.title }}</h3>\n                            <div class=\"mr-content-block__content n7-grid-3\">\n                                <n7-item-preview *ngFor=\"let item of works.items\"\n                                                 [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n            </ng-container>\n        </div>\n    </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: i1$2.Location }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: i1.MainStateService }, { type: MrLayoutStateService }, { type: MrLocaleService }]; } });

class MrPostsLayoutDS extends LayoutDataSource {
    onInit(payload) {
        this.configuration = payload.configuration;
        this.mainState = payload.mainState;
        this.routerData = payload.routerData;
        this.communication = payload.communication;
        this.pageConfig = this.configuration.get(this.routerData.configId);
        // config
        this.all().updateOptions({ config: this.pageConfig });
        // manual updates
        this.one('mr-search-page-title').update({});
        // update head title
        this.updateHeadTitle();
        // update translations
        this.addTranslations(this.pageConfig);
    }
    updateSearchTags(params) {
        if (!this.pageConfig.filters) {
            return;
        }
        const { labels } = this.pageConfig.filters;
        const tags = [];
        Object.keys(labels)
            .filter((key) => !!params[key])
            .forEach((key) => {
            tags[key] = params[key];
        });
        this.one('mr-advanced-search-tags').updateOptions({ labels });
        this.one('mr-advanced-search-tags').update(tags);
    }
    request$(params, onError) {
        const { locale } = this.routerData;
        const { searchId } = this.pageConfig;
        const searchParams = {
            ...params
        };
        Object.keys(searchParams)
            .filter((key) => ['page', 'limit', 'sort'].includes(key))
            .forEach((key) => {
            searchParams.results = searchParams.results || {};
            searchParams.results[key] = searchParams[key];
            delete searchParams[key];
        });
        // normalize results filters
        const resultsParams = {};
        const results = searchParams.results || {};
        const page = results.page ? +results.page : 1;
        resultsParams.limit = results.limit ? +results.limit : 12;
        resultsParams.offset = page === 1 ? 0 : resultsParams.limit * (page - 1);
        resultsParams.sort = results.sort || 'sort_ASC';
        return this.communication.request$('posts', {
            method: 'POST',
            params: {
                ...searchParams,
                searchId,
                results: {
                    ...resultsParams
                }
            },
            urlParams: locale ? `?locale=${locale}` : '',
            onError
        });
    }
    handleResponse(response) {
        this.some([
            'mr-search-results-title',
            'mr-search-results',
        ]).update(response);
        // pagination
        this.one('n7-smart-pagination').updateOptions({ mode: 'payload' });
        this.one('n7-smart-pagination').update(this.getPaginationParams(response));
    }
    updateHeadTitle() {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle)].join(' > '));
    }
    addTranslations(config) {
        if (config?.sort?.label) {
            config.sort.label = _t(config.sort.label);
            config.sort.options = config.sort.options.map((option) => ({
                ...option,
                label: _t(option.label)
            }));
        }
        ['text', 'button'].forEach((key) => {
            if (config.fallback) {
                config.fallback[key] = _t(config.fallback[key]);
            }
            if (config.ko) {
                config.ko[key] = _t(config.ko[key]);
            }
        });
        // filters
        const { filters } = this.pageConfig;
        if (filters) {
            filters.title = _t(filters.title);
            Object.keys(filters.labels).forEach((key) => {
                filters.labels[key] = _t(filters.labels[key]);
            });
        }
    }
    getPaginationParams(response) {
        const { total_count: totalCount, offset, limit } = response;
        const { pagination: paginationConfig } = this.pageConfig;
        return {
            totalPages: Math.ceil(totalCount / limit),
            currentPage: (offset + limit) / limit,
            pageLimit: paginationConfig.limit,
            sizes: {
                label: paginationConfig.selectLabel ? _t(paginationConfig.selectLabel) : null,
                list: paginationConfig.options,
                active: limit,
            },
        };
    }
}

class MrPostsLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-posts-layout.init':
                    this.activatedRoute = payload.activatedRoute;
                    this.router = payload.router;
                    this.layoutState = payload.layoutState;
                    this.dataSource.onInit(payload);
                    // listen route changes
                    this.listenToRouterChanges();
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                case 'mr-posts-layout.destroy':
                    this.destroy$.next();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'n7-smart-pagination.click':
                    this.updateRouter({ page: payload.page });
                    break;
                case 'n7-smart-pagination.change':
                    this.updateRouter({ limit: payload.value, page: 1 });
                    break;
                case 'mr-search-results-title.change':
                    this.updateRouter({ sort: payload.value, page: 1 });
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
    }
    /** URL changes */
    listenToRouterChanges() {
        this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$), tap(() => {
            this.layoutState.set('results', LayoutState.LOADING);
        }), switchMap((params) => {
            this.dataSource.updateSearchTags(params);
            return this.dataSource.request$(params, (error) => {
                console.warn('Posts search error', error);
                this.layoutState.set('results', LayoutState.ERROR);
            });
        })).subscribe((response) => {
            this.dataSource.handleResponse(response);
            this.layoutState.set('results', isEmpty(response.results) ? LayoutState.EMPTY : LayoutState.SUCCESS);
            // scroll to ref element
            if (!this.scrollRefElement) {
                this.scrollRefElement = document.querySelector('.scroll-ref');
            }
            else if (!helpers.isElementInViewport(this.scrollRefElement)) {
                this.scrollRefElement.scrollIntoView();
            }
        });
    }
    updateRouter(queryParams) {
        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge'
        });
    }
}

const MrPostsLayoutConfig = {
    layoutId: 'mr-posts-layout',
    widgets: [
        {
            id: 'mr-search-page-title'
        }, {
            id: 'mr-search-results-title'
        }, {
            id: 'mr-search-results'
        }, {
            id: 'n7-smart-pagination',
            dataSource: SmartPaginationDS,
            eventHandler: SmartPaginationEH,
        }, {
            id: 'mr-advanced-search-tags'
        }
    ],
    layoutDS: MrPostsLayoutDS,
    layoutEH: MrPostsLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class MrPostsLayoutComponent extends AbstractLayout {
    constructor(router, activatedRoute, mainState, configuration, communication, layoutState, layoutsConfiguration) {
        super(layoutsConfiguration.get('MrPostsLayoutConfig') || MrPostsLayoutConfig);
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.mainState = mainState;
        this.configuration = configuration;
        this.communication = communication;
        this.layoutState = layoutState;
    }
    initPayload() {
        return {
            routerData: this.routerData,
            configuration: this.configuration,
            communication: this.communication,
            mainState: this.mainState,
            router: this.router,
            activatedRoute: this.activatedRoute,
            layoutState: this.layoutState,
            options: this.config.options || {},
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routerData) => {
            this.routerData = routerData;
            // add layout states
            this.layoutState.add(['results']);
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrPostsLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrPostsLayoutComponent, deps: [{ token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1.MainStateService }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: MrLayoutStateService }, { token: i1.LayoutsConfigurationService }], target: i0.ɵɵFactoryTarget.Component });
MrPostsLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrPostsLayoutComponent, selector: "mr-posts-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-search mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-search__title\">\n            <div class=\"scroll-ref\">&nbsp;</div>\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n        \n        <div class=\"mr-search__results-content\">\n            <div class=\"mr-search__results-wrapper\">\n                <div class=\"mr-search__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                <div *ngIf=\"lb.dataSource.pageConfig['filters']\" class=\"mr-search__results-filters\">\n                    <span *ngIf=\"lb.dataSource.pageConfig['filters'].title\" \n                    class=\"mr-search__results-filters-title\">{{ lb.dataSource.pageConfig['filters'].title }}</span>\n                    <div class=\"mr-search__results-filters-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)\"\n                            [data]=\"tag\">\n                        </n7-tag>\n                    </div>\n                </div>\n                <main class=\"mr-search__results\">\n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-search__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-item-preview *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div class=\"mr-search__results-fallback\">\n                                <p class=\"mr-search__results-fallback-string\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <button class=\"n7-btn mr-search__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.fallback.button }}\n                                </button>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p class=\"mr-search__results-ko-string\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <button class=\"n7-btn mr-search__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                {{ lb.dataSource.pageConfig.ko.button }}\n                            </button>\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                <n7-smart-pagination\n                *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n            </div>\n        </div>\n\n    </section>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.ContentPlaceholderComponent, selector: "n7-content-placeholder", inputs: ["data"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.TagComponent, selector: "n7-tag", inputs: ["data", "emit"] }, { kind: "component", type: i1.SmartPaginationComponent, selector: "n7-smart-pagination", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrPostsLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-posts-layout', template: "<div class=\"mr-search mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-search__title\">\n            <div class=\"scroll-ref\">&nbsp;</div>\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n        \n        <div class=\"mr-search__results-content\">\n            <div class=\"mr-search__results-wrapper\">\n                <div class=\"mr-search__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                <div *ngIf=\"lb.dataSource.pageConfig['filters']\" class=\"mr-search__results-filters\">\n                    <span *ngIf=\"lb.dataSource.pageConfig['filters'].title\" \n                    class=\"mr-search__results-filters-title\">{{ lb.dataSource.pageConfig['filters'].title }}</span>\n                    <div class=\"mr-search__results-filters-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-advanced-search-tags'].ds.out$ | async)\"\n                            [data]=\"tag\">\n                        </n7-tag>\n                    </div>\n                </div>\n                <main class=\"mr-search__results\">\n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-search__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-item-preview *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div class=\"mr-search__results-fallback\">\n                                <p class=\"mr-search__results-fallback-string\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <button class=\"n7-btn mr-search__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.fallback.button }}\n                                </button>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p class=\"mr-search__results-ko-string\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <button class=\"n7-btn mr-search__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                {{ lb.dataSource.pageConfig.ko.button }}\n                            </button>\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                <n7-smart-pagination\n                *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n            </div>\n        </div>\n\n    </section>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1.MainStateService }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: MrLayoutStateService }, { type: i1.LayoutsConfigurationService }]; } });

class MrResourceLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.errorTitle = _t('global#layout_error_title');
        this.errorDescription = _t('global#layout_error_description');
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.mainState = payload.mainState;
        this.localeService = payload.localeService;
        this.routerData = payload.routerData;
        this.pageConfig = this.configuration.get(this.routerData.configId);
        // tabs config
        const tabs = this.configuration.get('tabs');
        const pageTabs = this.pageConfig.tabs;
        if (tabs && pageTabs) {
            this.tabConfig = tabs[pageTabs];
        }
        // add translations
        ['top', 'content'].forEach((type) => {
            this.pageConfig.sections[type] = this.pageConfig.sections[type].map((section) => ({
                ...section,
                title: _t(section.title)
            }));
        });
    }
    /** Request the configured widgets data */
    pageRequest$(id, onError) {
        const { locale } = this.routerData;
        const params = linksHelper.getQueryParams(document.location.search);
        const { top, content } = this.pageConfig.sections;
        const sections = top.concat(content);
        return this.communication.request$('resource', {
            onError,
            method: 'POST',
            params: {
                id,
                type: this.pageConfig.type,
                sections: sections.map((s) => s.id),
            },
            queryParams: params,
            urlParams: locale ? `?locale=${locale}` : '',
        });
    }
    handleResponse(response) {
        this.initSections(response);
        this.setLocaleResourceConfig(response);
        this.updateHeadTitle(response);
    }
    /** Load all the configured widgets */
    initSections(response) {
        const { top, content } = this.pageConfig.sections;
        const sections = top.concat(content);
        sections.forEach(({ id, type, tools, options }) => {
            // update section datasource
            const widgetDataSource = this.getWidgetDataSource(id);
            if (!widgetDataSource)
                return;
            const responseSection = response.sections[id];
            // set id
            widgetDataSource.id = id;
            // check viewer tools
            if (type === 'viewer') {
                // update image viewer options
                this.one(id).updateOptions({ tools });
            }
            if (type === 'text-viewer') {
                let url;
                try {
                    url = this.communication.getUrl('xmlSearch');
                }
                catch (e) {
                    url = '';
                    // do nothing
                }
                const searchApi = {
                    url,
                    'resource-id': this.id
                };
                options.searchApi = searchApi;
                this.one(id).updateOptions(options);
            }
            // update data
            if (responseSection && !helpers.isEmpty(responseSection)) {
                this.one(id).update(responseSection);
            }
            else {
                // unload the component without data
                this.one(id).update(undefined);
            }
            // init viewer overlay details
            if (type === 'viewer') {
                const overlayDetailsId = `${id}-overlay-details`;
                const widgetOverlayDetailsDS = this.getWidgetDataSource(overlayDetailsId);
                if (!widgetOverlayDetailsDS)
                    return;
                // set id
                widgetOverlayDetailsDS.id = overlayDetailsId;
            }
            // image viewer tools check
            if (type === 'viewer' && tools) {
                const toolsId = `${id}-tools`;
                // update image viewer tools datasource
                const widgetToolsDataSource = this.getWidgetDataSource(toolsId);
                if (!widgetToolsDataSource)
                    return;
                // set id
                widgetToolsDataSource.id = toolsId;
                // update data
                if (responseSection && !helpers.isEmpty(responseSection)) {
                    this.one(toolsId).update(responseSection);
                }
                else {
                    // unload the component without data
                    this.one(toolsId).update(undefined);
                }
            }
        });
        // update tabs
        if (this.tabConfig) {
            const tabSection = sections.find(({ type }) => type === 'tabs');
            this.one(tabSection.id).updateOptions({
                id: this.id,
                root: this.pageConfig.tabs,
                slug: this.slug,
                currentTab: this.tab,
                localeService: this.localeService
            });
            this.one(tabSection.id).update(this.tabConfig);
        }
    }
    // set resource locale config
    // used by language switcher
    setLocaleResourceConfig(response) {
        if (response?.locale) {
            this.localeService.setResourceConfig(response.locale);
        }
    }
    updateHeadTitle({ title: resourceTitle }) {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle), resourceTitle].join(' > '));
    }
}

// import mock from './resource-layout-mock-cambridge';
class MrResourceLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-resource-layout.init':
                    {
                        this.route = payload.route;
                        this.router = payload.router;
                        this.modalService = payload.modalService;
                        const { slug, id } = this.route.snapshot.params;
                        const { url } = this.route.snapshot;
                        this.dataSource.tab = url[url.length - 1].path;
                        this.dataSource.slug = slug;
                        this.dataSource.id = id;
                        this.layoutState = payload.layoutState;
                        this.dataSource.onInit(payload);
                        this.listenRoute();
                        // scroll top
                        window.scrollTo(0, 0);
                        // emit signal
                        this.emitOuter('init');
                    }
                    break;
                case 'mr-resource-layout.destroy':
                    this.destroy$.next();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            if (type.indexOf('openresourcemodal') !== -1) {
                const { id, type: resourceType } = payload;
                this.modalService.open(id, resourceType);
            }
            if (type.indexOf('thumbclick') !== -1) {
                const [sourceId] = type.split('.');
                const targetId = sourceId.replace('-tools', '');
                this.emitOuter('thumbclick', {
                    targetId,
                    thumbindex: payload
                });
            }
            if (type.indexOf('pagechange') !== -1) {
                const [sourceId] = type.split('.');
                const targetId = `${sourceId}-tools`;
                this.emitOuter('pagechange', {
                    targetId,
                    eventData: payload
                });
            }
            // image viewer overlay events
            if (type.includes('overlayclick')) {
                this.emitOuter('showoverlaydetails', payload);
            }
            if (type.includes('overlaypagechange')) {
                this.emitOuter('hideoverlaydetails', payload);
            }
        });
    }
    listenRoute() {
        this.route.paramMap.pipe(takeUntil(this.destroy$), tap(() => {
            this.layoutState.set('content', LayoutState.LOADING);
        }), map((params) => params.get('id')), switchMap((id) => this.dataSource.pageRequest$(id, (err) => {
            if (err.status === 404) {
                // getting not found path
                const { config } = this.router;
                const route404 = config.find(({ data }) => data?.id === 'page-404');
                const path404 = route404?.path || 'page-404';
                this.router.navigate([path404]);
            }
            console.warn(`Error loading resource layout for ${id}`, err.message);
            this.dataSource.id = id;
            this.layoutState.set('content', LayoutState.ERROR);
        }))).subscribe((response) => {
            this.layoutState.set('content', LayoutState.SUCCESS);
            this.dataSource.handleResponse(response);
            // this.dataSource.handleResponse(mock);
            // scroll top
            window.scrollTo(0, 0);
        });
    }
}

const MrResourceLayoutConfig = {
    layoutId: 'mr-resource-layout',
    widgets: [
        { id: 'mr-read-more' },
        { id: 'mr-metadata-readmore' },
        { id: 'mr-metadata-dynamic' }
    ],
    layoutDS: MrResourceLayoutDS,
    layoutEH: MrResourceLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    options: {
    // TODO
    },
};

class MrImageViewerEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.click`:
                    this.emitOuter('click', payload);
                    break;
                case `${this.dataSource.id}.pagechange`:
                    this.emitOuter('pagechange', payload);
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-resource-layout.init':
                    this.listenToViewer();
                    break;
                case 'mr-resource-layout.thumbclick':
                    if (payload.targetId === this.dataSource.id) {
                        this.dataSource.changePage(payload.thumbindex);
                    }
                    break;
                case 'mr-resource-layout.overlaycloseclick':
                    this.dataSource.overlayCloseClick();
                    break;
                case 'mr-resource-layout.pagechange':
                    // Silent
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
    }
    listenToViewer() {
        // loaded
        this.dataSource.viewerLoaded$.pipe(first()).subscribe(() => {
            const { viewer } = this.dataSource;
            viewer.addHandler('page', (eventData) => {
                this.emitOuter('pagechange', eventData);
            });
        });
        this.dataSource.overlayEvents$.subscribe(({ type, payload }) => {
            this.emitOuter(`overlay${type}`, {
                targetId: this.dataSource.id,
                ...payload
            });
        });
    }
}

class MrImageViewerToolsEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.click`:
                    if (payload.thumbindex !== undefined) {
                        const index = payload.thumbindex;
                        this.dataSource.handleThumbs(index);
                        this.emitOuter('thumbclick', index);
                        break;
                    }
                    if (payload === 'close-description') {
                        this.dataSource.toggleDescription();
                        break;
                    }
                    if (payload === 'toggle-description') {
                        this.dataSource.toggleDescription();
                        break;
                    }
                    if (payload === 'toggle-thumbs') {
                        this.dataSource.toggleThumbs();
                        break;
                    }
                    if (payload === 'next') {
                        // let index = this.dataSource.retrieveIndex();
                        // this.dataSource.handleThumbsNext(index);
                        // let updatedIndex = this.dataSource.retrieveIndex();
                        // this.emitOuter('thumbclick', updatedIndex);
                        this.dataSource.scrollRight();
                        break;
                    }
                    if (payload === 'prev') {
                        // let index = this.dataSource.retrieveIndex();
                        // this.dataSource.handleThumbsPrev(index);
                        // let updatedIndex = this.dataSource.retrieveIndex();
                        // this.emitOuter('thumbclick', updatedIndex);
                        this.dataSource.scrollLeft();
                        break;
                    }
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            // console.log(payload);
            switch (type) {
                case 'mr-resource-layout.init':
                case 'mr-resource-layout.thumbclick':
                    // Silent
                    break;
                case 'mr-resource-layout.pagechange':
                    if (payload.targetId === this.dataSource.id) {
                        this.dataSource.handlePageChange(payload.eventData);
                    }
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
    }
}

class MrImageViewerOverlayDetailsEH extends EventHandler {
    listen() {
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-resource-layout.hideoverlaydetails':
                case 'mr-resource-layout.overlaycloseclick':
                    this.dataSource.hide();
                    break;
                case 'mr-resource-layout.showoverlaydetails':
                    {
                        const target = `${payload.targetId}-overlay-details`;
                        if (target === this.dataSource.id) {
                            this.dataSource.show(payload);
                        }
                    }
                    break;
                default:
                    // console.warn('unhandled event of type', type);
                    break;
            }
        });
    }
}

//---------------------------
class MrMetadataReadmoreComponent {
}
MrMetadataReadmoreComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMetadataReadmoreComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrMetadataReadmoreComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrMetadataReadmoreComponent, selector: "mr-metadata-readmore", inputs: { data: "data", emit: "emit" }, ngImport: i0, template: "<div *ngIf=\"data\"\n     class=\"n7-metadata-viewer\">\n    <!-- Metadata groups wrapper -->\n    <div class=\"n7-metadata-viewer__wrapper {{ data.classes  || ''}}\"\n         *ngFor=\"let grp of data.group\">\n        <!-- For each data group, render it -->\n        <ng-container *ngTemplateOutlet=\"group; context:{$implicit: grp}\"></ng-container>\n    </div>\n</div>\n\n<!-- Template for Metadata.group -->\n<ng-template #group\n             let-grp>\n    <div *ngIf=\"grp.title || grp.items\"\n         class=\"n7-metadata-viewer__group {{ grp.classes || '' }}\">\n        <div class=\"n7-metadata-viewer__group-title\"\n             *ngIf=\"grp.title\">{{grp.title}}</div>\n        <div class=\"n7-metadata-viewer__group-content\"\n             *ngIf=\"grp.items\">\n            <!-- \"read more\" enabled on the metadata group -->\n            <mr-read-more [data]=\"grp.groupReadmore\">\n                <div class=\"n7-metadata-viewer__item\"\n                    *ngFor=\"let i of grp.items\">\n                    <span *ngIf=\"i.label\" class=\"n7-metadata-viewer__item-label\">\n                        {{ i.label }}\n                    </span>\n                    <!-- \"read more\" enabled on the metadata value field -->\n                    <mr-read-more [data]=\"grp.readmore\">\n                        <span *ngIf=\"i.value\"\n                            class=\"n7-metadata-viewer__item-value\"\n                            [innerHTML]=\"i.value\">\n                        </span>\n                    </mr-read-more>\n                </div>\n            </mr-read-more>\n        </div>\n    </div>\n    <!-- Render all child groups -->\n    <div *ngIf=\"grp.group\">\n        <div class=\"n7-metadata-viewer__group-wrapper\"\n             *ngFor=\"let grp of grp.group\">\n            <ng-container *ngTemplateOutlet=\"group; context:{$implicit: grp}\"></ng-container>\n        </div>\n    </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: ReadMoreComponent, selector: "mr-read-more", inputs: ["data"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMetadataReadmoreComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-metadata-readmore', template: "<div *ngIf=\"data\"\n     class=\"n7-metadata-viewer\">\n    <!-- Metadata groups wrapper -->\n    <div class=\"n7-metadata-viewer__wrapper {{ data.classes  || ''}}\"\n         *ngFor=\"let grp of data.group\">\n        <!-- For each data group, render it -->\n        <ng-container *ngTemplateOutlet=\"group; context:{$implicit: grp}\"></ng-container>\n    </div>\n</div>\n\n<!-- Template for Metadata.group -->\n<ng-template #group\n             let-grp>\n    <div *ngIf=\"grp.title || grp.items\"\n         class=\"n7-metadata-viewer__group {{ grp.classes || '' }}\">\n        <div class=\"n7-metadata-viewer__group-title\"\n             *ngIf=\"grp.title\">{{grp.title}}</div>\n        <div class=\"n7-metadata-viewer__group-content\"\n             *ngIf=\"grp.items\">\n            <!-- \"read more\" enabled on the metadata group -->\n            <mr-read-more [data]=\"grp.groupReadmore\">\n                <div class=\"n7-metadata-viewer__item\"\n                    *ngFor=\"let i of grp.items\">\n                    <span *ngIf=\"i.label\" class=\"n7-metadata-viewer__item-label\">\n                        {{ i.label }}\n                    </span>\n                    <!-- \"read more\" enabled on the metadata value field -->\n                    <mr-read-more [data]=\"grp.readmore\">\n                        <span *ngIf=\"i.value\"\n                            class=\"n7-metadata-viewer__item-value\"\n                            [innerHTML]=\"i.value\">\n                        </span>\n                    </mr-read-more>\n                </div>\n            </mr-read-more>\n        </div>\n    </div>\n    <!-- Render all child groups -->\n    <div *ngIf=\"grp.group\">\n        <div class=\"n7-metadata-viewer__group-wrapper\"\n             *ngFor=\"let grp of grp.group\">\n            <ng-container *ngTemplateOutlet=\"group; context:{$implicit: grp}\"></ng-container>\n        </div>\n    </div>\n</ng-template>\n" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }] } });

class MrMetadataDynamicComponent {
    constructor() {
        this.fakeEmit = (type, payload) => {
            if (!this.emit) {
                return;
            }
            this.emit(type, payload);
        };
    }
    obtainData(section) {
        const metadataDynamicDS = new MrMetadataDynamicDS();
        return metadataDynamicDS.prepareMeta(section);
    }
}
MrMetadataDynamicComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMetadataDynamicComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrMetadataDynamicComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrMetadataDynamicComponent, selector: "mr-metadata-dynamic", inputs: { data: "data", emit: "emit" }, ngImport: i0, template: "<ng-container *ngIf=\"data.data\">\n  <ng-container *ngFor=\"let group of data.data; index as i\">\n    <ng-container *ngTemplateOutlet=\"metadataViewer; context:{$implicit: group}\"></ng-container>\n  </ng-container>\n</ng-container>\n\n<ng-template #metadataViewer let-group>\n  <div class=\"mr-content-block mr-content-block-metadata\">\n    <div *ngIf=\"group\" class=\"mr-metadata-dynamic\" [ngClass]=\"{'mr-metadata-dynamic-accordion' : group.title}\">\n      \n      <!-- Header accordion (se presente) -->\n      <div *ngIf=\"group.options && (group.options.showHeader !== false) && group.title\" class=\"mr-metadata-dynamic-accordion__header\">\n        <n7-facet-header \n          [data]=\"group.options\" \n          [emit]=\"fakeEmit\"\n        ></n7-facet-header>\n      </div>\n\n      <!-- Content -->\n      <div *ngIf=\"group.options.isOpen\" class=\"mr-content-block__content\" [attr.id]=\"group.id\">\n        <mr-read-more [data]=\"group.readmore\">\n          <ng-container *ngIf=\" group?.options?.readmore || group?.options?.groupReadmore;\n                      else wihoutReadmore\">\n            <mr-metadata-readmore\n                [data]=\"obtainData(group)\">\n            </mr-metadata-readmore>\n          </ng-container>\n          <ng-template #wihoutReadmore>\n            <n7-metadata-viewer\n                [data]=\"obtainData(group)\">\n            </n7-metadata-viewer>\n          </ng-template>\n        </mr-read-more>\n      </div>\n\n    </div>\n  </div>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i7.FacetHeaderComponent, selector: "n7-facet-header", inputs: ["data", "emit"] }, { kind: "component", type: i7.MetadataViewerComponent, selector: "n7-metadata-viewer", inputs: ["data", "emit"] }, { kind: "component", type: MrMetadataReadmoreComponent, selector: "mr-metadata-readmore", inputs: ["data", "emit"] }, { kind: "component", type: ReadMoreComponent, selector: "mr-read-more", inputs: ["data"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMetadataDynamicComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-metadata-dynamic', template: "<ng-container *ngIf=\"data.data\">\n  <ng-container *ngFor=\"let group of data.data; index as i\">\n    <ng-container *ngTemplateOutlet=\"metadataViewer; context:{$implicit: group}\"></ng-container>\n  </ng-container>\n</ng-container>\n\n<ng-template #metadataViewer let-group>\n  <div class=\"mr-content-block mr-content-block-metadata\">\n    <div *ngIf=\"group\" class=\"mr-metadata-dynamic\" [ngClass]=\"{'mr-metadata-dynamic-accordion' : group.title}\">\n      \n      <!-- Header accordion (se presente) -->\n      <div *ngIf=\"group.options && (group.options.showHeader !== false) && group.title\" class=\"mr-metadata-dynamic-accordion__header\">\n        <n7-facet-header \n          [data]=\"group.options\" \n          [emit]=\"fakeEmit\"\n        ></n7-facet-header>\n      </div>\n\n      <!-- Content -->\n      <div *ngIf=\"group.options.isOpen\" class=\"mr-content-block__content\" [attr.id]=\"group.id\">\n        <mr-read-more [data]=\"group.readmore\">\n          <ng-container *ngIf=\" group?.options?.readmore || group?.options?.groupReadmore;\n                      else wihoutReadmore\">\n            <mr-metadata-readmore\n                [data]=\"obtainData(group)\">\n            </mr-metadata-readmore>\n          </ng-container>\n          <ng-template #wihoutReadmore>\n            <n7-metadata-viewer\n                [data]=\"obtainData(group)\">\n            </n7-metadata-viewer>\n          </ng-template>\n        </mr-read-more>\n      </div>\n\n    </div>\n  </div>\n</ng-template>\n" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }] } });

const DATASOURCE_MAP$2 = {
    breadcrumbs: MrBreadcrumbsDS,
    collection: MrCollectionDS,
    info: MrInfoBoxDS,
    metadata: MrMetadataDS,
    preview: MrItemPreviewDS,
    text: MrTextViewerDS,
    title: MrInnerTitleDS,
    viewer: MrImageViewerDS,
    'viewer-tools': MrImageViewerToolsDS,
    'viewer-overlay-details': MrImageViewerOverlayDetailsDS,
    tabs: MrResourceTabsDS,
    'text-viewer': MrTextViewerDS,
    map: MrMapDS,
    'metadata-dynamic': MrMetadataDynamicDS,
};
const EVENTHANDLER_MAP$1 = {
    viewer: MrImageViewerEH,
    'viewer-tools': MrImageViewerToolsEH,
    'viewer-overlay-details': MrImageViewerOverlayDetailsEH,
    'text-viewer': MrTextViewerEH,
    collection: MrCollectionEH,
    'metadata-dynamic': MrMetadataDynamicEH,
    // map: MrMapEH
};
class MrResourceLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, activatedRoute, configuration, communication, mainState, route, router, layoutState, modalService, localeService) {
        super(layoutsConfiguration.get('MrResourceLayoutConfig') || MrResourceLayoutConfig);
        this.activatedRoute = activatedRoute;
        this.configuration = configuration;
        this.communication = communication;
        this.mainState = mainState;
        this.route = route;
        this.router = router;
        this.layoutState = layoutState;
        this.modalService = modalService;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routerData: this.routerData,
            configuration: this.configuration,
            communication: this.communication,
            mainState: this.mainState,
            layoutState: this.layoutState,
            modalService: this.modalService,
            localeService: this.localeService,
            options: this.config.options || {},
            route: this.route,
            router: this.router
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routerData) => {
            this.layoutState.add('content');
            this.routerData = routerData;
            this.loadWidgets();
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
    loadWidgets() {
        const { configId } = this.routerData;
        const { top, content } = this.configuration.get(configId).sections;
        const sections = top.concat(content);
        this.widgets = [];
        if (sections) {
            sections.forEach(({ id, type, options, tools }) => {
                const widgetOptions = options || {};
                widgetOptions.localeService = this.localeService;
                this.widgets.push({
                    id,
                    options: widgetOptions,
                    dataSource: DATASOURCE_MAP$2[type],
                    eventHandler: EVENTHANDLER_MAP$1[type]
                });
                // image viewer overlay details
                if (type === 'viewer') {
                    this.widgets.push({
                        id: `${id}-overlay-details`,
                        options: widgetOptions,
                        dataSource: DATASOURCE_MAP$2[`${type}-overlay-details`],
                        eventHandler: EVENTHANDLER_MAP$1[`${type}-overlay-details`]
                    });
                }
                // image viewer tools
                if (type === 'viewer' && tools) {
                    this.widgets.push({
                        id: `${id}-tools`,
                        options: widgetOptions,
                        dataSource: DATASOURCE_MAP$2[`${type}-tools`],
                        eventHandler: EVENTHANDLER_MAP$1[`${type}-tools`]
                    });
                }
            });
        }
    }
}
MrResourceLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.ActivatedRoute }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: i1.MainStateService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: MrLayoutStateService }, { token: MrResourceModalService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrResourceLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrResourceLayoutComponent, selector: "mr-resource-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-resource mr-layout\" \n     *ngIf=\"lb.dataSource && lb.dataSource.pageConfig\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- RESOURCE LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <ng-container *ngIf=\"lb.dataSource.pageConfig.sections as sections\">\n                <!-- Pass the list of blocks to render to the block template -->\n                <div class=\"mr-resource__top\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.top }\"></ng-container>\n                </div>\n                <div class=\"mr-resource__content mr-side-margin\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.content }\"></ng-container>\n                </div>\n            </ng-container>\n        </ng-container>\n\n    </ng-container>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"lb.widgets[section.id].ds.out$ | async\"\n        class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n    \n                <!-- TABS -->\n                <ng-container *ngSwitchCase=\"'tabs'\">\n                    <ng-container *ngFor=\"let tab of lb.widgets[section.id].ds.out$ | async\">\n                        <n7-anchor-wrapper [data]=\"tab.anchor\" [classes]=\"tab.classes\">\n                            <span class=\"mr-resource__tabs-item\">{{ tab.label }}</span>\n                        </n7-anchor-wrapper>\n                    </ng-container>\n                </ng-container>\n    \n                <!-- INNER TITLE -->\n                <ng-container *ngSwitchCase=\"'title'\">\n                    <div class=\"mr-resource__title-content mr-side-margin\">\n                        <n7-inner-title [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                            [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-inner-title>\n                    </div>\n                </ng-container>\n    \n                <!-- IMAGE VIEWER -->\n                <ng-container *ngSwitchCase=\"'viewer'\">\n                    <n7-image-viewer \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\" \n                        [emit]=\"lb.widgets[section.id].emit\">\n                    </n7-image-viewer>\n                    <!-- IMAGE VIEWER TOOLS -->\n                    <n7-image-viewer-tools *ngIf=\"section.tools\" \n                        [data]=\"lb.widgets[section.id + '-tools'].ds.out$ | async\" \n                        [emit]=\"lb.widgets[section.id + '-tools'].emit\">\n                    </n7-image-viewer-tools>\n                    <!-- IMAGE VIEWER OVERLAY DETAILS -->\n                    <div *ngIf=\"lb.widgets[section.id + '-overlay-details'].ds.out$ | async as viewerDetailsData\" \n                        class=\"mr-resource__viewer-overlay-details\">\n                        <div class=\"mr-resource__viewer-overlay-details__close\">\n                            <a class=\"mr-resource__viewer-overlay-details__close-link\" \n                                (click)=\"lb.eventHandler.emitOuter('overlaycloseclick')\">\n                                <span class=\"n7-icon-close\"></span>\n                            </a>\n                        </div>\n                        <n7-item-preview \n                            [data]=\"viewerDetailsData\" \n                            [emit]=\"lb.widgets[section.id + '-overlay-details'].emit\">\n                        </n7-item-preview>\n                    </div>\n                </ng-container>\n    \n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    \n                    <div class=\"mr-content-block mr-content-block-metadata\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-read-more [data]=\"section.readmore\">\n                                <ng-container *ngIf=\" section?.options?.readmore || section?.options?.groupReadmore;\n                                            else wihoutReadmore\">\n                                    <mr-metadata-readmore\n                                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                        [emit]=\"lb.widgets[section.id].emit\">\n                                    </mr-metadata-readmore>\n                                </ng-container>\n                                <ng-template #wihoutReadmore>\n                                    <n7-metadata-viewer\n                                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                        [emit]=\"lb.widgets[section.id].emit\">\n                                    </n7-metadata-viewer>\n                                </ng-template>\n                            </mr-read-more>\n                        </div>\n                    </div>\n      \n                </ng-container>\n                    \n                <!-- METADATA DYNAMIC -->\n                <ng-container *ngSwitchCase=\"'metadata-dynamic'\">\n                    <mr-metadata-dynamic \n                    [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[section.id].emit\">\n                    </mr-metadata-dynamic>                    \n                </ng-container>\n    \n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"lb.widgets[section.id].ds.out$ | async as collection$\">\n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-content-block mr-content-block-collection\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                                {{ section.title }}\n                            </h3>\n                            <div class=\"mr-content-block__content {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\"\n                                    [data]=\"item\" [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n    \n                <!-- ITEM PREVIEW -->\n                <ng-container *ngSwitchCase=\"'preview'\">\n                    <div class=\"mr-content-block mr-content-block-item-preview\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <n7-item-preview [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">        \n                            </n7-item-preview>\n                        </div>\n                    </div>\n                </ng-container>\n    \n                <!-- TEXT VIEWER -->\n                <ng-container *ngSwitchCase=\"'text-viewer'\">\n                  <div class=\"mr-content-block mr-content-block-text-viewer\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                          <n7-text-viewer [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">\n                          </n7-text-viewer>\n                    </div>\n                  </div>\n                  \n                </ng-container>\n\n                <!-- MAP -->\n             <ng-container *ngSwitchCase=\"'map'\">\n                  <div class=\"mr-content-block mr-content-block-map\">\n                    <div class=\"mr-content-block__content\">\n                        <n7-map [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\"></n7-map>\n                    </div>\n                </div>\n            </ng-container>\n    \n                <!-- INFO BOX -->\n                <ng-container *ngSwitchCase=\"'info-box'\">\n                    <div class=\"mr-content-block mr-content-block-info-box\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <div class=\"info-box__mock\">info-box</div>    \n                        </div>\n                    </div>\n                </ng-container>\n    \n                <!-- BREADCRUMBS -->\n                <ng-container *ngSwitchCase=\"'breadcrumbs'\">\n                    <n7-breadcrumbs [data]=\"lb.widgets[section.id].ds.out$ | async\">\n                    </n7-breadcrumbs>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.AnchorWrapperComponent, selector: "n7-anchor-wrapper", inputs: ["data", "classes"], outputs: ["clicked"] }, { kind: "component", type: i7.BreadcrumbsComponent, selector: "n7-breadcrumbs", inputs: ["data", "emit"] }, { kind: "component", type: i7.ImageViewerComponent, selector: "n7-image-viewer", inputs: ["data", "emit"] }, { kind: "component", type: i7.ImageViewerToolsComponent, selector: "n7-image-viewer-tools", inputs: ["data", "emit"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "component", type: i7.MapComponent, selector: "n7-map", inputs: ["data", "emit"] }, { kind: "component", type: i7.MetadataViewerComponent, selector: "n7-metadata-viewer", inputs: ["data", "emit"] }, { kind: "component", type: i7.TextViewerComponent, selector: "n7-text-viewer", inputs: ["data", "emit"] }, { kind: "component", type: MrMetadataReadmoreComponent, selector: "mr-metadata-readmore", inputs: ["data", "emit"] }, { kind: "component", type: ReadMoreComponent, selector: "mr-read-more", inputs: ["data"] }, { kind: "component", type: MrMetadataDynamicComponent, selector: "mr-metadata-dynamic", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-resource-layout', template: "<div class=\"mr-resource mr-layout\" \n     *ngIf=\"lb.dataSource && lb.dataSource.pageConfig\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- RESOURCE LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <ng-container *ngIf=\"lb.dataSource.pageConfig.sections as sections\">\n                <!-- Pass the list of blocks to render to the block template -->\n                <div class=\"mr-resource__top\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.top }\"></ng-container>\n                </div>\n                <div class=\"mr-resource__content mr-side-margin\">\n                    <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.content }\"></ng-container>\n                </div>\n            </ng-container>\n        </ng-container>\n\n    </ng-container>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"lb.widgets[section.id].ds.out$ | async\"\n        class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n    \n                <!-- TABS -->\n                <ng-container *ngSwitchCase=\"'tabs'\">\n                    <ng-container *ngFor=\"let tab of lb.widgets[section.id].ds.out$ | async\">\n                        <n7-anchor-wrapper [data]=\"tab.anchor\" [classes]=\"tab.classes\">\n                            <span class=\"mr-resource__tabs-item\">{{ tab.label }}</span>\n                        </n7-anchor-wrapper>\n                    </ng-container>\n                </ng-container>\n    \n                <!-- INNER TITLE -->\n                <ng-container *ngSwitchCase=\"'title'\">\n                    <div class=\"mr-resource__title-content mr-side-margin\">\n                        <n7-inner-title [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                            [emit]=\"lb.widgets[section.id].emit\">\n                        </n7-inner-title>\n                    </div>\n                </ng-container>\n    \n                <!-- IMAGE VIEWER -->\n                <ng-container *ngSwitchCase=\"'viewer'\">\n                    <n7-image-viewer \n                        [data]=\"lb.widgets[section.id].ds.out$ | async\" \n                        [emit]=\"lb.widgets[section.id].emit\">\n                    </n7-image-viewer>\n                    <!-- IMAGE VIEWER TOOLS -->\n                    <n7-image-viewer-tools *ngIf=\"section.tools\" \n                        [data]=\"lb.widgets[section.id + '-tools'].ds.out$ | async\" \n                        [emit]=\"lb.widgets[section.id + '-tools'].emit\">\n                    </n7-image-viewer-tools>\n                    <!-- IMAGE VIEWER OVERLAY DETAILS -->\n                    <div *ngIf=\"lb.widgets[section.id + '-overlay-details'].ds.out$ | async as viewerDetailsData\" \n                        class=\"mr-resource__viewer-overlay-details\">\n                        <div class=\"mr-resource__viewer-overlay-details__close\">\n                            <a class=\"mr-resource__viewer-overlay-details__close-link\" \n                                (click)=\"lb.eventHandler.emitOuter('overlaycloseclick')\">\n                                <span class=\"n7-icon-close\"></span>\n                            </a>\n                        </div>\n                        <n7-item-preview \n                            [data]=\"viewerDetailsData\" \n                            [emit]=\"lb.widgets[section.id + '-overlay-details'].emit\">\n                        </n7-item-preview>\n                    </div>\n                </ng-container>\n    \n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    \n                    <div class=\"mr-content-block mr-content-block-metadata\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <mr-read-more [data]=\"section.readmore\">\n                                <ng-container *ngIf=\" section?.options?.readmore || section?.options?.groupReadmore;\n                                            else wihoutReadmore\">\n                                    <mr-metadata-readmore\n                                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                        [emit]=\"lb.widgets[section.id].emit\">\n                                    </mr-metadata-readmore>\n                                </ng-container>\n                                <ng-template #wihoutReadmore>\n                                    <n7-metadata-viewer\n                                        [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                                        [emit]=\"lb.widgets[section.id].emit\">\n                                    </n7-metadata-viewer>\n                                </ng-template>\n                            </mr-read-more>\n                        </div>\n                    </div>\n      \n                </ng-container>\n                    \n                <!-- METADATA DYNAMIC -->\n                <ng-container *ngSwitchCase=\"'metadata-dynamic'\">\n                    <mr-metadata-dynamic \n                    [data]=\"lb.widgets[section.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[section.id].emit\">\n                    </mr-metadata-dynamic>                    \n                </ng-container>\n    \n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"lb.widgets[section.id].ds.out$ | async as collection$\">\n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-content-block mr-content-block-collection\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                                {{ section.title }}\n                            </h3>\n                            <div class=\"mr-content-block__content {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\"\n                                    [data]=\"item\" [emit]=\"lb.widgets[section.id].emit\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n    \n                <!-- ITEM PREVIEW -->\n                <ng-container *ngSwitchCase=\"'preview'\">\n                    <div class=\"mr-content-block mr-content-block-item-preview\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <n7-item-preview [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">        \n                            </n7-item-preview>\n                        </div>\n                    </div>\n                </ng-container>\n    \n                <!-- TEXT VIEWER -->\n                <ng-container *ngSwitchCase=\"'text-viewer'\">\n                  <div class=\"mr-content-block mr-content-block-text-viewer\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                          <n7-text-viewer [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\">\n                          </n7-text-viewer>\n                    </div>\n                  </div>\n                  \n                </ng-container>\n\n                <!-- MAP -->\n             <ng-container *ngSwitchCase=\"'map'\">\n                  <div class=\"mr-content-block mr-content-block-map\">\n                    <div class=\"mr-content-block__content\">\n                        <n7-map [data]=\"lb.widgets[section.id].ds.out$ | async\" [emit]=\"lb.widgets[section.id].emit\"></n7-map>\n                    </div>\n                </div>\n            </ng-container>\n    \n                <!-- INFO BOX -->\n                <ng-container *ngSwitchCase=\"'info-box'\">\n                    <div class=\"mr-content-block mr-content-block-info-box\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-content-block__title\">\n                            {{ section.title }}\n                        </h3>\n                        <div class=\"mr-content-block__content\">\n                            <div class=\"info-box__mock\">info-box</div>    \n                        </div>\n                    </div>\n                </ng-container>\n    \n                <!-- BREADCRUMBS -->\n                <ng-container *ngSwitchCase=\"'breadcrumbs'\">\n                    <n7-breadcrumbs [data]=\"lb.widgets[section.id].ds.out$ | async\">\n                    </n7-breadcrumbs>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.ActivatedRoute }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: i1.MainStateService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: MrLayoutStateService }, { type: MrResourceModalService }, { type: MrLocaleService }]; } });

class SearchFacetsLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.inputsDS = {};
    }
    onInit(payload) {
        this.searchService = payload.searchService;
        this.searchConfig = this.searchService.getConfig();
        this.facets = this.searchConfig.facets;
        this.initInputs();
    }
    initInputs() {
        // set components data
        this.facets.sections.forEach(({ header, inputs }) => {
            [header, ...inputs]
                .filter((input) => input)
                .forEach((input) => {
                // set id
                const widgetDataSource = this.getWidgetDataSource(input.id);
                widgetDataSource.id = input.id;
                // caching DS for next updates
                this.inputsDS[input.id] = widgetDataSource;
                // first update
                if (input.data) {
                    widgetDataSource.update(input.data);
                }
            });
        });
    }
    updateInputValue(id, newValue) {
        const ds = this.inputsDS[id];
        ds.setValue(newValue, ds.value !== newValue);
    }
    updateInputData(id, newData) {
        const ds = this.inputsDS[id];
        ds.update({
            ...ds.input,
            ...newData
        });
        // refresh selected
        ds.setValue(ds.value, true);
    }
    clearInput(id) {
        const ds = this.inputsDS[id];
        ds.clear();
        ds.setValue(ds.value, true);
    }
    clearInputs() {
        Object.keys(this.inputsDS).forEach((id) => {
            this.clearInput(id);
        });
    }
}

class SearchFacetsLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.changed$ = {};
        this.destroyed$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-facets-layout.init':
                    this.searchService = payload.searchService;
                    // listeners
                    this.initChangedListener(this.searchService.getConfig());
                    this.initStateListener();
                    // init
                    this.dataSource.onInit(payload);
                    this.emitOuter('facetloaded');
                    break;
                case 'mr-search-facets-layout.destroy':
                    this.destroyed$.next();
                    break;
                default:
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            if (type.indexOf('change')) {
                this.changed$[payload.id].next(payload);
            }
        });
    }
    initChangedListener({ facets }) {
        facets.sections.forEach((section) => {
            const sources = [];
            if (section.header) {
                const { id, delay } = section.header;
                sources.push({ id, delay });
            }
            section.inputs.forEach(({ id, delay }) => {
                sources.push({ id, delay });
            });
            sources.forEach((source) => {
                this.changed$[source.id] = new Subject();
                this.changed$[source.id].pipe(debounceTime(source.delay || 1)).subscribe(({ id, value }) => {
                    this.searchService.setState(INPUT_STATE_CONTEXT, id, value);
                });
            });
        });
    }
    initStateListener() {
        // listener for input updates
        this.searchService.getState$(INPUT_STATE_CONTEXT)
            .pipe(takeUntil(this.destroyed$), filter(({ lastUpdated }) => this.dataSource.inputsDS[lastUpdated])).subscribe(({ lastUpdated, state }) => {
            const newValue = state[lastUpdated];
            if (newValue === null) {
                this.dataSource.clearInput(lastUpdated);
            }
            else {
                this.dataSource.updateInputValue(lastUpdated, newValue);
            }
        });
        // listener for facet updates
        this.searchService.getState$(FACET_STATE_CONTEXT)
            .pipe(takeUntil(this.destroyed$), filter(({ lastUpdated }) => this.dataSource.inputsDS[lastUpdated])).subscribe(({ lastUpdated, state }) => {
            const newData = state[lastUpdated];
            this.dataSource.updateInputData(lastUpdated, newData);
        });
        // listener for facet header updates
        this.searchService.getState$(FACETS_REQUEST_STATE_CONTEXT, 'success')
            .pipe(takeUntil(this.destroyed$)).subscribe((response) => {
            const { facets } = response;
            Object.keys(facets).forEach((id) => {
                const { total_count: totalCount } = facets[id];
                this.dataSource.updateInputValue(`header-${id}`, totalCount);
            });
        });
    }
}

const SearchFacetsLayoutConfig = {
    layoutId: 'mr-search-facets-layout',
    widgets: [],
    layoutDS: SearchFacetsLayoutDS,
    layoutEH: SearchFacetsLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class FacetTextDS extends DataSource {
    constructor() {
        super(...arguments);
        this.getValue = () => this.value;
    }
    transform(data) {
        return {
            ...data,
            placeholder: _t(data.placeholder)
        };
    }
    setValue(value, update = false) {
        this.value = value;
        if (update) {
            this.update({
                ...this.input,
                value: value || value === 0 ? `${value}` : null
            });
            // fix element update
            const el = document.getElementById(this.output.id);
            if (el) {
                el.value = value || value === 0 ? `${value}` : null;
            }
        }
    }
    clear() {
        this.value = null;
    }
}

class FacetCheckboxDS extends DataSource {
    constructor() {
        super(...arguments);
        this.value = [];
        this.getValue = () => this.value;
    }
    transform(data) {
        return data;
    }
    setValue(value, update = false) {
        this.value = Array.isArray(value) ? value : [value];
        if (update) {
            const { checkboxes } = this.input;
            const updatedCheckboxes = checkboxes.map((checkbox) => ({
                ...checkbox,
                checked: this.value.indexOf(checkbox.payload) !== -1
            }));
            this.update({
                ...this.input,
                checkboxes: updatedCheckboxes
            });
        }
    }
    toggleValue({ inputPayload, value: isChecked }) {
        const exists = this.value.indexOf(inputPayload) !== -1;
        if (isChecked && !exists) {
            this.value.push(inputPayload);
        }
        else if (!isChecked && exists) {
            this.value.splice(this.value.indexOf(inputPayload), 1);
        }
    }
    clear() {
        this.value = [];
    }
}

class FacetSelectDS extends DataSource {
    constructor() {
        super(...arguments);
        this.getValue = () => this.value;
    }
    transform(data) {
        return data;
    }
    setValue(value, update = false) {
        this.value = value;
        if (update) {
            const { options } = this.input;
            const updatedOptions = options.map((option) => ({
                ...option,
                selected: value === option.value
            }));
            this.update({
                ...this.input,
                options: updatedOptions
            });
        }
    }
    clear() {
        this.value = null;
    }
}

const ACTIVE_CLASS$2 = 'is-active';
class FacetLinkDS extends DataSource {
    constructor() {
        super(...arguments);
        this.value = null;
        this.isUpdate = false;
        this.getValue = () => this.value;
    }
    transform(data) {
        const { links } = data;
        // empty state check
        if (this.isUpdate && !links.length) {
            return {
                links: [{
                        text: _t('global#facet_empty_text'),
                        classes: 'empty-text-link',
                        payload: null,
                    }]
            };
        }
        return data;
    }
    setValue(value, update = false) {
        this.value = value;
        this.isUpdate = update;
        if (update) {
            const { links } = this.input;
            const updatedLinks = links.map((link) => ({
                ...link,
                classes: this.value && (this.value === link.payload) ? ACTIVE_CLASS$2 : ''
            }));
            this.update({
                ...this.input,
                links: updatedLinks
            });
        }
    }
    toggleValue(linkValue) {
        // update
        this.setValue(this.value !== linkValue ? linkValue : null, true);
    }
    clear() {
        this.value = null;
    }
}

const ICON_OPEN = 'n7-icon-angle-up';
const ICON_CLOSE = 'n7-icon-angle-down';
class FacetHeaderDS extends DataSource {
    constructor() {
        super(...arguments);
        this.getValue = () => this.value;
    }
    transform(data) {
        return {
            ...data,
            text: _t(data.text),
            iconRight: data.iconRight || ICON_OPEN
        };
    }
    setValue(value, update = false) {
        this.value = value;
        if (update) {
            this.update({
                ...this.input,
                additionalText: value
            });
        }
    }
    toggle() {
        let { iconRight } = this.output;
        iconRight = iconRight === ICON_OPEN ? ICON_CLOSE : ICON_OPEN;
        this.update({
            ...this.input,
            iconRight
        });
    }
    isOpen() {
        return this.output.iconRight === ICON_OPEN;
    }
    clear() {
        this.value = null;
    }
}

class FacetHeaderEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type }) => {
            switch (type) {
                case `${this.dataSource.id}.click`:
                    this.dataSource.toggle();
                    this.emitOuter('change', {
                        isOpen: this.dataSource.isOpen(),
                        id: this.dataSource.id,
                        value: this.dataSource.value
                    });
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetTextEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`:
                    if (typeof payload.value === 'string') {
                        payload.value = payload.value.trim();
                    }
                    this.dataSource.setValue(payload.value);
                    this.emitOuter('change', {
                        ...payload,
                        id: this.dataSource.id
                    });
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetCheckboxEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`:
                    this.dataSource.toggleValue(payload);
                    this.emitOuter('change', {
                        value: this.dataSource.getValue(),
                        id: this.dataSource.id
                    });
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetSelectEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`:
                    this.dataSource.setValue(payload.value);
                    this.emitOuter('change', {
                        ...payload,
                        id: this.dataSource.id
                    });
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetLinkEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`:
                    if (payload) {
                        this.dataSource.toggleValue(payload);
                        this.emitOuter('change', {
                            value: this.dataSource.getValue(),
                            id: this.dataSource.id
                        });
                    }
                    break;
                default:
                    break;
            }
        });
    }
}

const ACTIVE_CLASS$1 = 'is-active';
class FacetLinkMultipleDS extends DataSource {
    constructor() {
        super(...arguments);
        this.value = [];
        this.isUpdate = false;
        this.getValue = () => this.value;
    }
    transform(data) {
        const { links } = data;
        // empty state check
        if (this.isUpdate && !links.length) {
            return {
                links: [{
                        text: _t('global#facet_empty_text'),
                        classes: 'empty-text-link',
                        payload: null,
                    }]
            };
        }
        return data;
    }
    setValue(value, update = false) {
        this.value = value;
        this.isUpdate = update;
        if (update) {
            const { links } = this.input;
            const updatedLinks = links.map((link) => ({
                ...link,
                classes: this.value.includes(link.payload) ? ACTIVE_CLASS$1 : ''
            }));
            this.update({
                ...this.input,
                links: updatedLinks
            });
        }
    }
    toggleValue(linkValue) {
        const exists = this.value.includes(linkValue);
        if (!exists) {
            this.value.push(linkValue);
        }
        else if (exists) {
            this.value.splice(this.value.indexOf(linkValue), 1);
        }
        // update
        this.setValue(this.value, true);
    }
    clear() {
        this.value = [];
    }
}

class FacetLinkMultipleEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.change`:
                    if (payload) {
                        this.dataSource.toggleValue(payload);
                        this.emitOuter('change', {
                            value: this.dataSource.getValue(),
                            id: this.dataSource.id
                        });
                    }
                    break;
                default:
                    break;
            }
        });
    }
}

var mapHelper = {
    /**
     * Performs validation for a leaflet marker data.
     * If the data is invalid displays an error.
     *
     * @param coords data for a leaflet marker as array with data[0]
     * @returns true if the marker data is valid
     */
    isValidMarker({ lat, lon }) {
        const test = (lat
            && lon
            && /^-?\d+\.\d*$/.test(lat)
            && /^-?\d+\.\d*$/.test(lon));
        if (test)
            return true;
        console.error(`${lat}, ${lon} is not a valid marker!`);
        return false;
    }
};

// leaflet is already present in the window,
// a double import results in errors with tooltips.
// declare const L;
const ACTIVE_CLASS = 'is-active';
class FacetMapDS extends DataSource {
    constructor() {
        super(...arguments);
        this.value = [];
        this.markerEvents$ = new Subject();
        this.isUpdate = false;
        this.iconSize = this.options.libOptions.iconSize ?? [13, 20];
        this.MARKER_ICON = L.icon({
            iconUrl: '/assets/pin.png',
            iconSize: this.iconSize,
            popupAnchor: [0, -15],
            className: 'marker-icon'
        });
        this.MARKER_ICON_UNAVAILABLE = L.icon({
            iconUrl: '/assets/pin-unavailable.png',
            iconSize: this.iconSize,
            popupAnchor: [0, -15],
            className: 'marker-icon'
        });
        this.MARKER_ICON_SELECTED = L.icon({
            iconUrl: '/assets/pin-selected.png',
            iconSize: this.iconSize,
            popupAnchor: [0, -15],
            className: 'marker-icon-selected'
        });
        this.getIcon = (id, counter) => {
            if (this.value.includes(id))
                return this.MARKER_ICON_SELECTED;
            if (counter > 0)
                return this.MARKER_ICON;
            return this.MARKER_ICON_UNAVAILABLE;
        };
        this.getZindex = (id, counter) => {
            if (this.value.includes(id))
                return 19999;
            if (counter > 0)
                return 9999;
            return null;
        };
        this.getValue = () => this.value;
    }
    transform({ links }) {
        const markers = [];
        links
            .filter((d) => d.args?.lat && d.args?.lon)
            .forEach((d) => {
            // if a link has more than one corresponding marker
            if (Array.isArray(d.args.lat)) {
                d.args.lat.forEach((element, i) => {
                    markers.push({
                        coords: [+d.args.lat[i], +d.args.lon[i]],
                        template: d.text,
                        title: d.text,
                        id: d.payload,
                        slug: d.payload,
                        counter: d.counter,
                    });
                });
            }
            else {
                // if a link has only one marker
                markers.push({
                    coords: [+d.args.lat, +d.args.lon],
                    template: d.text,
                    title: d.text,
                    id: d.payload,
                    slug: d.payload,
                    counter: d.counter,
                });
            }
        });
        const mapConfig = this.options?.libOptions;
        return {
            containerId: 'map-canvas',
            libOptions: {
                attributionControl: false,
                minZoom: 8,
                ...mapConfig
            },
            tileLayers: [{
                    // url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                    url: mapConfig.layerUrl ?? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png',
                    // url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
                    options: null
                }],
            initialView: {
                center: mapConfig.center ?? [46.06, 11.21],
                zoom: mapConfig.zoom ?? 9
            },
            _setInstance: (map) => {
                this.mapInstance = map;
                this.buildMarkers(markers);
                if (mapConfig.autoCenter) {
                    const bounds = new L
                        .LatLngBounds(markers
                        .map((d) => ({ lat: d.coords[0], lon: d.coords[1] }))
                        .filter((d) => mapHelper.isValidMarker(d)));
                    this.mapInstance.fitBounds(bounds);
                }
            },
        };
    }
    /**
     * Builds markers with a custom icon and adds them to the map.
     * @param markers an array of markers
     */
    buildMarkers(markers) {
        if (!markers)
            return;
        const mapConfig = this.options?.libOptions;
        // remove all existing markers
        if (this.markerLayer) {
            this.markerLayer.clearLayers();
            this.mapInstance.removeLayer(this.markerLayer);
        }
        const markerGroup = L.markerClusterGroup({
            maxClusterRadius: mapConfig.maxClusterRadius ?? 10,
            disableClusteringAtZoom: mapConfig.disableClusteringAtZoom ?? 8
        });
        markers.forEach(({ coords, template, id, slug, counter }) => {
            // create custom icon marker
            const newMarker = L.marker(coords, {
                icon: this.getIcon(id, counter),
                zIndexOffset: this.getZindex(id, counter)
            });
            if (id && slug) {
                newMarker.id = id;
                newMarker.counter = counter;
                newMarker.slug = slug;
            }
            newMarker
                // add the marker to the group
                .addTo(markerGroup)
                // add the on-click tooltip
                .bindPopup(template);
            newMarker.on('click', ({ target }) => {
                this.markerEvents$.next({
                    type: 'marker.click',
                    id: target.id
                });
            });
            newMarker.on('mouseover', ({ target }) => {
                target.openPopup();
            });
            newMarker.on('mouseout', ({ target }) => {
                target.closePopup();
            });
        });
        // add the markers to the map instance
        this.mapInstance.addLayer(markerGroup);
        // update the marker layer instance
        this.markerLayer = markerGroup;
    }
    setValue(value, update = false) {
        // prevent the search service from assigning a plain string
        // eslint-disable-next-line no-param-reassign
        if (typeof value === 'string')
            value = [value];
        if (this.value !== value) {
            this.value = value;
        }
        this.isUpdate = update || this.value?.length === 0;
        if (update && this.input) {
            const { links } = this.input;
            const updatedLinks = links.map((link) => ({
                ...link,
                classes: this.value.includes(link.payload) ? ACTIVE_CLASS : ''
            }));
            // update marker icons
            if (this.markerLayer) {
                this.markerLayer.eachLayer((marker) => {
                    const { id } = marker;
                    const counter = links.find(({ payload }) => payload === id)?.counter || 0;
                    marker.getPopup()._source.setIcon(this.getIcon(id, counter))
                        .setZIndexOffset(this.getZindex(id, counter));
                });
            }
            // ---
            this.update({
                ...this.input,
                links: updatedLinks
            });
        }
    }
    toggleValue(value) {
        const exists = this.value.includes(value);
        if (!exists) {
            this.value.push(value);
        }
        else if (exists) {
            this.value.splice(this.value.indexOf(value), 1);
        }
        // update
        this.setValue(this.value, true);
    }
    clear() {
        this.value = [];
    }
}

class FacetMapEH extends EventHandler {
    listen() {
        this.outerEvents$.subscribe(({ type }) => {
            switch (type) {
                case 'mr-search-facets-layout.facetloaded':
                    // Listen for incoming marker events
                    this.dataSource.markerEvents$.subscribe((event) => {
                        switch (event.type) {
                            // trigger search facet logic
                            case 'marker.click':
                                {
                                    // detect "multiple: true | false" in config > facet > schema
                                    // and apply the correct logic.
                                    const { isMultiple } = this.dataSource.options;
                                    if (isMultiple) {
                                        this.dataSource.toggleValue(event.id);
                                    }
                                    else {
                                        const currentValue = this.dataSource.value;
                                        if (currentValue[0] === event.id) {
                                            this.dataSource.toggleValue(event.id);
                                        }
                                        else {
                                            this.dataSource.setValue([event.id]);
                                        }
                                    }
                                    // (make request and update component)
                                    this.emitOuter('change', {
                                        value: this.dataSource.getValue(),
                                        id: this.dataSource.id
                                    });
                                }
                                break;
                            default:
                                break;
                        }
                    });
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetHistogramEH extends EventHandler {
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case `${this.dataSource.id}.rangeselected`:
                    if (payload) {
                        this.dataSource.setValue(payload.join('-'));
                        this.emitOuter('change', {
                            value: this.dataSource.getValue(),
                            id: this.dataSource.id
                        });
                    }
                    break;
                case `${this.dataSource.id}.loaded`:
                    this.dataSource.loadTooltips();
                    break;
                default:
                    break;
            }
        });
    }
}

class FacetHistogramDS extends DataSource {
    constructor() {
        super(...arguments);
        this.value = '';
        this.isUpdate = false;
        this.setValue = (value, update = false) => {
            this.value = value;
            this.isUpdate = update;
            if (update && this.input) {
                const { links } = this.input;
                this.update({ ...this.input, links });
                if (!this.histogramApi)
                    return;
                // format the new bars data
                const newBars = this.parseLinks(links);
                const [firstYear, lastYear] = this.value.split('-');
                // get years for slider positions
                const firstLabel = this.getFirstLabel(firstYear, newBars);
                const lastLabel = this.getLastLabel(lastYear, newBars);
                // update the histogram
                setTimeout(() => {
                    this.histogramApi.setSliders([`${firstLabel}`, `${lastLabel}`], // move the sliders
                    false // do not emit
                    );
                    this.loadTooltips();
                });
                this.histogramApi.setBars(newBars);
            }
            // reload the tooltips
            this.loadTooltips();
        };
        /**
         * Returns the current facet value
         */
        this.getValue = () => this.value;
    }
    transform({ links }) {
        const items = this.parseLinks(links); // format data
        const histogramData = {
            containerId: 'container-for-histogram',
            width: 450,
            height: 50,
            colours: {
                top: '#7091B3',
                bottom: '#96c2f2',
                accent: '#2F528B',
            },
            margin: {
                left: 30,
                right: 0,
                top: 10,
                bottom: 45
            },
            axis: {
                yAxis: {
                    show: true,
                    // tickAmount: 3
                    values: [0, 5, 20, 60]
                }
            },
            items,
            setApi: (api) => {
                if (!this.histogramApi)
                    this.histogramApi = api;
                if (this.value) {
                    const [firstYear, lastYear] = this.value.split('-');
                    const firstLabel = this.getFirstLabel(firstYear, items);
                    const lastLabel = this.getLastLabel(lastYear, items);
                    setTimeout(() => {
                        // when the component loads, set the sliders and the bars
                        // (necessary if search url has params)
                        this.histogramApi.setSliders([firstLabel, lastLabel]);
                        this.histogramApi.setBars(items);
                    });
                }
            }
        };
        return histogramData;
    }
    /**
     * Reset to the default facet value
     */
    clear() {
        this.value = '';
    }
    /**
     * Loads tippy tooltips and appends them to the histogram bars
     */
    loadTooltips() {
        const elements = document.querySelectorAll('#container-for-histogram g.bars rect.bars');
        tippy(elements, {
            content(reference) {
                const start = reference.getAttribute('data-start');
                const end = reference.getAttribute('data-end');
                return `<span class="tippy-template">${start}<br>${end}</span>`;
            },
            allowHTML: true,
            appendTo: () => document.body,
        });
    }
    /**
     * Convert the links into the histogram component format
     */
    parseLinks(links) {
        return links.map((link) => ({
            label: `${link.text}`,
            value: link.counter,
            payload: link.payload,
            range: link.range ? {
                payload: link.range.payload,
                label: link.range.text
            } : undefined,
        })).sort((a, b) => +a.label - b.label);
    }
    /**
     * Get the left-most label
     */
    getFirstLabel(year, items) {
        if (!year)
            return items[0].label;
        return items.find(({ label }) => +label === +year)?.label;
    }
    /**
     * Get the right-most label
     */
    getLastLabel(year, items) {
        if (!year)
            return items[items.length - 1].label;
        return items.find(({ label, range }) => {
            if (range) {
                return +range.label === +year;
            }
            return +label === +year;
        })?.label;
    }
}

const DATASOURCE_MAP$1 = {
    header: FacetHeaderDS,
    text: FacetTextDS,
    checkbox: FacetCheckboxDS,
    select: FacetSelectDS,
    link: FacetLinkDS,
    map: FacetMapDS,
    // if the facet value is an array you MUST include it in the name
    'map-multiple': FacetMapDS,
    'link-multiple': FacetLinkMultipleDS,
    histogram: FacetHistogramDS,
};
const EVENTHANDLER_MAP = {
    header: FacetHeaderEH,
    text: FacetTextEH,
    checkbox: FacetCheckboxEH,
    select: FacetSelectEH,
    link: FacetLinkEH,
    map: FacetMapEH,
    // if the facet value is an array you MUST include it in the name
    'map-multiple': FacetMapEH,
    'link-multiple': FacetLinkMultipleEH,
    histogram: FacetHistogramEH,
};
class MrSearchFacetsLayoutComponent extends AbstractLayout {
    constructor() {
        super(SearchFacetsLayoutConfig);
    }
    initPayload() {
        return {
            searchService: this.searchService
        };
    }
    ngOnInit() {
        this.loadWidgets();
        this.onInit();
    }
    ngOnDestroy() {
        this.onDestroy();
    }
    loadWidgets() {
        const { facets } = this.searchService.getConfig();
        this.widgets = [];
        facets.sections.forEach(({ header, inputs }) => {
            if (header) {
                this.widgets.push({
                    id: header.id,
                    dataSource: DATASOURCE_MAP$1.header,
                    eventHandler: EVENTHANDLER_MAP.header
                });
            }
            inputs.forEach((input) => {
                let inputType = input.type;
                const { multiple } = input.schema;
                // multiple control
                if (multiple) {
                    inputType += '-multiple';
                }
                this.widgets.push({
                    id: input.id,
                    options: {
                        isMultiple: !!multiple,
                        libOptions: input.libOptions ?? undefined,
                    },
                    dataSource: DATASOURCE_MAP$1[inputType],
                    eventHandler: EVENTHANDLER_MAP[inputType]
                });
            });
        });
    }
}
MrSearchFacetsLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchFacetsLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrSearchFacetsLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrSearchFacetsLayoutComponent, selector: "mr-search-facets-layout", inputs: { searchService: "searchService" }, usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"lb.dataSource.facets\" class=\"mr-facets__facets-wrapper {{ lb.dataSource.facets.classes || '' }}\">\n    <div *ngFor=\"let section of lb.dataSource.facets.sections\" \n    class=\"mr-facets__single-facet {{ section.classes || '' }}\"\n    [ngClass]=\"lb.dataSource.searchService.getState$('section', section.id) | async\">\n        <n7-facet-header\n        *ngIf=\"section.header\"\n        [data]=\"lb.widgets[section.header.id].ds.out$ | async\"\n        [emit]=\"lb.widgets[section.header.id].emit\"\n        ></n7-facet-header>\n\n        <div [hidden]=\"section.header && !lb.widgets[section.header.id].ds.isOpen()\" class=\"mr-facets__single-facet-content\">\n            <div *ngFor=\"let input of section.inputs\" \n            [attr.id]=\"'facet-container-' + input.id\"\n            class=\"mr-facets__single-facet-inner-content {{ input.classes || '' }}\">\n                <ng-container [ngSwitch]=\"input.type\">\n    \n                    <!-- INPUT TEXT -->\n                    <n7-input-text \n                    *ngSwitchCase=\"'text'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-text>\n    \n                    <!-- INPUT CHECKBOX -->\n                    <n7-input-checkbox \n                    *ngSwitchCase=\"'checkbox'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-checkbox>\n                    \n                    <!-- INPUT SELECT -->\n                    <n7-input-select \n                    *ngSwitchCase=\"'select'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-select>\n                    \n                    <!-- INPUT LINK -->\n                    <n7-input-link \n                    *ngSwitchCase=\"'link'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-link>\n\n                    <!-- INPUT LINKMULTI -->\n                    <n7-input-link \n                    *ngSwitchCase=\"'linkMulti'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-link>\n                    \n                    <!-- INPUT MAP -->\n                    <n7-map \n                    *ngSwitchCase=\"'map'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-map>\n\n                    <!-- INPUT HISTOGRAM -->\n                    <n7-histogram-range \n                    *ngSwitchCase=\"'histogram'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-histogram-range>\n                \n                </ng-container>\n            </div>\n        </div>\n    </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.FacetHeaderComponent, selector: "n7-facet-header", inputs: ["data", "emit"] }, { kind: "component", type: i7.HistogramRangeComponent, selector: "n7-histogram-range", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputCheckboxComponent, selector: "n7-input-checkbox", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputLinkComponent, selector: "n7-input-link", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputSelectComponent, selector: "n7-input-select", inputs: ["data", "emit"] }, { kind: "component", type: i7.InputTextComponent, selector: "n7-input-text", inputs: ["data", "emit"] }, { kind: "component", type: i7.MapComponent, selector: "n7-map", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchFacetsLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-search-facets-layout', template: "<div *ngIf=\"lb.dataSource.facets\" class=\"mr-facets__facets-wrapper {{ lb.dataSource.facets.classes || '' }}\">\n    <div *ngFor=\"let section of lb.dataSource.facets.sections\" \n    class=\"mr-facets__single-facet {{ section.classes || '' }}\"\n    [ngClass]=\"lb.dataSource.searchService.getState$('section', section.id) | async\">\n        <n7-facet-header\n        *ngIf=\"section.header\"\n        [data]=\"lb.widgets[section.header.id].ds.out$ | async\"\n        [emit]=\"lb.widgets[section.header.id].emit\"\n        ></n7-facet-header>\n\n        <div [hidden]=\"section.header && !lb.widgets[section.header.id].ds.isOpen()\" class=\"mr-facets__single-facet-content\">\n            <div *ngFor=\"let input of section.inputs\" \n            [attr.id]=\"'facet-container-' + input.id\"\n            class=\"mr-facets__single-facet-inner-content {{ input.classes || '' }}\">\n                <ng-container [ngSwitch]=\"input.type\">\n    \n                    <!-- INPUT TEXT -->\n                    <n7-input-text \n                    *ngSwitchCase=\"'text'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-text>\n    \n                    <!-- INPUT CHECKBOX -->\n                    <n7-input-checkbox \n                    *ngSwitchCase=\"'checkbox'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-checkbox>\n                    \n                    <!-- INPUT SELECT -->\n                    <n7-input-select \n                    *ngSwitchCase=\"'select'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-select>\n                    \n                    <!-- INPUT LINK -->\n                    <n7-input-link \n                    *ngSwitchCase=\"'link'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-link>\n\n                    <!-- INPUT LINKMULTI -->\n                    <n7-input-link \n                    *ngSwitchCase=\"'linkMulti'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-input-link>\n                    \n                    <!-- INPUT MAP -->\n                    <n7-map \n                    *ngSwitchCase=\"'map'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-map>\n\n                    <!-- INPUT HISTOGRAM -->\n                    <n7-histogram-range \n                    *ngSwitchCase=\"'histogram'\"\n                    [data]=\"lb.widgets[input.id].ds.out$ | async\"\n                    [emit]=\"lb.widgets[input.id].emit\"></n7-histogram-range>\n                \n                </ng-container>\n            </div>\n        </div>\n    </div>\n</div>\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { searchService: [{
                type: Input
            }] } });

var localStorageHelper = {
    set(key, value) {
        localStorage.setItem(key, value);
    },
    get(key) {
        return localStorage.getItem(key);
    },
    remove(key) {
        localStorage.removeItem(key);
    },
    toggle(key, value) {
        if (!this.get(key)) {
            this.set(key, value);
        }
        else {
            this.remove(key);
        }
    }
};

class MrSearchLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.totalResultsText = null;
        this.descriptionLoaded = false;
        this.showDescription = false;
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.mainState = payload.mainState;
        this.localeService = payload.localeService;
        this.searchService = payload.searchService;
        this.routerData = payload.routerData;
        this.pageConfig = this.configuration.get(this.routerData.configId);
        this.hideDescriptionKey = `hide-description-${this.routerData.configId}`;
        // config
        this.all().updateOptions({
            config: this.pageConfig,
            localeService: this.localeService
        });
        // manual updates
        this.one('mr-search-page-title').update({});
        // update head title
        this.updateHeadTitle();
        // update translations
        this.addTranslations(this.pageConfig);
        // description
        this.getPageDescription();
    }
    handleResponse(response) {
        this.some([
            'mr-search-results-title',
            'mr-search-results',
        ]).update(response);
        // pagination
        this.one('n7-smart-pagination').updateOptions({ mode: 'payload' });
        this.one('n7-smart-pagination').update(this.getPaginationParams(response));
    }
    updateActiveFilters(state, linksResponse) {
        // active "tags" filters
        this.one('mr-search-tags').update({
            state,
            linksResponse,
            facetsConfig: this.searchService.getConfig().facets
        });
    }
    toggleDescription() {
        localStorageHelper.toggle(this.hideDescriptionKey, true);
        this.showDescription = !(localStorageHelper.get(this.hideDescriptionKey));
        if (this.showDescription && !this.descriptionLoaded) {
            this.getPageDescription();
        }
    }
    getPaginationParams(response) {
        const { total_count: totalCount, offset, limit } = response;
        const { pagination: paginationConfig } = this.pageConfig;
        return {
            totalPages: Math.ceil(totalCount / limit),
            currentPage: (offset + limit) / limit,
            pageLimit: paginationConfig.limit,
            sizes: {
                label: paginationConfig.selectLabel ? _t(paginationConfig.selectLabel) : null,
                list: paginationConfig.options,
                active: limit,
            },
        };
    }
    updateHeadTitle() {
        const appName = this.configuration.get('name');
        const pageTitle = this.pageConfig.title;
        this.mainState.update('headTitle', [appName, _t(pageTitle)].join(' > '));
    }
    addTranslations(config) {
        if (config.facetsTitle) {
            config.facetsTitle = _t(config.facetsTitle);
        }
        if (config.filtersTitle) {
            config.filtersTitle = _t(config.filtersTitle);
        }
        if (config?.sort?.label) {
            config.sort.label = _t(config.sort.label);
            config.sort.options = config.sort.options.map((option) => ({
                ...option,
                label: _t(option.label)
            }));
        }
        ['text', 'button'].forEach((key) => {
            if (config.fallback) {
                config.fallback[key] = _t(config.fallback[key]);
            }
            if (config.ko) {
                config.ko[key] = _t(config.ko[key]);
            }
        });
    }
    getPageDescription() {
        if (this.pageConfig.description && !localStorageHelper.get(this.hideDescriptionKey)) {
            const { description } = this.pageConfig;
            const { locale } = this.routerData;
            this.communication.request$('searchDescription', {
                urlParams: locale ? `${description.id}?locale=${locale}` : description.id,
            }).subscribe((response) => {
                this.one('mr-search-page-description').update(response);
                this.descriptionLoaded = true;
                this.showDescription = true;
            });
        }
    }
}

class MrSearchLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroyed$ = new Subject();
        this.searchState = {};
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-search-layout.init':
                    this.searchService = payload.searchService;
                    this.layoutState = payload.layoutState;
                    this.modalService = payload.modalService;
                    this.dataSource.onInit(payload);
                    // listeners
                    this.initStateListener();
                    // scroll top
                    window.scrollTo(0, 0);
                    // reset scroll ref
                    this.scrollRefElement = null;
                    break;
                case 'mr-search-layout.destroy':
                    this.searchService.destroy();
                    this.destroyed$.next(true);
                    break;
                case 'mr-search-layout.searchreset':
                    this.searchService.reset();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'n7-smart-pagination.click':
                    this.searchService.setState('input', 'page', payload.page);
                    break;
                case 'n7-smart-pagination.change':
                    this.searchService.setState('input', 'limit', payload.value);
                    break;
                case 'mr-search-results-title.change':
                    this.searchService.setState('input', 'sort', payload.value);
                    break;
                case 'mr-search-page-description.click':
                case 'mr-search-page-title.click':
                    this.dataSource.toggleDescription();
                    break;
                case 'mr-search-tags.click': {
                    const stateValue = this.searchState[payload.id];
                    let newValue = null;
                    if (Array.isArray(stateValue)) {
                        newValue = stateValue.filter((value) => value !== payload.value);
                    }
                    this.searchService.setState('input', payload.id, newValue);
                    break;
                }
                case 'mr-search-results.openresourcemodal': {
                    const { id, type: resourceType } = payload;
                    this.modalService.open(id, resourceType);
                    break;
                }
                default:
                    break;
            }
        });
    }
    initStateListener() {
        // default params
        const { pageConfig } = this.dataSource;
        const defaultLimit = pageConfig.pagination.options[0];
        const defaultSort = pageConfig.sort.options.find((option) => option.selected === true)?.value;
        // inputs listener
        this.searchService.getState$(INPUT_STATE_CONTEXT).pipe(filter(({ lastUpdated }) => this.searchService.isQueryParamKey(lastUpdated)), takeUntil(this.destroyed$)).subscribe(({ lastUpdated, state }) => {
            this.searchState = state;
            if (lastUpdated !== 'page') {
                this.searchService.setState(INPUT_STATE_CONTEXT, 'page', 1);
            }
        });
        this.searchService.getState$(INPUT_STATE_CONTEXT, 'query').pipe(takeUntil(this.destroyed$)).subscribe((val) => {
            this.emitOuter('inputquerychange', val);
        });
        this.searchService.getState$(FACETS_REQUEST_STATE_CONTEXT, 'success').pipe(takeUntil(this.destroyed$)).subscribe((response) => {
            this.linksResponse = response;
            this.dataSource.updateActiveFilters(this.searchState, this.linksResponse);
            // update sections
            if (response) {
                const { facets } = response;
                Object.keys(facets).forEach((inputKey) => {
                    const { total_count: totalCount } = facets[inputKey];
                    this.searchService.setState(SECTION_STATE_CONTEXT, `section-${inputKey}`, totalCount ? 'is-not-empty' : 'is-empty');
                });
            }
        });
        this.searchService.getState$(RESULTS_REQUEST_STATE_CONTEXT, 'loading').pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.layoutState.set('results', LayoutState.LOADING);
        });
        // results params hook
        this.searchService.setBeforeHook(RESULTS_REQUEST_STATE_CONTEXT, 'loading', (params = {}) => {
            const results = {
                limit: defaultLimit,
                offset: 0
            };
            const sortParam = params.sort || defaultSort;
            // sort check
            if (sortParam) {
                results.sort = sortParam;
            }
            // limit check
            if (params.limit) {
                results.limit = params.limit;
            }
            // offset check
            if (params.page && params.page > 1) {
                results.offset = results.limit * (params.page - 1);
            }
            params.results = results;
            // cleanup
            Object.keys(params)
                .filter((key) => ['sort', 'page', 'limit'].includes(key))
                .forEach((key) => {
                delete params[key];
            });
            return params;
        });
        // facets params hook
        this.searchService.setBeforeHook(FACETS_REQUEST_STATE_CONTEXT, 'loading', (params = {}) => {
            // clean up
            delete params.results;
            return params;
        });
        this.searchService.getState$(RESULTS_REQUEST_STATE_CONTEXT, 'success')
            .subscribe((response) => {
            this.dataSource.handleResponse(response);
            // update layout state
            this.layoutState.set('results', isEmpty(response.results) ? LayoutState.EMPTY : LayoutState.SUCCESS);
            // scroll to ref element
            if (!pageConfig.disableScroll) {
                if (!this.scrollRefElement) {
                    this.scrollRefElement = document.querySelector('.scroll-ref');
                }
                else if (!helpers.isElementInViewport(this.scrollRefElement)) {
                    this.scrollRefElement.scrollIntoView();
                }
            }
        });
        this.searchService.getState$(RESULTS_REQUEST_STATE_CONTEXT, 'error')
            .subscribe((error) => {
            console.warn(RESULTS_REQUEST_STATE_CONTEXT, error);
            this.layoutState.set('results', LayoutState.ERROR);
        });
    }
}

const MrSearchLayoutConfig = {
    layoutId: 'mr-search-layout',
    widgets: [{
            id: 'mr-search-page-title'
        }, {
            id: 'mr-search-page-description'
        }, {
            id: 'mr-search-results-title'
        }, {
            id: 'mr-search-results'
        }, {
            id: 'mr-search-tags'
        }, {
            id: 'mr-resources', dataSource: MrItemPreviewsDS,
        }, {
            id: 'n7-smart-pagination',
            dataSource: SmartPaginationDS,
            eventHandler: SmartPaginationEH,
        }],
    layoutDS: MrSearchLayoutDS,
    layoutEH: MrSearchLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class MrSearchPageDescriptionComponent {
    onClick(payload) {
        if (this.emit) {
            this.emit('click', payload);
        }
    }
}
MrSearchPageDescriptionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchPageDescriptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MrSearchPageDescriptionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrSearchPageDescriptionComponent, selector: "mr-search-page-description", inputs: { data: "data", emit: "emit" }, ngImport: i0, template: "<div *ngIf=\"data\" class=\"mr-search-page-description\">\n    <div class=\"mr-search-page-description__text\" [innerHTML]=\"data.text\"></div>\n    <a class=\"mr-search-page-description__link\" (click)=\"onClick(data.link.payload)\">{{ data.link.text }}</a>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchPageDescriptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-search-page-description', template: "<div *ngIf=\"data\" class=\"mr-search-page-description\">\n    <div class=\"mr-search-page-description__text\" [innerHTML]=\"data.text\"></div>\n    <a class=\"mr-search-page-description__link\" (click)=\"onClick(data.link.payload)\">{{ data.link.text }}</a>\n</div>" }]
        }], propDecorators: { data: [{
                type: Input
            }], emit: [{
                type: Input
            }] } });

class MrSearchLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, router, activatedRoute, communication, configuration, searchService, layoutState, mainState, modalService, localeService) {
        super(layoutsConfiguration.get('MrSearchLayoutConfig') || MrSearchLayoutConfig);
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.communication = communication;
        this.configuration = configuration;
        this.searchService = searchService;
        this.layoutState = layoutState;
        this.mainState = mainState;
        this.modalService = modalService;
        this.localeService = localeService;
    }
    initPayload() {
        return {
            routerData: this.routerData,
            configuration: this.configuration,
            mainState: this.mainState,
            router: this.router,
            activatedRoute: this.activatedRoute,
            communication: this.communication,
            searchService: this.searchService,
            layoutState: this.layoutState,
            modalService: this.modalService,
            localeService: this.localeService,
            options: this.config.options || {},
        };
    }
    ngOnInit() {
        this.activatedRoute.data.subscribe((routerData) => {
            this.routerData = routerData;
            const { configId, locale } = this.routerData;
            const { searchId, searchConfig } = this.configuration.get(configId);
            this.searchService.init(searchId, searchConfig, locale);
            // add layout states
            this.layoutState.add(['results']);
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrSearchLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.Router }, { token: i2.ActivatedRoute }, { token: i1.CommunicationService }, { token: i1.ConfigurationService }, { token: MrSearchService }, { token: MrLayoutStateService }, { token: i1.MainStateService }, { token: MrResourceModalService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrSearchLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrSearchLayoutComponent, selector: "mr-search-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-search mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-search__title\">\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n\n        <div *ngIf=\"lb.dataSource.showDescription\" class=\"mr-search__description\">\n            <mr-search-page-description\n            [data]=\"lb.widgets['mr-search-page-description'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-description'].emit\">\n            </mr-search-page-description>\n        </div>\n        \n        <div class=\"mr-search__results-content\">\n            <aside class=\"mr-facets\"\n                   [ngStyle]=\"{\n                    width: lb.dataSource.pageConfig['facetsWidthPercentage'] ? lb.dataSource.pageConfig['facetsWidthPercentage'] + '%' : ''\n                    }\">\n                <div class=\"scroll-ref\">&nbsp;</div>\n                <div class=\"mr-facets__contents\">\n                    <h2 class=\"mr-facets__title\" \n                        *ngIf=\"lb.dataSource.pageConfig['facetsTitle']\">\n                        {{ lb.dataSource.pageConfig['facetsTitle'] }}\n                    </h2>\n                    <mr-search-facets-layout \n                    [searchService]=\"lb.dataSource.searchService\">\n                    </mr-search-facets-layout>\n                </div>\n            </aside>\n            <div class=\"mr-search__results-wrapper\">\n                <div class=\"mr-search__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                \n                <div *ngIf=\"(\n                    lb.dataSource.pageConfig['filtersTitle'] && \n                    lb.widgets['mr-search-tags'].ds.hasFilters\n                )\" \n                class=\"mr-active-filters\">\n                    <span class=\"mr-active-filters__label\">{{ lb.dataSource.pageConfig['filtersTitle'] }}</span>\n                    <div class=\"mr-active-filters__tags-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-search-tags'].ds.out$ | async)\"\n                        [data]=\"tag\"\n                        [emit]=\"lb.widgets['mr-search-tags'].emit\">\n                        </n7-tag>\n                    </div>\n                </div>\n\n                <main class=\"mr-search__results\">\n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-search__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <ng-container *ngIf=\"!lb.dataSource.pageConfig?.advancedResults\">\n                                    <n7-item-preview *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                    [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                    </n7-item-preview>\n                                </ng-container>\n                                <ng-container *ngIf=\"lb.dataSource.pageConfig?.advancedResults\">\n                                    <mr-advanced-result\n                                        *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                        [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                    </mr-advanced-result>\n                                </ng-container>\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div class=\"mr-search__results-fallback\">\n                                <p class=\"mr-search__results-fallback-string\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <button class=\"n7-btn mr-search__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.fallback.button }}\n                                </button>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p class=\"mr-search__results-ko-string\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <button class=\"n7-btn mr-search__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                {{ lb.dataSource.pageConfig.ko.button }}\n                            </button>\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                <n7-smart-pagination\n                *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n            </div>\n        </div>\n\n    </section>\n</div>", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.ContentPlaceholderComponent, selector: "n7-content-placeholder", inputs: ["data"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.TagComponent, selector: "n7-tag", inputs: ["data", "emit"] }, { kind: "component", type: i1.SmartPaginationComponent, selector: "n7-smart-pagination", inputs: ["data", "emit"] }, { kind: "component", type: MrSearchFacetsLayoutComponent, selector: "mr-search-facets-layout", inputs: ["searchService"] }, { kind: "component", type: MrSearchPageDescriptionComponent, selector: "mr-search-page-description", inputs: ["data", "emit"] }, { kind: "component", type: MrAdvancedResultComponent, selector: "mr-advanced-result", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrSearchLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-search-layout', template: "<div class=\"mr-search mr-layout\"\n     *ngIf=\"lb.dataSource\">\n    <section class=\"mr-layout__maxwidth mr-side-margin\">\n\n        <div class=\"mr-search__title\">\n            <n7-inner-title\n            [data]=\"lb.widgets['mr-search-page-title'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-title'].emit\">\n            </n7-inner-title>\n        </div>\n\n        <div *ngIf=\"lb.dataSource.showDescription\" class=\"mr-search__description\">\n            <mr-search-page-description\n            [data]=\"lb.widgets['mr-search-page-description'].ds.out$ | async\"\n            [emit]=\"lb.widgets['mr-search-page-description'].emit\">\n            </mr-search-page-description>\n        </div>\n        \n        <div class=\"mr-search__results-content\">\n            <aside class=\"mr-facets\"\n                   [ngStyle]=\"{\n                    width: lb.dataSource.pageConfig['facetsWidthPercentage'] ? lb.dataSource.pageConfig['facetsWidthPercentage'] + '%' : ''\n                    }\">\n                <div class=\"scroll-ref\">&nbsp;</div>\n                <div class=\"mr-facets__contents\">\n                    <h2 class=\"mr-facets__title\" \n                        *ngIf=\"lb.dataSource.pageConfig['facetsTitle']\">\n                        {{ lb.dataSource.pageConfig['facetsTitle'] }}\n                    </h2>\n                    <mr-search-facets-layout \n                    [searchService]=\"lb.dataSource.searchService\">\n                    </mr-search-facets-layout>\n                </div>\n            </aside>\n            <div class=\"mr-search__results-wrapper\">\n                <div class=\"mr-search__results-info\">\n                    <n7-inner-title\n                    [data]=\"lb.widgets['mr-search-results-title'].ds.out$ | async\"\n                    [emit]=\"lb.widgets['mr-search-results-title'].emit\">\n                    </n7-inner-title>\n                </div>\n                \n                <div *ngIf=\"(\n                    lb.dataSource.pageConfig['filtersTitle'] && \n                    lb.widgets['mr-search-tags'].ds.hasFilters\n                )\" \n                class=\"mr-active-filters\">\n                    <span class=\"mr-active-filters__label\">{{ lb.dataSource.pageConfig['filtersTitle'] }}</span>\n                    <div class=\"mr-active-filters__tags-wrapper\">\n                        <n7-tag *ngFor=\"let tag of (lb.widgets['mr-search-tags'].ds.out$ | async)\"\n                        [data]=\"tag\"\n                        [emit]=\"lb.widgets['mr-search-tags'].emit\">\n                        </n7-tag>\n                    </div>\n                </div>\n\n                <main class=\"mr-search__results\">\n                    <!-- SEARCH RESULTS -->\n                    <ng-container [ngSwitch]=\"layoutState.get$('results') | async\">\n                        \n                        <!-- loading -->\n                        <ng-container *ngSwitchCase=\"'LOADING'\">\n                            <div class=\"mr-search__results-loading n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <n7-content-placeholder *ngFor=\"let n of [0,1,2,3,4,5,6,7,8,9]\" [data]=\"{\n                                    blocks: [\n                                        { classes: 'search-result-placeholder-title' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' },\n                                        { classes: 'search-result-placeholder-metadata' }\n                                    ]\n                                }\"></n7-content-placeholder>\n                            </div>\n                        </ng-container>\n                        \n                        <!-- success: items > 0 -->\n                        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                            <div class=\"n7-grid-{{ lb.dataSource.pageConfig.grid || 3 }}\">\n                                <ng-container *ngIf=\"!lb.dataSource.pageConfig?.advancedResults\">\n                                    <n7-item-preview *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                    [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                    </n7-item-preview>\n                                </ng-container>\n                                <ng-container *ngIf=\"lb.dataSource.pageConfig?.advancedResults\">\n                                    <mr-advanced-result\n                                        *ngFor=\"let item of (lb.widgets['mr-search-results'].ds.out$ | async)\"\n                                        [data]=\"item\" [emit]=\"lb.widgets['mr-search-results'].emit\">\n                                    </mr-advanced-result>\n                                </ng-container>\n                            </div>\n                        </ng-container>\n\n                        <!-- empty: items === 0 -->\n                        <ng-container *ngSwitchCase=\"'EMPTY'\">\n                            <div class=\"mr-search__results-fallback\">\n                                <p class=\"mr-search__results-fallback-string\">\n                                    {{ lb.dataSource.pageConfig.fallback.text }}\n                                </p>\n                                <button class=\"n7-btn mr-search__results-fallback-button\"\n                                    (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                    {{ lb.dataSource.pageConfig.fallback.button }}\n                                </button>\n                            </div>\n                        </ng-container>\n\n                        <!-- error: request problem -->\n                        <ng-container *ngSwitchCase=\"'ERROR'\">\n                            <p class=\"mr-search__results-ko-string\">\n                                {{ lb.dataSource.pageConfig.ko.text }}\n                            </p>\n                            <button class=\"n7-btn mr-search__results-ko-button\"\n                                (click)=\"lb.eventHandler.emitInner('searchreset')\">\n                                {{ lb.dataSource.pageConfig.ko.button }}\n                            </button>\n                        </ng-container>\n                        \n                    </ng-container>\n                </main>               \n                <n7-smart-pagination\n                *ngIf=\"(layoutState.get$('results') | async) === 'SUCCESS'\"\n                [data]=\"lb.widgets['n7-smart-pagination'].ds.out$ | async\"\n                [emit]=\"lb.widgets['n7-smart-pagination'].emit\">\n                </n7-smart-pagination>\n            </div>\n        </div>\n\n    </section>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.Router }, { type: i2.ActivatedRoute }, { type: i1.CommunicationService }, { type: i1.ConfigurationService }, { type: MrSearchService }, { type: MrLayoutStateService }, { type: i1.MainStateService }, { type: MrResourceModalService }, { type: MrLocaleService }]; } });

class MrStaticLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.errorTitle = _t('global#layout_error_title');
        this.errorDescription = _t('global#layout_error_description');
    }
    onInit(payload) {
        this.communication = payload.communication;
        this.configuration = payload.configuration;
        this.mainState = payload.mainState;
        this.routerData = payload.routerData;
        this.pageConfig = this.configuration.get(this.routerData.configId);
    }
    pageRequest$({ urlSegments, routerParams, onError }) {
        const { slug } = routerParams;
        const { apiPoint } = this.pageConfig || {};
        // no locale slug setup
        if (!slug) {
            if (urlSegments.length > 1) {
                return this.communication.request$(apiPoint || 'post', {
                    onError,
                    urlParams: urlSegments[1].path,
                });
            }
            return this.communication.request$(apiPoint || 'static', {
                onError,
                urlParams: urlSegments[0].path,
            });
        }
        return this.communication.request$(apiPoint || 'static', {
            onError,
            urlParams: slug,
        });
    }
    handleResponse(response) {
        this.setHtml(response);
        this.updateHeadTitle(response.title);
    }
    setHtml(response) {
        const { content, title } = response;
        this.title = title;
        this.content = content;
        this.one('mr-static-metadata').update(response);
    }
    updateHeadTitle(pageTitle) {
        const appName = this.configuration.get('name');
        this.mainState.update('headTitle', [appName, pageTitle].join(' > '));
    }
}

class MrStaticLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.destroy$ = new Subject();
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-static-layout.init':
                    this.route = payload.route;
                    this.router = payload.router;
                    this.layoutState = payload.layoutState;
                    this.dataSource.onInit(payload);
                    // listen route
                    this.listenRoute();
                    // scroll top
                    window.scrollTo(0, 0);
                    break;
                case 'mr-static-layout.destroy':
                    this.destroy$.next();
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
    }
    listenRoute() {
        this.route.url.pipe(takeUntil(this.destroy$), map((urlSegments) => ({
            urlSegments,
            routerParams: this.route.snapshot.params,
            routerData: this.route.snapshot.data,
        })), tap(() => {
            this.layoutState.set('content', LayoutState.LOADING);
        }), switchMap(({ urlSegments, routerParams, routerData }) => this.dataSource.pageRequest$({
            urlSegments,
            routerParams,
            onError: (err) => {
                if (err.status === 404) {
                    // getting not found path
                    const { config } = this.router;
                    let route404 = config.find(({ data }) => data?.id === 'page-404' && data.locale === routerData.locale);
                    if (!route404) {
                        route404 = config.find(({ data }) => data?.id === 'page-404');
                    }
                    const path404 = route404?.path || 'page-404';
                    this.router.navigate([path404]);
                }
                else {
                    console.warn(`Error loading static layout for ${urlSegments}`, err.message);
                    this.layoutState.set('content', LayoutState.ERROR);
                }
            }
        }))).subscribe((response) => {
            this.layoutState.set('content', LayoutState.SUCCESS);
            this.dataSource.handleResponse(response);
        });
    }
}

const MrStaticLayoutConfig = {
    layoutId: 'mr-static-layout',
    widgets: [{
            id: 'mr-static-metadata'
        }],
    layoutDS: MrStaticLayoutDS,
    layoutEH: MrStaticLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    layoutOptions: {}
};

class MrStaticLayoutComponent extends AbstractLayout {
    constructor(communication, configuration, mainState, route, router, layoutState, layoutsConfiguration) {
        super(layoutsConfiguration.get('MrStaticLayoutConfig') || MrStaticLayoutConfig);
        this.communication = communication;
        this.configuration = configuration;
        this.mainState = mainState;
        this.route = route;
        this.router = router;
        this.layoutState = layoutState;
    }
    initPayload() {
        return {
            routerData: this.routerData,
            communication: this.communication,
            configuration: this.configuration,
            mainState: this.mainState,
            layoutState: this.layoutState,
            route: this.route,
            router: this.router,
            options: this.config.options || {}
        };
    }
    ngOnInit() {
        this.route.data.subscribe((routerData) => {
            this.routerData = routerData;
            // add layout states
            this.layoutState.add('content');
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrStaticLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrStaticLayoutComponent, deps: [{ token: i1.CommunicationService }, { token: i1.ConfigurationService }, { token: i1.MainStateService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: MrLayoutStateService }, { token: i1.LayoutsConfigurationService }], target: i0.ɵɵFactoryTarget.Component });
MrStaticLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrStaticLayoutComponent, selector: "mr-static-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-static mr-layout\"\n     *ngIf=\"lb.dataSource\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- STATIC LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <div class=\"mr-static__top\">\n                <h1 class=\"mr-static__title mr-generated-title-WP\">{{lb.dataSource.title}}</h1>\n                <div class=\"mr-static__metadata\">\n                    <n7-metadata-viewer \n                    [data]=\"lb.widgets['mr-static-metadata'].ds.out$ | async\">\n                    </n7-metadata-viewer>\n                </div>\n            </div>\n            \n            <div class=\"mr-static__content mr-wp-content\" [innerHTML]=\"lb.dataSource.content | keepHtml\"></div>\n        </ng-container>\n    \n    </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "component", type: i7.MetadataViewerComponent, selector: "n7-metadata-viewer", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }, { kind: "pipe", type: EscapeHtmlPipe, name: "keepHtml" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrStaticLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-static-layout', template: "<div class=\"mr-static mr-layout\"\n     *ngIf=\"lb.dataSource\"\n     [ngClass]=\"{\n        'is-loading': ( layoutState.get$('content') | async ) == 'LOADING',\n        'is-error': ( layoutState.get$('content') | async ) == 'ERROR'\n      }\">\n    <!-- STATIC LAYOUT CONTENT -->\n    <ng-container [ngSwitch]=\"layoutState.get$('content') | async\">\n        <!-- loading -->\n        <ng-container *ngSwitchCase=\"'LOADING'\">\n            <div class=\"mr-layout__loader\">\n                <n7-loader></n7-loader>\n            </div>\n        </ng-container>\n\n        <!-- error -->\n        <ng-container *ngSwitchCase=\"'ERROR'\">\n            <div class=\"mr-layout__error\">\n                <h2>{{ lb.dataSource.errorTitle }}</h2>\n                <p>{{ lb.dataSource.errorDescription }}</p>\n            </div>\n        </ng-container>\n\n        <!-- success -->\n        <ng-container *ngSwitchCase=\"'SUCCESS'\">\n            <div class=\"mr-static__top\">\n                <h1 class=\"mr-static__title mr-generated-title-WP\">{{lb.dataSource.title}}</h1>\n                <div class=\"mr-static__metadata\">\n                    <n7-metadata-viewer \n                    [data]=\"lb.widgets['mr-static-metadata'].ds.out$ | async\">\n                    </n7-metadata-viewer>\n                </div>\n            </div>\n            \n            <div class=\"mr-static__content mr-wp-content\" [innerHTML]=\"lb.dataSource.content | keepHtml\"></div>\n        </ng-container>\n    \n    </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.CommunicationService }, { type: i1.ConfigurationService }, { type: i1.MainStateService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: MrLayoutStateService }, { type: i1.LayoutsConfigurationService }]; } });

// demo page: http://localhost:4200/timeline/2992/missione-venezia
class MrTimelineLayoutDS extends LayoutDataSource {
    constructor() {
        super(...arguments);
        this.loading = {
            resourceDetails: true,
            timeline: true,
        };
        this.defaultDescription = '';
        this.eventDescription = '';
        this.hasMap = false;
        this.timelineListener$ = new Subject();
    }
    onInit(payload) {
        this.configuration = payload.configuration;
        this.communication = payload.communication;
        this.route = payload.route;
        this.router = payload.router;
        this.localeService = payload.localeService;
        this.location = payload.location;
        this.configId = payload.configId;
        this.pageConfig = this.configuration.get(this.configId) || {};
        const locale = this.localeService.getLocale();
        // update the timeline
        this.loading.timeline = true;
        this.communication
            .request$('timeline', {
            method: 'GET',
            urlParams: locale ? `?locale=${locale}` : '',
            onError: (e) => console.error(e),
        })
            .subscribe((d) => {
            this.timelineData = d;
            this.loading.timeline = false;
            this.one('mr-timeline').updateOptions({
                libOptions: this.pageConfig.libOptions,
            });
            this.one('mr-timeline').update(d);
            this.loading.timeline = false;
        });
        this.getWidgetDataSource('mr-timeline')
            .timelineLoaded$.pipe(first())
            .subscribe((timeline) => {
            this.timelineListener$.next(timeline);
        });
        // update the description
        this.communication
            .request$('timelineDescription', {
            method: 'GET',
            urlParams: locale ? `?locale=${locale}` : '',
            onError: (e) => console.error(e),
        })
            .subscribe((d) => {
            this.defaultDescription = d.text;
            this.loadDefaults(false);
        });
        // set map header
        this.mapHeader = _t(this.pageConfig.mapHeader);
    }
    loadDefaults(navigate) {
        const timelineInstance = this.getWidgetDataSource('mr-timeline')
            .timeline;
        if (timelineInstance) {
            timelineInstance.setSelection([]);
        }
        this.eventDescription = this.defaultDescription;
        this.eventHeader = '';
        this.hasMap = false;
        this.bibliographyData = undefined;
        this.collectionWitnessData = undefined;
        this.collectionWorksData = undefined;
        this.collectionBooksData = undefined;
        this.collectionGalleryData = undefined;
        this.collectionData = [];
        if (navigate) {
            const href = this.localeService.getLinkByRouteId('timeline');
            this.location.go(href);
        }
        this.one('mr-year-header').update({
            title: { main: { text: _t(this.pageConfig.title) } },
        });
    }
    updatePageDetails(id) {
        const locale = this.localeService.getLocale();
        this.communication
            .request$('resource', {
            onError: (e) => console.error(e),
            method: 'POST',
            urlParams: locale ? `?locale=${locale}` : '',
            params: {
                id,
                type: 'views/time-events',
            },
        })
            .subscribe((res) => {
            // any
            if (!res || res == null)
                return;
            const { 
            /* eslint-disable */
            "collection-bibliography": bibData, "collection-places": placesData, gallery, header,
            /* eslint-enable */
             } = res.sections;
            if (res.sections['collection-bibliography']) {
                delete res.sections['collection-bibliography'];
            }
            if (res.sections['collection-places']) {
                delete res.sections['collection-places'];
            }
            const collections = [];
            Object.keys(res.sections).forEach((collection) => {
                if (String(collection).startsWith('collection-')) {
                    if (res.sections[collection].items) {
                        collections.push({
                            items: res.sections[collection].items.map((item) => {
                                let anchor;
                                if (item.routeId) {
                                    const routeLink = this.localeService
                                        .getLinkByRouteId(item.routeId, item.id, item.slug);
                                    anchor = {
                                        href: routeLink,
                                        queryParams: item.params || null,
                                    };
                                }
                                else if (item.link) {
                                    anchor = {
                                        href: linksHelper.getRouterLink(item.link),
                                        queryParams: linksHelper.getQueryParams(item.link),
                                    };
                                }
                                return {
                                    title: item.title,
                                    anchor
                                };
                            }),
                            header: res.sections[collection].header,
                        });
                    }
                    this.collectionData = collections;
                }
            });
            if (gallery) {
                this.collectionGalleryData = gallery;
            }
            else {
                this.collectionGalleryData = undefined;
            }
            if (header) {
                this.eventDescription = header.content;
                this.eventHeader = res.title;
                this.one('mr-year-header').update({
                    title: { main: { text: header.title } },
                    actions: {
                        buttons: [
                            {
                                text: '',
                                icon: 'n7-icon-close',
                                anchor: {
                                    payload: 'closebutton',
                                },
                            },
                        ],
                    },
                });
            }
            if (placesData) {
                this.hasMap = true;
                this.one('mr-map').update(placesData);
            }
            else {
                this.hasMap = false;
            }
            if (bibData) {
                this.bibliographyData = {
                    header: bibData.header,
                    items: bibData.items.map((item) => ({
                        ...item,
                        anchor: {
                            payload: item.payload,
                        },
                        classes: 'mr-item-preview-bibliography',
                    })),
                };
            }
            else {
                this.bibliographyData = undefined;
            }
            this.loading.resourceDetails = false;
        });
    }
}

class MrTimelineLayoutEH extends EventHandler {
    constructor() {
        super(...arguments);
        this.itemPreviewEmit = (type, payload) => {
            if (type === 'click' && payload?.action === 'resource-modal') {
                const { id, type: resourceType } = payload;
                this.modalService.open(id, resourceType);
            }
        };
    }
    listen() {
        this.innerEvents$.subscribe(({ type, payload }) => {
            switch (type) {
                case 'mr-timeline-layout.init':
                    this.dataSource.onInit(payload);
                    this.modalService = payload.modalService;
                    this.route = payload.route;
                    this.router = payload.router;
                    this.localeService = payload.localeService;
                    this.location = payload.location;
                    this.listenRoute();
                    // scroll top
                    window.scrollTo(0, 0);
                    this.dataSource.timelineListener$.subscribe((timeline) => {
                        timeline.on('click', (props) => {
                            if (!props.item)
                                return;
                            // build URL slug
                            const { content } = this.dataSource.timelineData.dataSet
                                .find((d) => d.id === props.item);
                            const slug = helpers.slugify(content);
                            // navigate without reloading the layout
                            const href = this.localeService.getLinkByRouteId('timelineItem', props.item, slug);
                            this.location.go(href);
                            this.dataSource.updatePageDetails(props.item);
                        });
                    });
                    break;
                case 'mr-timeline-layout.destroy':
                    break;
                default:
                    console.warn('unhandled inner event of type', type);
                    break;
            }
        });
        this.outerEvents$.subscribe(({ type }) => {
            switch (type) {
                case 'mr-year-header.closeevent':
                    this.dataSource.loadDefaults(true);
                    break;
                default:
                    break;
            }
        });
    }
    listenRoute() {
        this.route.paramMap.subscribe((params) => {
            const paramId = params.get('id');
            if (paramId) {
                this.dataSource.currentId = paramId;
                this.emitOuter('routechanged', paramId);
                this.dataSource.updatePageDetails(paramId);
            }
            else {
                this.dataSource.loadDefaults(true);
            }
        });
    }
}

const MrTimelineLayoutConfig = {
    layoutId: 'mr-timeline-layout',
    widgets: [
        { id: 'mr-timeline' },
        { id: 'mr-map' },
        { id: 'mr-year-header' }
    ],
    layoutDS: MrTimelineLayoutDS,
    layoutEH: MrTimelineLayoutEH,
    widgetsDataSources: DS,
    widgetsEventHandlers: EH,
    options: {
    // TODO
    },
};

class MrTimelineLayoutComponent extends AbstractLayout {
    constructor(layoutsConfiguration, route, router, configuration, communication, mainState, layoutState, modalService, localeService, location) {
        super(layoutsConfiguration.get('MrTimelineLayoutConfig') || MrTimelineLayoutConfig);
        this.route = route;
        this.router = router;
        this.configuration = configuration;
        this.communication = communication;
        this.mainState = mainState;
        this.layoutState = layoutState;
        this.modalService = modalService;
        this.localeService = localeService;
        this.location = location;
    }
    initPayload() {
        return {
            configId: this.configId,
            mainState: this.mainState,
            configuration: this.configuration,
            communication: this.communication,
            layoutState: this.layoutState,
            modalService: this.modalService,
            route: this.route,
            router: this.router,
            localeService: this.localeService,
            location: this.location,
            options: this.config.options || {}
        };
    }
    ngOnInit() {
        this.route.data.subscribe((data) => {
            this.configId = data.configId;
            this.layoutState.add('content');
            this.onInit();
        });
    }
    ngOnDestroy() {
        this.onDestroy();
    }
}
MrTimelineLayoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrTimelineLayoutComponent, deps: [{ token: i1.LayoutsConfigurationService }, { token: i2.ActivatedRoute }, { token: i2.Router }, { token: i1.ConfigurationService }, { token: i1.CommunicationService }, { token: i1.MainStateService }, { token: MrLayoutStateService }, { token: MrResourceModalService }, { token: MrLocaleService }, { token: i1$2.Location }], target: i0.ɵɵFactoryTarget.Component });
MrTimelineLayoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrTimelineLayoutComponent, selector: "mr-timeline-layout", usesInheritance: true, ngImport: i0, template: "<div class=\"mr-timeline mr-layout\" *ngIf=\"lb.dataSource\">\n  <div class=\"mr-timeline__timeline\">\n    <div\n      class=\"mr-timeline__timeline-loading\"\n      *ngIf=\"lb.dataSource.loading.timeline\">\n        <n7-loader></n7-loader>\n    </div>\n    <n7-timeline\n      [data]=\"lb.widgets['mr-timeline'].ds.out$ | async\"\n      *ngIf=\"!lb.dataSource.loading.timeline\"\n    >\n    </n7-timeline>\n  </div>\n\n  <div class=\"mr-timeline__page mr-side-margin\">\n    <!-- Result header -->\n    <div class=\"mr-timeline__page-header\">\n      <p\n        class=\"mr-timeline__date\"\n        *ngIf=\"!lb.dataSource.loading.resourceDetails\"\n      >\n        {{lb.dataSource.eventHeader}}\n      </p>\n      <div class=\"mr-timeline__title\">\n        <n7-inner-title\n          [data]=\"lb.widgets['mr-year-header'].ds.out$ | async\"\n          [emit]=\"lb.widgets['mr-year-header'].emit\"\n        >\n        </n7-inner-title>\n      </div>\n    </div>\n    <!-- END // Result header -->\n\n    <div class=\"mr-timeline__content\">\n      <!-- DESCRIZIONE -->\n      <div *ngIf=\"lb.dataSource.eventDescription\" class=\"mr-content-block mr-content-block-description\">\n        <p [innerHTML]=\"lb.dataSource.eventDescription\"></p>\n        <p></p>\n      </div>\n\n      <ng-container *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n        <!-- GALLERIA -->\n        <div *ngIf=\"lb.dataSource.collectionGalleryData && lb.dataSource.collectionGalleryData.length\" \n        class=\"mr-content-block n7-grid-6\">\n          <ng-container\n            *ngFor=\"let image of lb.dataSource.collectionGalleryData\"\n          >\n            <a [href]=\"image.image\" class=\"mr-gallery__image\">\n              <img [src]=\"image.thumbnail\" alt=\"image.title\" />\n            </a>\n          </ng-container>\n        </div>\n\n        <!-- MAPPA -->\n        <div\n          class=\"mr-content-block mr-content-block-map\"\n          *ngIf=\"lb.dataSource.hasMap\"\n        >\n          <h3 class=\"mr-content-block__title\" *ngIf=\"lb.dataSource.mapHeader\">\n            {{ lb.dataSource.mapHeader }}\n          </h3>\n          <div class=\"mr-content-block__content\">\n            <n7-map [data]=\"lb.widgets['mr-map'].ds.out$ | async\"></n7-map>\n          </div>\n        </div>\n\n        <!-- BIBLIOGRAFIA -->\n        <ng-container *ngIf=\"lb.dataSource.bibliographyData as biblio\">\n          <ng-container *ngIf=\"biblio.items && biblio.items.length > 0\">\n            <div class=\"mr-content-block mr-content-block-collection\">\n              <h3 class=\"mr-content-block__title\">{{ biblio.header.title }}</h3>\n              <div class=\"mr-content-block__content n7-grid-1\">\n                <ng-container *ngFor=\"let item of biblio.items\">\n                  <div class=\"mr-timeline__collection-content\">\n                    <n7-item-preview\n                      [emit]=\"lb.eventHandler.itemPreviewEmit\"\n                      [data]=\"item\"\n                    >\n                    </n7-item-preview>\n                  </div>\n                </ng-container>\n              </div>\n            </div>\n          </ng-container>\n        </ng-container>\n\n        <!-- COLLECTIONS  -->\n        <ng-container *ngFor=\"let coll of lb.dataSource.collectionData\">\n          <ng-container *ngIf=\"coll.items && coll.items.length > 0\">\n            <div class=\"mr-content-block-collection mr-content-block\">\n              <h3 class=\"mr-content-block__title\">{{ coll.header.title }}</h3>\n              <div class=\"mr-content-block__content n7-grid-3\">\n                <n7-item-preview *ngFor=\"let item of coll.items\" [data]=\"item\">\n                </n7-item-preview>\n              </div>\n            </div>\n          </ng-container>\n        </ng-container>\n      </ng-container>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "component", type: i7.MapComponent, selector: "n7-map", inputs: ["data", "emit"] }, { kind: "component", type: i7.TimelineComponent, selector: "n7-timeline", inputs: ["data", "emit"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrTimelineLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-timeline-layout', template: "<div class=\"mr-timeline mr-layout\" *ngIf=\"lb.dataSource\">\n  <div class=\"mr-timeline__timeline\">\n    <div\n      class=\"mr-timeline__timeline-loading\"\n      *ngIf=\"lb.dataSource.loading.timeline\">\n        <n7-loader></n7-loader>\n    </div>\n    <n7-timeline\n      [data]=\"lb.widgets['mr-timeline'].ds.out$ | async\"\n      *ngIf=\"!lb.dataSource.loading.timeline\"\n    >\n    </n7-timeline>\n  </div>\n\n  <div class=\"mr-timeline__page mr-side-margin\">\n    <!-- Result header -->\n    <div class=\"mr-timeline__page-header\">\n      <p\n        class=\"mr-timeline__date\"\n        *ngIf=\"!lb.dataSource.loading.resourceDetails\"\n      >\n        {{lb.dataSource.eventHeader}}\n      </p>\n      <div class=\"mr-timeline__title\">\n        <n7-inner-title\n          [data]=\"lb.widgets['mr-year-header'].ds.out$ | async\"\n          [emit]=\"lb.widgets['mr-year-header'].emit\"\n        >\n        </n7-inner-title>\n      </div>\n    </div>\n    <!-- END // Result header -->\n\n    <div class=\"mr-timeline__content\">\n      <!-- DESCRIZIONE -->\n      <div *ngIf=\"lb.dataSource.eventDescription\" class=\"mr-content-block mr-content-block-description\">\n        <p [innerHTML]=\"lb.dataSource.eventDescription\"></p>\n        <p></p>\n      </div>\n\n      <ng-container *ngIf=\"!lb.dataSource.loading.resourceDetails\">\n        <!-- GALLERIA -->\n        <div *ngIf=\"lb.dataSource.collectionGalleryData && lb.dataSource.collectionGalleryData.length\" \n        class=\"mr-content-block n7-grid-6\">\n          <ng-container\n            *ngFor=\"let image of lb.dataSource.collectionGalleryData\"\n          >\n            <a [href]=\"image.image\" class=\"mr-gallery__image\">\n              <img [src]=\"image.thumbnail\" alt=\"image.title\" />\n            </a>\n          </ng-container>\n        </div>\n\n        <!-- MAPPA -->\n        <div\n          class=\"mr-content-block mr-content-block-map\"\n          *ngIf=\"lb.dataSource.hasMap\"\n        >\n          <h3 class=\"mr-content-block__title\" *ngIf=\"lb.dataSource.mapHeader\">\n            {{ lb.dataSource.mapHeader }}\n          </h3>\n          <div class=\"mr-content-block__content\">\n            <n7-map [data]=\"lb.widgets['mr-map'].ds.out$ | async\"></n7-map>\n          </div>\n        </div>\n\n        <!-- BIBLIOGRAFIA -->\n        <ng-container *ngIf=\"lb.dataSource.bibliographyData as biblio\">\n          <ng-container *ngIf=\"biblio.items && biblio.items.length > 0\">\n            <div class=\"mr-content-block mr-content-block-collection\">\n              <h3 class=\"mr-content-block__title\">{{ biblio.header.title }}</h3>\n              <div class=\"mr-content-block__content n7-grid-1\">\n                <ng-container *ngFor=\"let item of biblio.items\">\n                  <div class=\"mr-timeline__collection-content\">\n                    <n7-item-preview\n                      [emit]=\"lb.eventHandler.itemPreviewEmit\"\n                      [data]=\"item\"\n                    >\n                    </n7-item-preview>\n                  </div>\n                </ng-container>\n              </div>\n            </div>\n          </ng-container>\n        </ng-container>\n\n        <!-- COLLECTIONS  -->\n        <ng-container *ngFor=\"let coll of lb.dataSource.collectionData\">\n          <ng-container *ngIf=\"coll.items && coll.items.length > 0\">\n            <div class=\"mr-content-block-collection mr-content-block\">\n              <h3 class=\"mr-content-block__title\">{{ coll.header.title }}</h3>\n              <div class=\"mr-content-block__content n7-grid-3\">\n                <n7-item-preview *ngFor=\"let item of coll.items\" [data]=\"item\">\n                </n7-item-preview>\n              </div>\n            </div>\n          </ng-container>\n        </ng-container>\n      </ng-container>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.LayoutsConfigurationService }, { type: i2.ActivatedRoute }, { type: i2.Router }, { type: i1.ConfigurationService }, { type: i1.CommunicationService }, { type: i1.MainStateService }, { type: MrLayoutStateService }, { type: MrResourceModalService }, { type: MrLocaleService }, { type: i1$2.Location }]; } });

const DATASOURCE_MAP = {
    collection: MrCollectionDS,
    metadata: MrMetadataDS,
    preview: MrItemPreviewDS,
    title: MrInnerTitleDS,
};
class MrResourceModalComponent {
    constructor(router, modalService, localeService) {
        this.router = router;
        this.modalService = modalService;
        this.localeService = localeService;
        this.destroy$ = new Subject();
        this.status = 'IDLE';
        this.widgets = {};
        this.errorTitle = _t('global#layout_error_title');
        this.errorDescription = _t('global#layout_error_description');
    }
    ngOnInit() {
        this.modalService.state$
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ status, config, response }) => {
            this.status = status;
            this.config = config;
            if (status === 'SUCCESS') {
                this.loadWidgets(config, response);
            }
        });
        // on router change close
        this.router.events.pipe(takeUntil(this.destroy$), filter(() => !isEmpty(this.widgets)), filter((event) => event instanceof NavigationStart)).subscribe(() => {
            this.onClose();
        });
    }
    ngOnDestroy() {
        // reset
        this.onClose();
        this.destroy$.next();
    }
    onClose(target) {
        if (target && target.className !== 'mr-resource-modal__overlay') {
            return;
        }
        this.widgets = {};
        this.modalService.close();
    }
    loadWidgets(config, response) {
        const { top, content } = config.sections;
        const sections = top.concat(content);
        if (sections) {
            sections.forEach(({ id, type, options }) => {
                const data = response.sections[id];
                this.widgets[id] = {
                    ds: new DATASOURCE_MAP[type]()
                };
                // update options
                this.widgets[id].ds.options = {
                    ...(options || {}),
                    localeService: this.localeService
                };
                // update data
                if (data) {
                    this.widgets[id].ds.update(data);
                }
            });
        }
    }
}
MrResourceModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceModalComponent, deps: [{ token: i2.Router }, { token: MrResourceModalService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Component });
MrResourceModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.12", type: MrResourceModalComponent, selector: "mr-resource-modal", ngImport: i0, template: "<div *ngIf=\"status !== 'IDLE'\" class=\"mr-modal mr-resource-modal\" [ngClass]=\"{\n        'is-loading': status === 'LOADING',\n        'is-error': status === 'ERROR'\n      }\">\n    <div class=\"mr-modal__overlay\" (click)=\"onClose($event.target)\">\n        <div class=\"mr-modal__window mr-resource-modal__window\">\n            <ng-container [ngSwitch]=\"status\">\n\n                <!-- Loading -->\n                <ng-container *ngSwitchCase=\"'LOADING'\">\n                    <div class=\"mr-modal__loader\">\n                        <n7-loader></n7-loader>\n                    </div>\n                </ng-container>\n\n                <!-- Error -->\n                <ng-container *ngSwitchCase=\"'ERROR'\">\n\n                    <div class=\"mr-modal__header mr-resource-modal__header\">\n                        <h2 class=\"mr-modal__header-title\">\n                            {{ errorTitle }}\n                        </h2>\n                        <div class=\"mr-modal__close\">\n                            <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                        </div>\n                    </div>\n                    <div class=\"mr-modal__content\">\n                        <p class=\"mr-modal__error-text\">{{ errorDescription }}</p>\n                    </div>\n\n                </ng-container>\n\n                <!-- Success -->\n                <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                    \n                    <div class=\"mr-modal__header mr-resource-modal__header\">\n                        <ng-container *ngIf=\"config.sections as sections\">\n                            <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.top }\"></ng-container>\n                        </ng-container>\n                        <div class=\"mr-modal__close\">\n                            <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                        </div>\n                    </div>\n                    <div class=\"mr-modal__content mr-resource-modal__content\">\n                        <ng-container *ngIf=\"config.sections as sections\">\n                            <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.content }\"></ng-container>                            \n                        </ng-container>\n                    </div>\n                </ng-container>\n\n            </ng-container>\n\n        </div>\n    </div>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"widgets[section.id].ds && (widgets[section.id].ds.out$ | async)\"\n            class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n\n                <!-- INNER TITLE -->\n                <ng-container *ngSwitchCase=\"'title'\">\n                    <n7-inner-title [data]=\"widgets[section.id].ds.out$ | async\">\n                    </n7-inner-title>\n                </ng-container>\n\n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    <div class=\"mr-resource-modal__metadata-content\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title mr-resource__metadata-title\">\n                            {{ section.title }}\n                        </h3>\n                        <mr-read-more [data]=\"section.readmore\">\n                            <n7-metadata-viewer [data]=\"widgets[section.id].ds.out$ | async\">\n                            </n7-metadata-viewer>\n                        </mr-read-more>\n                    </div>\n                </ng-container>\n\n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"widgets[section.id].ds.out$ | async as collection$\">\n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-resource__collection-content\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title\">\n                                {{ section.title }}\n                            </h3>\n                            <div\n                                class=\"mr-resource__collection-grid {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\" [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- ITEM PREVIEW -->\n                <ng-container *ngSwitchCase=\"'preview'\">\n                    <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title\">\n                        {{ section.title }}\n                    </h3>\n                    <n7-item-preview [data]=\"widgets[section.id].ds.out$ | async\">\n                    </n7-item-preview>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1$2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "component", type: i7.InnerTitleComponent, selector: "n7-inner-title", inputs: ["data", "emit"] }, { kind: "component", type: i7.ItemPreviewComponent, selector: "n7-item-preview", inputs: ["data", "emit"] }, { kind: "component", type: i7.LoaderComponent, selector: "n7-loader", inputs: ["data"] }, { kind: "component", type: i7.MetadataViewerComponent, selector: "n7-metadata-viewer", inputs: ["data", "emit"] }, { kind: "component", type: ReadMoreComponent, selector: "mr-read-more", inputs: ["data"] }, { kind: "pipe", type: i1$2.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrResourceModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mr-resource-modal', template: "<div *ngIf=\"status !== 'IDLE'\" class=\"mr-modal mr-resource-modal\" [ngClass]=\"{\n        'is-loading': status === 'LOADING',\n        'is-error': status === 'ERROR'\n      }\">\n    <div class=\"mr-modal__overlay\" (click)=\"onClose($event.target)\">\n        <div class=\"mr-modal__window mr-resource-modal__window\">\n            <ng-container [ngSwitch]=\"status\">\n\n                <!-- Loading -->\n                <ng-container *ngSwitchCase=\"'LOADING'\">\n                    <div class=\"mr-modal__loader\">\n                        <n7-loader></n7-loader>\n                    </div>\n                </ng-container>\n\n                <!-- Error -->\n                <ng-container *ngSwitchCase=\"'ERROR'\">\n\n                    <div class=\"mr-modal__header mr-resource-modal__header\">\n                        <h2 class=\"mr-modal__header-title\">\n                            {{ errorTitle }}\n                        </h2>\n                        <div class=\"mr-modal__close\">\n                            <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                        </div>\n                    </div>\n                    <div class=\"mr-modal__content\">\n                        <p class=\"mr-modal__error-text\">{{ errorDescription }}</p>\n                    </div>\n\n                </ng-container>\n\n                <!-- Success -->\n                <ng-container *ngSwitchCase=\"'SUCCESS'\">\n                    \n                    <div class=\"mr-modal__header mr-resource-modal__header\">\n                        <ng-container *ngIf=\"config.sections as sections\">\n                            <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.top }\"></ng-container>\n                        </ng-container>\n                        <div class=\"mr-modal__close\">\n                            <a class=\"mr-modal__close-link\" (click)=\"onClose()\"><span class=\"n7-icon-close\"></span></a>\n                        </div>\n                    </div>\n                    <div class=\"mr-modal__content mr-resource-modal__content\">\n                        <ng-container *ngIf=\"config.sections as sections\">\n                            <ng-container *ngTemplateOutlet=\"blocks; context: { $implicit: sections.content }\"></ng-container>                            \n                        </ng-container>\n                    </div>\n                </ng-container>\n\n            </ng-container>\n\n        </div>\n    </div>\n</div>\n\n<ng-template #blocks let-list>\n    <ng-container *ngFor=\"let section of list\">\n        <section *ngIf=\"widgets[section.id].ds && (widgets[section.id].ds.out$ | async)\"\n            class=\"{{ 'mr-resource__section mr-resource__' + section.type }}\">\n            <ng-container [ngSwitch]=\"section.type\">\n\n                <!-- INNER TITLE -->\n                <ng-container *ngSwitchCase=\"'title'\">\n                    <n7-inner-title [data]=\"widgets[section.id].ds.out$ | async\">\n                    </n7-inner-title>\n                </ng-container>\n\n                <!-- METADATA VIEWER -->\n                <ng-container *ngSwitchCase=\"'metadata'\">\n                    <div class=\"mr-resource-modal__metadata-content\">\n                        <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title mr-resource__metadata-title\">\n                            {{ section.title }}\n                        </h3>\n                        <mr-read-more [data]=\"section.readmore\">\n                            <n7-metadata-viewer [data]=\"widgets[section.id].ds.out$ | async\">\n                            </n7-metadata-viewer>\n                        </mr-read-more>\n                    </div>\n                </ng-container>\n\n                <!-- COLLECTION -->\n                <ng-container *ngSwitchCase=\"'collection'\">\n                    <ng-container *ngIf=\"widgets[section.id].ds.out$ | async as collection$\">\n                        <div *ngIf=\"collection$.items?.length > 0\" class=\"mr-resource__collection-content\">\n                            <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title\">\n                                {{ section.title }}\n                            </h3>\n                            <div\n                                class=\"mr-resource__collection-grid {{ section.grid ? 'n7-grid-' + section.grid : '' }}\">\n                                <n7-item-preview *ngFor=\"let item of collection$?.items\" [data]=\"item\">\n                                </n7-item-preview>\n                            </div>\n                        </div>\n                    </ng-container>\n                </ng-container>\n\n                <!-- ITEM PREVIEW -->\n                <ng-container *ngSwitchCase=\"'preview'\">\n                    <h3 *ngIf=\"section.title\" class=\"mr-resource-modal__section-title\">\n                        {{ section.title }}\n                    </h3>\n                    <n7-item-preview [data]=\"widgets[section.id].ds.out$ | async\">\n                    </n7-item-preview>\n                </ng-container>\n\n            </ng-container>\n        </section>\n    </ng-container>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: MrResourceModalService }, { type: MrLocaleService }]; } });

// MODULES
const COMPONENTS = [
    // Layout components
    MrAdvancedResultsLayoutComponent,
    MrAdvancedSearchLayoutComponent,
    MrHomeLayoutComponent,
    MrItineraryLayoutComponent,
    MrMapLayoutComponent,
    MrMetadataReadmoreComponent,
    MrPostsLayoutComponent,
    MrResourceLayoutComponent,
    MrSearchFacetsLayoutComponent,
    MrSearchLayoutComponent,
    MrStaticLayoutComponent,
    MrTimelineLayoutComponent,
    // Custom components
    ReadMoreComponent,
    MrFormComponent,
    MrFormWrapperAccordionComponent,
    MrSearchPageDescriptionComponent,
    MrResourceModalComponent,
    MrGalleryComponent,
    MrAdvancedResultComponent,
    MrMetadataDynamicComponent,
];
class N7BoilerplateMurucaModule {
    constructor(localeService, mainState, router) {
        this.localeService = localeService;
        this.mainState = mainState;
        this.router = router;
        // listen to locale (language select) change event
        this.mainState.get$('footerEvents').subscribe(({ type, payload }) => {
            if (type === 'footer.change' && payload?.inputPayload === 'locale') {
                const currentLocale = this.localeService.getLocale();
                const { value } = payload;
                if (currentLocale !== value) {
                    const href = this.localeService.getLinkByLocale(value);
                    if (href) {
                        this.router.navigate([href]);
                    }
                }
            }
        });
    }
}
N7BoilerplateMurucaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: N7BoilerplateMurucaModule, deps: [{ token: MrLocaleService }, { token: i1.MainStateService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.NgModule });
N7BoilerplateMurucaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: N7BoilerplateMurucaModule, declarations: [EscapeHtmlPipe, 
        // Layout components
        MrAdvancedResultsLayoutComponent,
        MrAdvancedSearchLayoutComponent,
        MrHomeLayoutComponent,
        MrItineraryLayoutComponent,
        MrMapLayoutComponent,
        MrMetadataReadmoreComponent,
        MrPostsLayoutComponent,
        MrResourceLayoutComponent,
        MrSearchFacetsLayoutComponent,
        MrSearchLayoutComponent,
        MrStaticLayoutComponent,
        MrTimelineLayoutComponent,
        // Custom components
        ReadMoreComponent,
        MrFormComponent,
        MrFormWrapperAccordionComponent,
        MrSearchPageDescriptionComponent,
        MrResourceModalComponent,
        MrGalleryComponent,
        MrAdvancedResultComponent,
        MrMetadataDynamicComponent], imports: [CommonModule,
        DvComponentsLibModule,
        N7BoilerplateCommonModule], exports: [
        // Layout components
        MrAdvancedResultsLayoutComponent,
        MrAdvancedSearchLayoutComponent,
        MrHomeLayoutComponent,
        MrItineraryLayoutComponent,
        MrMapLayoutComponent,
        MrMetadataReadmoreComponent,
        MrPostsLayoutComponent,
        MrResourceLayoutComponent,
        MrSearchFacetsLayoutComponent,
        MrSearchLayoutComponent,
        MrStaticLayoutComponent,
        MrTimelineLayoutComponent,
        // Custom components
        ReadMoreComponent,
        MrFormComponent,
        MrFormWrapperAccordionComponent,
        MrSearchPageDescriptionComponent,
        MrResourceModalComponent,
        MrGalleryComponent,
        MrAdvancedResultComponent,
        MrMetadataDynamicComponent] });
N7BoilerplateMurucaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: N7BoilerplateMurucaModule, providers: [
        MrSearchService,
        MrLayoutStateService,
        MrResourceModalService,
        MrLocaleService
    ], imports: [CommonModule,
        DvComponentsLibModule,
        N7BoilerplateCommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: N7BoilerplateMurucaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        EscapeHtmlPipe,
                        COMPONENTS
                    ],
                    imports: [
                        CommonModule,
                        DvComponentsLibModule,
                        N7BoilerplateCommonModule,
                    ],
                    providers: [
                        MrSearchService,
                        MrLayoutStateService,
                        MrResourceModalService,
                        MrLocaleService
                    ],
                    exports: COMPONENTS
                }]
        }], ctorParameters: function () { return [{ type: MrLocaleService }, { type: i1.MainStateService }, { type: i2.Router }]; } });

// FIXME: custom inputs
// export interface ConfigMurucaAdvancedSearchInputCustom<T>
//   extends ConfigMurucaAdvancedSearchInput<T> {
//   type: string;
//   data: object;
// }

// home layout

class MrFooterService {
    constructor(http, configuration, mainState) {
        this.http = http;
        this.configuration = configuration;
        this.mainState = mainState;
        this.cache = {};
    }
    load(locale = null) {
        if (locale && this.cache[locale]) {
            this._handleResponse(this.cache[locale]);
            return Promise.resolve();
        }
        const { defaultProvider, providers } = this.configuration.get('communication');
        const currentProvider = providers[defaultProvider] || {};
        const { baseUrl } = currentProvider;
        const menuPath = currentProvider?.config?.footer;
        if (baseUrl && menuPath) {
            let url = baseUrl + menuPath;
            if (locale) {
                url += `?locale=${locale}`;
            }
            return this.http.get(url).pipe(catchError(() => of(null)), tap((response) => {
                this.cache[locale] = response;
                this._handleResponse(response);
            })).toPromise();
        }
        return of(null).toPromise();
    }
    _handleResponse(response) {
        if (response) {
            this.configuration.set('footer', response);
            // update state
            this.mainState.update('footer', response);
        }
    }
}
MrFooterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFooterService, deps: [{ token: i1$3.HttpClient }, { token: i1.ConfigurationService }, { token: i1.MainStateService }], target: i0.ɵɵFactoryTarget.Injectable });
MrFooterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFooterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrFooterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }, { type: i1.ConfigurationService }, { type: i1.MainStateService }]; } });

class MrMenuService {
    constructor(http, configuration, localeService) {
        this.http = http;
        this.configuration = configuration;
        this.localeService = localeService;
        this.dynamicPaths = [];
        this.cache = {};
        this.isDynamicPath = (path) => this.dynamicPaths.includes(path);
    }
    load(locale = null) {
        if (locale && this.cache[locale]) {
            this._handleResponse(this.cache[locale]);
            return Promise.resolve();
        }
        const { defaultProvider, providers } = this.configuration.get('communication');
        const currentProvider = providers[defaultProvider] || {};
        const { baseUrl } = currentProvider;
        const menuPath = currentProvider?.config?.menu;
        if (baseUrl && menuPath) {
            let url = baseUrl + menuPath;
            if (locale) {
                url += `?locale=${locale}`;
            }
            return this.http.get(url).pipe(catchError(() => of(null)), tap((response) => {
                this.cache[locale] = response;
                this._handleResponse(response, locale);
            })).toPromise();
        }
        return of(null).toPromise();
    }
    _handleResponse(response, locale) {
        if (response) {
            const headerConfig = this.configuration.get('header');
            headerConfig.nav.items = response.map(({ label, slug, isStatic, subpages, classes }) => {
                const href = this.getHref(slug);
                // dynamic path control
                if (!isStatic) {
                    this.dynamicPaths.push(href);
                }
                const item = {
                    classes,
                    text: label,
                    anchor: href ? {
                        href: linksHelper.getRouterLink(href),
                        queryParams: linksHelper.getQueryParams(href)
                    } : null,
                    _meta: {
                        id: href
                    }
                };
                if (subpages !== undefined) {
                    item.subnav = [];
                    subpages.forEach((el) => {
                        const subHref = this.getHref(el.slug);
                        if (!el.isStatic) {
                            this.dynamicPaths.push(subHref);
                        }
                        item.subnav.push({
                            classes: el.classes || null,
                            text: el.label,
                            anchor: subHref ? {
                                href: linksHelper.getRouterLink(subHref),
                                queryParams: linksHelper.getQueryParams(subHref)
                            } : null,
                            _meta: {
                                id: subHref
                            }
                        });
                    });
                }
                return item;
            });
            if (locale) {
                const href = this.localeService.getLink(locale, 'home');
                if (href) {
                    headerConfig.logo.anchor = { href };
                }
            }
            this.configuration.set('header', headerConfig);
        }
    }
    getHref(link) {
        let href = link || null;
        if (href && !linksHelper.isExternalLink(href)) {
            href = `/${href}`;
        }
        return href;
    }
}
MrMenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMenuService, deps: [{ token: i1$3.HttpClient }, { token: i1.ConfigurationService }, { token: MrLocaleService }], target: i0.ɵɵFactoryTarget.Injectable });
MrMenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMenuService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrMenuService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }, { type: i1.ConfigurationService }, { type: MrLocaleService }]; } });

class MrTranslationsLoaderService {
    constructor(http, configuration) {
        this.http = http;
        this.configuration = configuration;
        this.loaded = {};
    }
    load(langCode) {
        // update translate module
        translate.setCurrentLang(langCode);
        // locale already loaded check
        if (this.loaded[langCode]) {
            this.loaded[langCode] = true;
            return Promise.resolve();
        }
        const { defaultProvider, providers } = this.configuration.get('communication');
        const currentProvider = providers[defaultProvider] || {};
        const { baseUrl } = currentProvider;
        const translationsPath = currentProvider?.config?.translation;
        if (baseUrl && translationsPath) {
            const url = baseUrl + translationsPath + langCode;
            return this.http.get(url).pipe(catchError(() => of(null)), tap((response) => this._handleResponse(response, langCode))).toPromise();
        }
        return of(null).toPromise();
    }
    _handleResponse(response, langCode) {
        if (response) {
            Object.keys(response).forEach((key) => {
                translate.setLangTranslation(langCode, key, response[key]);
            });
        }
    }
}
MrTranslationsLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrTranslationsLoaderService, deps: [{ token: i1$3.HttpClient }, { token: i1.ConfigurationService }], target: i0.ɵɵFactoryTarget.Injectable });
MrTranslationsLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrTranslationsLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: MrTranslationsLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }, { type: i1.ConfigurationService }]; } });

class DynamicPathGuard {
    constructor(menuService, router) {
        this.menuService = menuService;
        this.router = router;
    }
    canActivate(next, state) {
        const { url } = state;
        if (!this.menuService.isDynamicPath(url)) {
            const { notFoundPath } = next.data;
            this.router.navigate([notFoundPath ? `/${notFoundPath}` : '/']);
            return false;
        }
        return true;
    }
}
DynamicPathGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DynamicPathGuard, deps: [{ token: MrMenuService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
DynamicPathGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DynamicPathGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: DynamicPathGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: MrMenuService }, { type: i2.Router }]; } });

class LocaleDependenciesGuard {
    constructor(menuService, footerService, translationsLoader) {
        this.menuService = menuService;
        this.footerService = footerService;
        this.translationsLoader = translationsLoader;
    }
    canActivate(route, state) {
        const { locale, routeId } = route.data;
        // check routeId to force reload
        if ((routeId && this.prevRouteId === routeId)
            && (locale && this.prevLocale !== locale)) {
            const { url } = state;
            document.location.href = url;
            return Promise.reject();
        }
        this.prevRouteId = routeId;
        this.prevLocale = locale;
        return Promise.all([
            this.menuService.load(locale),
            this.footerService.load(locale),
            this.translationsLoader.load(locale)
        ])
            .then(() => Promise.resolve(true))
            .catch((err) => {
            console.warn('Locale dependencies error:', err);
            return Promise.resolve(true);
        });
    }
}
LocaleDependenciesGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocaleDependenciesGuard, deps: [{ token: MrMenuService }, { token: MrFooterService }, { token: MrTranslationsLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
LocaleDependenciesGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocaleDependenciesGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: LocaleDependenciesGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: MrMenuService }, { type: MrFooterService }, { type: MrTranslationsLoaderService }]; } });

/*
 * Public API Surface of n7-boilerplate-muruca
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DynamicPathGuard, FACETS_REQUEST_STATE_CONTEXT, FACET_STATE_CONTEXT, INPUT_STATE_CONTEXT, LayoutState, LocaleDependenciesGuard, MrAdvancedResultComponent, MrAdvancedResultsLayoutComponent, MrAdvancedResultsLayoutConfig, MrAdvancedResultsLayoutDS, MrAdvancedResultsLayoutEH, MrAdvancedSearchLayoutComponent, MrAdvancedSearchLayoutConfig, MrAdvancedSearchLayoutDS, MrAdvancedSearchLayoutEH, MrAdvancedSearchTagsDS, MrBreadcrumbsDS, MrCollectionDS, MrCollectionEH, MrContentDS, MrDummyEH, MrFiltersDS, MrFiltersEH, MrFooterService, MrFormComponent, MrFormWrapperAccordionComponent, MrFormWrapperAccordionDS, MrFormWrapperAccordionEH, MrGalleryComponent, MrGalleryDS, MrGalleryEH, MrHeroDS, MrHomeLayoutComponent, MrHomeLayoutConfig, MrHomeLayoutDS, MrHomeLayoutEH, MrImageViewerDS, MrImageViewerOverlayDetailsDS, MrImageViewerToolsDS, MrInfoBoxDS, MrInnerTitleDS, MrItemPreviewDS, MrItemPreviewsDS, MrItineraryLayoutComponent, MrItineraryLayoutConfig, MrItineraryLayoutDS, MrItineraryLayoutEH, MrLayoutStateService, MrLocaleService, MrMapDS, MrMapEH, MrMapLayoutComponent, MrMapLayoutConfig, MrMapLayoutDS, MrMapLayoutEH, MrMenuService, MrMetadataDS, MrMetadataDynamicComponent, MrMetadataDynamicDS, MrMetadataDynamicEH, MrMetadataReadmoreComponent, MrNavDS, MrNavEH, MrPostsLayoutComponent, MrPostsLayoutConfig, MrPostsLayoutDS, MrPostsLayoutEH, MrResourceLayoutComponent, MrResourceLayoutConfig, MrResourceLayoutDS, MrResourceLayoutEH, MrResourceModalComponent, MrResourceModalService, MrResourceTabsDS, MrSearchFacetsLayoutComponent, MrSearchLayoutComponent, MrSearchLayoutConfig, MrSearchLayoutDS, MrSearchLayoutEH, MrSearchPageDescriptionComponent, MrSearchPageDescriptionDS, MrSearchPageDescriptionEH, MrSearchPageTitleDS, MrSearchPageTitleEH, MrSearchResultsDS, MrSearchResultsEH, MrSearchResultsTitleDS, MrSearchResultsTitleEH, MrSearchService, MrSearchTagsDS, MrSearchTagsEH, MrStaticLayoutComponent, MrStaticLayoutConfig, MrStaticLayoutDS, MrStaticLayoutEH, MrStaticMetadataDS, MrTextViewerDS, MrTextViewerEH, MrTimelineDS, MrTimelineEH, MrTimelineLayoutComponent, MrTimelineLayoutConfig, MrTimelineLayoutDS, MrTimelineLayoutEH, MrTranslationsLoaderService, MrYearHeaderDS, MrYearHeaderEH, N7BoilerplateMurucaModule, RESULTS_REQUEST_STATE_CONTEXT, ReadMoreComponent, SECTION_STATE_CONTEXT, SearchFacetsLayoutConfig, SearchFacetsLayoutDS, SearchFacetsLayoutEH };
